import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, IconButton, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import NearMeIcon from '@material-ui/icons/NearMe';
import EditLocationIcon from '@material-ui/icons/EditLocation';
import HistoryIcon from '@material-ui/icons/History';
import { GoogleMap, useJsApiLoader, Polyline, Marker, InfoWindow, Circle, GoogleMarkerClusterer } from '@react-google-maps/api';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentRoute, addWaypoint, updateWaypoint, setOrigin, setDestination, saveRouteToAPI, fetchSavedRoutes, selectActualTrail, fetchActualTrail, clearActualTrail, clearRoute } from '../../store/slices/routesSlice';
import { addToast } from '../../store/slices/notificationsSlice';
import { selectAllVehicles, fetchAllVehicles } from '../../store/slices/vehiclesSlice';
import { selectLocations, createLocation, updateLocation } from '../../store/slices/locationsSlice';
import { selectUI, setMapClickMode, setNeedsRecalculation, setHoveredStopIdx, setResetMapView, setSidebarVisible, setSidebarCollapsed, setActiveView, navigateTo, setDisplayedSavedRoutes, setPendingOrderTimelineId } from '../../store/slices/uiSlice';
import { fetchIncidentsForViewport, INCIDENT_TYPE_MAP } from '../../utils/hereApi';
import { fetchIncidentsTomTomBbox } from '../../utils/tomtomApi';
/* eslint-disable no-unused-vars */
import { AuthContext } from '../../App';
import { analyzeTrail } from '../../utils/trailGeom';
import { vehicleIconCategory, vehicleIconZoom, getVehicleIconDataUrl, onVehicleIconLoaded, vehicleIconRotation } from '../../utils/vehicleIcons';
import VehicleHistoryChart from './VehicleHistoryChart';
import VehicleHistoryPlayback from './VehicleHistoryPlayback';
import MapToolbar from './MapToolbar';
import RouteServicesPanel from './RouteServicesPanel';
import ModuleGate from '../common/ModuleGate';
import TransitMonitor from './TransitMonitor';
import FleetQuickPanel from './FleetQuickPanel';
import RouteReplayGoogle from './RouteReplayGoogle';
import StreetViewThumbnail from '../common/StreetViewThumbnail';
import { exportHistoryGPX, exportHistoryCSV, exportHistoryPDF } from '../../utils/exportHistory';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];

// Global CSS overrides for Google Maps native controls
const gmapStyles = document.createElement('style');
gmapStyles.textContent = `
  /* Pegman — style and shrink the street view control */
  .gm-svpc {
    border-radius: 8px !important;
    box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
    overflow: hidden !important;
    border: 1.5px solid #fff !important;
    transform: scale(0.6) !important;
    transform-origin: bottom right !important;
  }
  /* Street View close button — push below header */
  .gm-iv-close,
  .gm-iv-close-button-holder,
  div[class*="closeButton"] {
    top: 64px !important;
    right: 12px !important;
    background: #fff !important;
    border-radius: 8px !important;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2) !important;
    width: 36px !important;
    height: 36px !important;
    z-index: 999 !important;
  }
  /* Street View address bar — push below header */
  .gm-iv-address,
  div[class*="addressControl"] {
    top: 64px !important;
    left: 16px !important;
  }
  /* Street View navigation links/arrows */
  .gm-iv-back-icon,
  .gm-iv-marker {
    top: 64px !important;
  }
  /* Google Maps Street View panorama controls */
  .gmnoprint.gm-bundled-control-on-bottom {
    bottom: 40px !important;
  }
  /* Zoom controls — style them if visible */
  .gm-bundled-control .gmnoprint {
    border-radius: 10px !important;
    overflow: hidden !important;
    box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
  }
  /* Vehicle plate label — balloon with pointer */
  .vehicle-plate-label {
    background: rgba(0,0,0,0.88) !important;
    padding: 3px 8px !important;
    border-radius: 4px !important;
    font-family: 'Roboto Mono', monospace !important;
    letter-spacing: 0.8px !important;
    white-space: nowrap !important;
    border: 1px solid rgba(255,255,255,0.25) !important;
    position: relative !important;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3) !important;
  }
  .vehicle-plate-label::before {
    content: '' !important;
    position: absolute !important;
    top: -6px !important;
    left: 50% !important;
    margin-left: -4px !important;
    border-left: 4px solid transparent !important;
    border-right: 4px solid transparent !important;
    border-bottom: 6px solid rgba(0,0,0,0.88) !important;
  }
  /* Selo "rota activa" no pop-up do veículo — anel a pulsar + ponto a piscar */
  @keyframes routeRingPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.7); }
    50%      { box-shadow: 0 0 0 9px rgba(249,115,22,0); }
  }
  @keyframes routeDotBlink {
    0%, 100% { opacity: 1; transform: scale(1.12); }
    50%      { opacity: 0.15; transform: scale(0.6); }
  }
  .route-active-badge { animation: routeRingPulse 1.25s ease-in-out infinite; }
  .route-active-dot { animation: routeDotBlink 0.85s ease-in-out infinite; }
  /* Sem altura fixa aqui — quem faz o scroll é o contentor do Google (acima),
     que se ajusta ao ecrã. Manter altura própria voltaria a cortar os botões. */
  .veh-popup-scroll { overscroll-behavior: contain; }
  @media (prefers-reduced-motion: reduce) {
    .route-active-badge, .route-active-dot { animation: none !important; }
  }
  /* InfoWindow como card flutuante — cantos arredondados + sombra, sem
     padding interno (o conteúdo traz o seu). Aplica-se a todas as popups. */
  .gm-style-iw.gm-style-iw-c {
    border-radius: 16px !important;
    box-shadow: 0 12px 34px rgba(15,23,42,0.22) !important;
    padding: 0 !important;
    overflow: hidden !important;
  }
  /* Deixa o próprio contentor da InfoWindow fazer scroll — o Google calcula
     a altura máxima para caber no ecrã, logo os botões de baixo ficam sempre
     alcançáveis (sem painel aninhado mais alto que o pai, que os cortava). */
  .gm-style-iw-d { overflow: auto !important; padding: 0 !important; }
  .gm-style-iw-d::-webkit-scrollbar { width: 8px; height: 8px; }
  .gm-style-iw-d::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.55); border-radius: 8px; }
  .gm-style-iw-d::-webkit-scrollbar-track { background: transparent; }
  /* Botão de fechar da InfoWindow — pequeno círculo discreto sobre a foto */
  .gm-style-iw button.gm-ui-hover-effect {
    top: 8px !important; right: 8px !important;
    width: 22px !important; height: 22px !important;
    min-width: 22px !important; min-height: 22px !important;
    background: rgba(255,255,255,0.92) !important;
    border-radius: 999px !important;
    box-shadow: 0 1px 4px rgba(15,23,42,0.22) !important;
    opacity: 1 !important;
    display: flex !important; align-items: center !important; justify-content: center !important;
    transition: background 0.15s !important;
  }
  .gm-style-iw button.gm-ui-hover-effect:hover { background: #fff !important; }
  .gm-style-iw button.gm-ui-hover-effect > span,
  .gm-style-iw button.gm-ui-hover-effect > img {
    width: 13px !important; height: 13px !important; margin: 0 !important;
    background-color: #475569 !important;
  }
`;
if (!document.querySelector('#gmap-custom-styles')) {
  gmapStyles.id = 'gmap-custom-styles';
  document.head.appendChild(gmapStyles);
}

const containerStyle = { width: '100%', height: '100%' };
const DEFAULT_CENTER_FALLBACK = { lat: 39.5, lng: -8.5 }; // Portugal

// Map ISO country code → approximate country centroid (primary source of truth
// once the admin has picked a country in Preferences). Falls back to timezone below.
const COUNTRY_TO_CENTER = {
  AO: { lat: -11.2, lng: 17.8, zoom: 5 },     // Angola
  MZ: { lat: -18.7, lng: 35.5, zoom: 5 },     // Mozambique
  GW: { lat: 12.0, lng: -15.0, zoom: 7 },     // Guinea-Bissau
  CV: { lat: 15.1, lng: -23.6, zoom: 8 },     // Cape Verde
  ST: { lat: 0.2, lng: 6.6, zoom: 9 },        // São Tomé
  PT: { lat: 39.5, lng: -8.5, zoom: 7 },      // Portugal
  ES: { lat: 40.2, lng: -3.7, zoom: 6 },      // Spain
  FR: { lat: 46.5, lng: 2.5, zoom: 6 },       // France
  BE: { lat: 50.8, lng: 4.4, zoom: 7 },       // Belgium
};

// Map IANA timezones → approximate country centroid (legacy fallback when
// Preferences have no country set yet).
const TIMEZONE_TO_CENTER = {
  'Africa/Luanda': { lat: -11.2, lng: 17.8, zoom: 5 },
  'Africa/Maputo': { lat: -18.7, lng: 35.5, zoom: 5 },
  'Africa/Bissau': { lat: 12.0, lng: -15.0, zoom: 7 },
  'Africa/Praia': { lat: 15.1, lng: -23.6, zoom: 8 },
  'Africa/Sao_Tome': { lat: 0.2, lng: 6.6, zoom: 9 },
  'Europe/Lisbon': { lat: 39.5, lng: -8.5, zoom: 7 },
  'Atlantic/Madeira': { lat: 32.75, lng: -16.95, zoom: 9 },
  'Atlantic/Azores': { lat: 37.74, lng: -25.66, zoom: 8 },
  'Europe/Madrid': { lat: 40.2, lng: -3.7, zoom: 6 },
  'Europe/Paris': { lat: 46.5, lng: 2.5, zoom: 6 },
  'Europe/Brussels': { lat: 50.8, lng: 4.4, zoom: 7 },
};
// Bounding-box por país — para classificar a posição de uma viatura sem
// custos de geocoding. Lista ordenada: o primeiro bbox que contém o ponto
// vence (PT antes de ES evita ambiguidade na faixa ibérica norte).
const COUNTRY_BBOX = [
  // Portugal continental
  { code: 'PT', minLat: 36.8, maxLat: 42.2, minLng: -9.6, maxLng: -6.1 },
  // Madeira
  { code: 'PT', minLat: 32.3, maxLat: 33.2, minLng: -17.5, maxLng: -16.0 },
  // Açores
  { code: 'PT', minLat: 36.5, maxLat: 40.0, minLng: -31.5, maxLng: -24.5 },
  // Cabo Verde
  { code: 'CV', minLat: 14.5, maxLat: 17.5, minLng: -25.7, maxLng: -22.5 },
  // Guiné-Bissau
  { code: 'GW', minLat: 10.5, maxLat: 13.0, minLng: -17.0, maxLng: -13.5 },
  // São Tomé
  { code: 'ST', minLat: -0.5, maxLat: 2.0, minLng: 6.0, maxLng: 7.7 },
  // Espanha (continental + Baleares)
  { code: 'ES', minLat: 35.5, maxLat: 43.9, minLng: -7.0, maxLng: 4.6 },
  // Canárias (ES)
  { code: 'ES', minLat: 27.0, maxLat: 29.5, minLng: -18.5, maxLng: -13.0 },
  // França
  { code: 'FR', minLat: 41.0, maxLat: 51.5, minLng: -5.5, maxLng: 9.7 },
  // Bélgica
  { code: 'BE', minLat: 49.0, maxLat: 51.6, minLng: 2.5, maxLng: 6.5 },
  // Angola
  { code: 'AO', minLat: -18.5, maxLat: -4.0, minLng: 11.0, maxLng: 24.5 },
  // Moçambique
  { code: 'MZ', minLat: -27.0, maxLat: -10.0, minLng: 30.0, maxLng: 41.5 },
];

function classifyCountry(lat, lng) {
  if (lat == null || lng == null) return null;
  for (const b of COUNTRY_BBOX) {
    if (lat >= b.minLat && lat <= b.maxLat && lng >= b.minLng && lng <= b.maxLng) {
      return b.code;
    }
  }
  return null;
}

// Histórico de veículo — presets de período (estilo EcooGPS). Devolve
// { df, dt } em YYYY-MM-DD (hora local), ou null para "Escolher datas".
const _ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function periodRange(preset) {
  const now = new Date();
  const d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const minus = (n) => { const d = new Date(d0); d.setDate(d.getDate() - n); return d; };
  const dowMon = (d0.getDay() + 6) % 7; // 0 = segunda
  switch (preset) {
    case 'hoje': return { df: _ymd(d0), dt: _ymd(d0) };
    case 'ontem': return { df: _ymd(minus(1)), dt: _ymd(minus(1)) };
    case 'anteontem': return { df: _ymd(minus(2)), dt: _ymd(minus(2)) };
    case 'esta_semana': return { df: _ymd(minus(dowMon)), dt: _ymd(d0) };
    case 'semana_passada': return { df: _ymd(minus(dowMon + 7)), dt: _ymd(minus(dowMon + 1)) };
    default: return null; // 'custom' → escolher datas
  }
}
// Detecta qual preset corresponde ao intervalo actual (ou 'custom').
function detectPreset(df, dt) {
  for (const key of ['hoje', 'ontem', 'anteontem', 'esta_semana', 'semana_passada']) {
    const r = periodRange(key);
    if (r && r.df === df && r.dt === dt) return key;
  }
  return 'custom';
}
const PERIOD_OPTIONS = [
  { key: 'hoje', label: 'Hoje' },
  { key: 'ontem', label: 'Ontem' },
  { key: 'anteontem', label: 'Anteontem' },
  { key: 'esta_semana', label: 'Esta semana' },
  { key: 'semana_passada', label: 'Semana passada' },
  { key: 'custom', label: 'Escolher datas' },
];

// Conta viaturas por país (via lat/lng da posição actual) e devolve
// { code, vehicles } do país com mais viaturas — ou null se não houver
// classificação fiável (frota sem posições ou minoria muito pequena).
function dominantCountry(vehicles) {
  if (!Array.isArray(vehicles) || vehicles.length === 0) return null;
  const byCountry = new Map();
  for (const v of vehicles) {
    if (v?.disabled) continue;
    const lat = v.position?.latitude;
    const lng = v.position?.longitude;
    const code = classifyCountry(lat, lng);
    if (!code) continue;
    const arr = byCountry.get(code) || [];
    arr.push({ lat, lng });
    byCountry.set(code, arr);
  }
  if (byCountry.size === 0) return null;
  let bestCode = null, bestArr = [];
  for (const [code, arr] of byCountry.entries()) {
    if (arr.length > bestArr.length) { bestCode = code; bestArr = arr; }
  }
  return { code: bestCode, points: bestArr };
}

// Preferences country takes precedence; timezone only used if country isn't set.
function centerFromSettings(settings) {
  if (!settings) return null;
  if (settings.defaultCountry && COUNTRY_TO_CENTER[settings.defaultCountry]) {
    return COUNTRY_TO_CENTER[settings.defaultCountry];
  }
  if (settings.timezone && TIMEZONE_TO_CENTER[settings.timezone]) {
    return TIMEZONE_TO_CENTER[settings.timezone];
  }
  return null;
}
// Kept for backward compat with any callsites that still pass a raw timezone
function centerFromTimezone(tz) {
  if (!tz) return null;
  return TIMEZONE_TO_CENTER[tz] || null;
}

// Road type colors (same as Mapbox version)
const ROAD_COLORS = {
  motorway: '#3b82f6',  // blue
  highway: '#8b5cf6',   // purple
  national: '#f59e0b',  // amber
  local: '#6b7280',     // grey
};

// Countries where HERE's road-class breakdown is accurate and useful for the
// operator (labels like "Autoestrada" / "IP" match the local road system).
// Outside this set (e.g. AO, MZ, NA, ZA, ST, GW, CV) the breakdown tends to
// misclassify roads or surprise the operator — fall back to a single neutral
// stroke for clarity.
const EU_ROAD_CLASS_COUNTRIES = new Set([
  'PT', 'ES', 'FR', 'IT', 'DE', 'BE', 'NL', 'LU', 'CH', 'AT',
  'DK', 'NO', 'SE', 'FI', 'IE', 'GB', 'UK', 'PL', 'CZ', 'SK',
]);

// Uniform stroke for "outside-EU" routes. Slightly heavier than before so the
// line reads clearly on satellite/hybrid basemaps at any zoom level.
const UNIFORM_ROUTE_COLOR = '#1d4ed8';

// Pin SVG path
const PIN_PATH = 'M12 0C5.37 0 0 5.37 0 12c0 9 12 20 12 20s12-11 12-20C24 5.37 18.63 0 12 0z';

// Paleta para distinguir viagens do histórico no mapa (cor estável por índice).
const TRIP_COLORS = ['#ef4444', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#10b981', '#6366f1', '#f97316', '#14b8a6', '#e11d48'];
const tripColor = (i) => TRIP_COLORS[((i % TRIP_COLORS.length) + TRIP_COLORS.length) % TRIP_COLORS.length];

// Redimensiona + comprime uma imagem escolhida pelo operador para uma data-URL
// leve (máx ~1000px, JPEG) — para a foto do veículo guardada na popup não pesar.
function fileToResizedDataURL(file, maxDim = 1000, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width >= height && width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim; }
        else if (height > width && height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Rosa-dos-ventos suave para o pop-up do veículo — indica o sentido de marcha
// (heading em graus, 0=N, 90=E, 180=S, 270=O). SVG estático + rótulo cardinal.
// Sem animação e sem dependências — desenho leve (~1 elemento).
function CompassRose({ heading = 0, lang = 'pt' }) {
  const deg = ((Number(heading) || 0) % 360 + 360) % 360;
  const dirs = lang === 'pt'
    ? ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO']
    : ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const label = dirs[Math.round(deg / 45) % 8];
  const W = lang === 'pt' ? 'O' : 'W';
  return (
    <div title={(lang === 'pt' ? 'Sentido de marcha' : 'Heading') + `: ${label} · ${Math.round(deg)}°`}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 }}>
      <svg width="42" height="42" viewBox="0 0 42 42" aria-hidden="true">
        <circle cx="21" cy="21" r="19" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
        <circle cx="21" cy="21" r="15" fill="none" stroke="#eef2f7" strokeWidth="1" />
        {/* Letras cardeais discretas */}
        <text x="21" y="9.5" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#94a3b8">N</text>
        <text x="34" y="23.3" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#cbd5e1">E</text>
        <text x="21" y="37" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#cbd5e1">S</text>
        <text x="8" y="23.3" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#cbd5e1">{W}</text>
        {/* Agulha rodada para o heading — metade laranja (frente), cauda cinza */}
        <g transform={`rotate(${deg} 21 21)`}>
          <polygon points="21,7 24.4,21 21,18.2 17.6,21" fill="#f97316" />
          <polygon points="21,35 24.4,21 21,23.8 17.6,21" fill="#cbd5e1" />
          <circle cx="21" cy="21" r="2.1" fill="#fff" stroke="#f97316" strokeWidth="1" />
        </g>
      </svg>
      <span style={{ fontSize: 9, fontWeight: 800, color: '#64748b', letterSpacing: 0.3 }}>{label}</span>
    </div>
  );
}

// Origin/destination marker SVGs — compact teardrop pins with a white inner
// circle holding the distinguishing glyph. Size 26×34 (was 34×44) so the
// endpoints don't dominate the map. Rendered via a data URL for vector scaling
// and a subtle drop shadow. The pin tip is the anchor point.
// Round-trip endpoint icon — used when origin and destination share the same
// coordinates (typical assistance routes with return_home=true). Shows the
// "partida" flag on top and the "chegada" flag on bottom of the same pin so
// the operator sees clearly that the route ends back at the start.
function buildRoundTripIcon({ w = 32, h = 44 }) {
  // Wider teardrop with a horizontal divider, top half green (partida),
  // bottom half red (chegada). Inner circle hosts both glyphs stacked.
  const pin = 'M17 0C7.61 0 0 7.61 0 17c0 12.75 17 27 17 27s17-14.25 17-27C34 7.61 26.39 0 17 0z';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 34 44">
    <defs>
      <linearGradient id="rt" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#16a34a"/>
        <stop offset="50%" stop-color="#16a34a"/>
        <stop offset="50%" stop-color="#dc2626"/>
        <stop offset="100%" stop-color="#dc2626"/>
      </linearGradient>
      <filter id="rtsh" x="-20%" y="-10%" width="140%" height="125%">
        <feDropShadow dx="0" dy="1" stdDeviation="0.9" flood-color="#0f172a" flood-opacity="0.4"/>
      </filter>
    </defs>
    <g filter="url(#rtsh)">
      <path d="${pin}" fill="url(#rt)"/>
      <circle cx="17" cy="17" r="11" fill="#ffffff"/>
      <text x="17" y="13" text-anchor="middle" dominant-baseline="central"
        font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif"
        font-weight="800" font-size="9" fill="#16a34a">⚐</text>
      <text x="17" y="22" text-anchor="middle" dominant-baseline="central"
        font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif"
        font-weight="800" font-size="9" fill="#dc2626">⚑</text>
    </g>
  </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    _w: w, _h: h,
  };
}

function buildEndpointIcon({ color, glyph, w = 26, h = 34 }) {
  // Teardrop path in a 34×44 viewBox — we reuse it and just render the <svg>
  // at a smaller display size. Tip at (17, 44).
  const pin = 'M17 0C7.61 0 0 7.61 0 17c0 12.75 17 27 17 27s17-14.25 17-27C34 7.61 26.39 0 17 0z';
  const cx = 17, cy = 15, r = 9.5; // inner white circle
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 34 44">
    <defs><filter id="sh" x="-20%" y="-10%" width="140%" height="125%">
      <feDropShadow dx="0" dy="1" stdDeviation="0.9" flood-color="#0f172a" flood-opacity="0.35"/>
    </filter></defs>
    <g filter="url(#sh)">
      <path d="${pin}" fill="${color}"/>
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="#ffffff"/>
      <text x="${cx}" y="${cy + 0.8}" text-anchor="middle" dominant-baseline="central"
        font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif"
        font-weight="800" font-size="13" fill="${color}">${glyph}</text>
    </g>
  </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    _w: w, _h: h,
  };
}

// Nome do favorito ELEVADO por cima do pin, ligado por um traço vertical
// (stalk) — mesma ideia das "pernas" que ligam o nome do condutor ao ícone
// da viatura. Assim o nome fica separado do pin e percebe-se a que ponto
// pertence, mesmo com muitos favoritos juntos. Tudo cozido no SVG com halo
// branco (paint-order stroke) para se ler sobre qualquer fundo.
function buildFavNameIcon(name, stalkColor = '#334155') {
  const text = String(name || '').slice(0, 42);
  const esc = s => String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
  const fontSize = 12;
  const charW = 6.7;
  const textW = Math.max(24, text.length * charW);
  const textH = 18;      // faixa do texto (topo)
  const rise = 34;       // comprimento do traço (mais cumprido)
  const dx = 22;         // inclinação para a DIREITA
  const gapOverPin = 22; // espaço livre por cima do pin (não sobrepor)
  const pad = 8;
  const W = Math.ceil(Math.max(textW, dx * 2)) + pad * 2;
  const H = textH + rise + gapOverPin;
  const textCx = W / 2;             // texto centrado no topo
  const baseX = textCx - dx;        // base do traço à ESQUERDA do texto → sobe p/ direita
  const stalkTopY = textH;          // topo do traço, logo abaixo do texto
  const stalkBaseY = textH + rise;  // base do traço (por cima do pin)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`
    + `<line x1="${baseX}" y1="${stalkBaseY}" x2="${textCx}" y2="${stalkTopY}" stroke="${stalkColor}" stroke-width="1.6" stroke-linecap="round"/>`
    + `<circle cx="${baseX}" cy="${stalkBaseY}" r="2.1" fill="${stalkColor}"/>`
    + `<text x="${textCx}" y="${textH / 2 + 1}" text-anchor="middle" dominant-baseline="central"`
    + ` font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="${fontSize}" font-weight="700"`
    + ` paint-order="stroke" stroke="#ffffff" stroke-width="3.2" stroke-linejoin="round" fill="#0f172a">${esc(text)}</text>`
    + `</svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    size: new window.google.maps.Size(W, H),
    scaledSize: new window.google.maps.Size(W, H),
    anchor: new window.google.maps.Point(baseX, H), // base do traço assente por cima do pin
  };
}

// Reusable map button component
function MapBtn({ title, onClick, icon, active, activeColor }) {
  const c = activeColor || '#3b82f6';
  return (
    <Box
      onClick={onClick}
      title={title}
      style={{
        width: 36, height: 36, borderRadius: 8, display: 'flex',
        alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        background: active ? `${c}18` : '#fff',
        border: `1px solid ${active ? `${c}66` : '#e2e8f0'}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = active ? `${c}28` : '#f8fafc'; }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? `${c}18` : '#fff'; }}
    >
      {icon}
    </Box>
  );
}

// ── Cluster donut (estilo EcooGPS) ────────────────────────────────
// Anel circular dividido por estado: verde=movimento, amarelo=ralenti,
// vermelho=parado. Centro branco com o nº de veículos. Réplica da
// createDonutChart() do EcooGPS (src/map/PositionsMap.js).
const CLUSTER_STATE_COLOR = { green: '#34d399', yellow: '#fbbf24', red: '#f87171' };

function donutSegment(cx, cy, start, end, outerR, innerR, color) {
  if (end - start >= 1) end = start + 0.99999; // anel completo — evita path degenerado
  const a0 = 2 * Math.PI * (start - 0.25);
  const a1 = 2 * Math.PI * (end - 0.25);
  const x0 = Math.cos(a0), y0 = Math.sin(a0);
  const x1 = Math.cos(a1), y1 = Math.sin(a1);
  const largeArc = end - start > 0.5 ? 1 : 0;
  return `<path d="M ${cx + innerR * x0} ${cy + innerR * y0}`
    + ` L ${cx + outerR * x0} ${cy + outerR * y0}`
    + ` A ${outerR} ${outerR} 0 ${largeArc} 1 ${cx + outerR * x1} ${cy + outerR * y1}`
    + ` L ${cx + innerR * x1} ${cy + innerR * y1}`
    + ` A ${innerR} ${innerR} 0 ${largeArc} 0 ${cx + innerR * x0} ${cy + innerR * y0}"`
    + ` fill="${color}"/>`;
}

// Constrói o ícone (data-URI SVG) do cluster a partir das contagens
// por estado. `counts` = { green, yellow, red }; `total` = nº total.
function buildClusterDonutIcon(counts, total) {
  const outerR = 21, halo = 2;
  const c = outerR + halo;          // centro
  const W = c * 2;                  // largura do SVG
  const innerR = 15;                  // anel mais fino — menos "carregado"
  const order = ['green', 'yellow', 'red'];
  const segs = order.map(k => counts[k] || 0);
  const sum = segs.reduce((a, b) => a + b, 0) || 1;
  let offset = 0, paths = '';
  for (let i = 0; i < segs.length; i++) {
    if (segs[i] <= 0) continue;
    paths += donutSegment(c, c, offset / sum, (offset + segs[i]) / sum, outerR, innerR, CLUSTER_STATE_COLOR[order[i]]);
    offset += segs[i];
  }
  const fontSize = total > 99 ? 13 : total > 9 ? 15 : 17;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${W}" viewBox="0 0 ${W} ${W}">`
    + `<circle cx="${c}" cy="${c}" r="${outerR + halo - 0.5}" fill="#fff"/>`
    + `<g opacity="0.9">${paths}</g>`
    + `<circle cx="${c}" cy="${c}" r="${innerR}" fill="#fff"/>`
    + `<text x="${c}" y="${c + 0.5}" text-anchor="middle" dominant-baseline="central"`
    + ` font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif"`
    + ` font-size="${fontSize}" font-weight="600" fill="#475569">${total}</text>`
    + `</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(W, W),
    anchor: new window.google.maps.Point(c, c),
  };
}

// ── Helpers para a aranha em hover sobre o cluster ─────────────────
// Converte um offset em PIXELS (ecrã) para LatLng, dado o centro e
// o mapa. Mantém os "braços" da aranha com tamanho constante em
// qualquer zoom (sem isto, os braços ficavam minúsculos a baixo
// zoom e gigantes a alto zoom).
function pixelOffsetToLatLng(map, centerLatLng, dx, dy) {
  if (!map || !window.google?.maps) return null;
  const proj = map.getProjection();
  if (!proj) return null;
  const zoom = map.getZoom();
  const scale = Math.pow(2, zoom);
  const worldPt = proj.fromLatLngToPoint(
    new window.google.maps.LatLng(centerLatLng.lat, centerLatLng.lng)
  );
  const newPt = new window.google.maps.Point(
    worldPt.x + dx / scale,
    worldPt.y + dy / scale,
  );
  const ll = proj.fromPointToLatLng(newPt);
  return { lat: ll.lat(), lng: ll.lng() };
}

// Acima deste nº de veículos por cluster a "aranha" fica ilegível
// (as bolhas das matrículas sobrepõem-se umas às outras a ponto de
// não dar para ler nada). Nesses clusters o hover não abre aranha —
// o operador faz zoom para os ver individualmente. O tooltip nativo
// do marker ainda dá o nº total para feedback.
const SPIDER_MAX_MEMBERS = 30;

// Pequena bolha SVG com a matrícula — usada nas pontas da aranha.
const SPIDER_BG = { green: '#22c55e', yellow: '#eab308', red: '#ef4444' };
function buildSpiderTipIcon(plate, status) {
  const bg = SPIDER_BG[status] || SPIDER_BG.red;
  const text = String(plate || '—').toUpperCase()
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const charW = 6.2, padX = 9;
  const w = Math.max(60, Math.min(160, padX * 2 + text.length * charW));
  const h = 22;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w + 4}" height="${h + 4}" viewBox="0 0 ${w + 4} ${h + 4}">`
    + `<defs><filter id="sp" x="-20%" y="-20%" width="140%" height="140%">`
    + `<feDropShadow dx="0" dy="1.5" stdDeviation="1.4" flood-color="#000" flood-opacity="0.35"/></filter></defs>`
    + `<g filter="url(#sp)">`
    + `<rect x="2" y="2" width="${w}" height="${h}" rx="6" ry="6" fill="${bg}" stroke="#fff" stroke-width="1.8"/>`
    + `</g>`
    + `<text x="${w / 2 + 2}" y="${h / 2 + 3}" text-anchor="middle" dominant-baseline="central"`
    + ` font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="11" font-weight="800"`
    + ` letter-spacing="0.3" fill="#fff">${text}</text>`
    + `</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    w: w + 4, h: h + 4,
  };
}

function MapViewGoogle() {
  const dispatch = useDispatch();
  const route = useSelector(selectCurrentRoute);
  // Pode usar atalhos "Origem / Destino" nos popups de favoritos e
  // veículos quando o operador está a planear uma rota MANUAL no mapa
  // — ou seja: rota nova (sem dbId) ou rascunho ainda editável. Em
  // rotas já planeadas/em curso/concluídas/abortadas os atalhos não
  // fazem sentido (a origem/destino estão fixas).
  // O selector também tem em conta o activeView: só no planeamento.
  // Calculado mais abaixo (depende de `activeView` que é desestruturado
  // a seguir) — exposto via `canQuickPickEndpoints`.
  // Percurso REAL do veículo (Redux) — partilhado com o painel de detalhes.
  const actualTrail = useSelector(selectActualTrail);
  // O mapa mostra TODOS os veículos (ativos + desativados); os desativados
  // aparecem com opacidade reduzida e um selo "DESATIVADO". Operações nunca
  // usam esta lista — só desenho/visual. Polling de posições só atualiza
  // os ativos (filtrado no backend antes do WS), por isso os desativados
  // ficam na última posição conhecida.
  const vehicles = useSelector(selectAllVehicles);
  const savedLocations = useSelector(selectLocations);
  const { lang, mapClickMode, hoveredStopIdx, highlightedStepIdx, resetMapView, displayedSavedRoutes, activeView, sidebarVisible } = useSelector(selectUI);
  const { token, clientSettings } = useContext(AuthContext);
  // Faz fetch do fullList (ativos + desativados) à entrada do mapa. Idempotente;
  // o list (ativos) continua a ser atualizado em paralelo pelo polling/WS.
  React.useEffect(() => {
    if (token) dispatch(fetchAllVehicles(token));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const tenantCenter = React.useMemo(() => {
    return centerFromSettings(clientSettings) || DEFAULT_CENTER_FALLBACK;
  }, [clientSettings?.defaultCountry, clientSettings?.timezone]);
  const didInitialFitRef = React.useRef(false);

  const mapRef = useRef(null);
  // Nível de zoom atual — os NOMES dos favoritos só aparecem a partir de um
  // certo zoom (senão, com muitos favoritos juntos, os nomes sobrepõem-se todos
  // e ficam ilegíveis; é assim que o Google Maps também faz com as labels).
  const [mapZoom, setMapZoom] = useState(7);
  const vehiclesRef = useRef(vehicles);
  useEffect(() => { vehiclesRef.current = vehicles; }, [vehicles]);

  // Ao entrar no Planeamento, mostra os veículos por defeito. Quando há
  // uma rota carregada, o `routeInContext` esconde a frota à mesma (só
  // ficam os veículos em simulação), por isso isto só tem efeito visível
  // quando NÃO há rota — exactamente o pedido. O operador pode togglar.
  useEffect(() => {
    if (activeView === 'planner') setShowVehicles(true);
  }, [activeView]);
  // Atalhos "Origem / Destino" nos popups (favoritos + veículos) —
  // disponíveis sempre que o operador está no planeamento. Se há uma
  // rota carregada no mapa (em qualquer estado: draft/planned/in_progress/
  // completed/aborted), o handler limpa-a primeiro — o gesto de clicar
  // "Origem" / "Destino" sobre um veículo/favorito é uma intenção clara
  // de começar uma rota nova, e não tem semântica se mantivesse a rota
  // anterior. O painel esquerdo é mostrado e descolapsado de seguida
  // para garantir que o operador vê o endpoint já preenchido.
  const canQuickPickEndpoints = activeView === 'planner';
  // Helper partilhado pelos popups (veículo, favorito) que aplica a
  // origem/destino com o ciclo completo: limpa a rota carregada,
  // popula o endpoint escolhido, abre+descolapsa o painel esquerdo,
  // e marca a rota como precisando de recálculo.
  //
  // Importante: o `clearRoute` apaga TUDO (incluindo o endpoint que o
  // operador já tinha preenchido na outra ponta). Por isso capturamos
  // o endpoint OPOSTO antes de limpar e re-aplicamo-lo depois — assim
  // o cenário "tenho destino no cliente X, clico num veículo como
  // origem" funciona: a rota antiga limpa-se mas o destino mantém-se.
  // `kind` é 'origin' ou 'destination'; `payload` é {name, coordinates}.
  const applyQuickPickEndpoint = useCallback((kind, payload) => {
    const keepOpposite = kind === 'origin' ? route?.destination : route?.origin;
    dispatch(clearRoute());
    if (kind === 'origin') {
      dispatch(setOrigin(payload));
      if (keepOpposite) dispatch(setDestination(keepOpposite));
    } else {
      dispatch(setDestination(payload));
      if (keepOpposite) dispatch(setOrigin(keepOpposite));
    }
    dispatch(setSidebarVisible(true));
    dispatch(setSidebarCollapsed(false));
    dispatch(setNeedsRecalculation(true));
  }, [dispatch, route]);
  // Aplica um VEÍCULO como origem/destino: o nome mostrado é a MORADA/zona
  // onde a viatura está (não a matrícula) — a matrícula fica em `vehiclePlate`
  // para o painel esquerdo a mostrar como tooltip. Usa a morada já resolvida
  // pelo EcooGPS; se não houver, faz reverse-geocode; fallback às coordenadas.
  const applyVehicleEndpoint = useCallback((kind, vehicle) => {
    const lat = Number(vehicle.position.lat);
    const lng = Number(vehicle.position.lng);
    const plate = vehicle.plate || `#${vehicle.id}`;
    const finalize = (addr) => {
      const name = addr || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      applyQuickPickEndpoint(kind, { name, coordinates: [lng, lat], vehiclePlate: plate });
    };
    if (vehicle.address) {
      finalize(vehicle.address);
    } else if (window.google && window.google.maps) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          finalize(status === 'OK' && results[0] ? results[0].formatted_address : null);
        });
      } catch (_) { finalize(null); }
    } else {
      finalize(null);
    }
  }, [applyQuickPickEndpoint]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  // Tacho data fetched on-demand when operator clicks a vehicle.
  // Shape: { loading, data, vehicleId } — data comes from /tacho-drivers (same as Frota screen).
  const [selectedVehicleTacho, setSelectedVehicleTacho] = useState(null);
  const [selectedVehicleConsumption, setSelectedVehicleConsumption] = useState(null); // { vehicleId, avgL100 } — consumo médio CAN (hoje)
  const [selectedVehiclePhoto, setSelectedVehiclePhoto] = useState(null); // { vehicleId, url, loading, uploading } — foto do veículo (upload do operador)
  const vehiclePhotoInputRef = React.useRef(null);
  // Mapa deviceId → rota que o veículo está a fazer agora (nº + nome), para
  // o pop-up do veículo. Alimentado por /routes/active-by-vehicle (poll).
  const [activeRouteByVehicle, setActiveRouteByVehicle] = useState({});
  const [selectedStop, setSelectedStop] = useState(null);
  const [highlightedSegment, setHighlightedSegment] = useState(null);
  // When viewing a route that was consolidated from multiple OTs, this stores the
  // orderId the operator is "monitoring" so we can highlight just that OT's leg.
  const [monitoringOTId, setMonitoringOTId] = useState(null);
  const [monitoringOrderedIds, setMonitoringOrderedIds] = useState([]);
  // Per-OT legs breakdown for consolidated routes — shown as a bottom panel.
  // Each entry: { orderId, orderNumber, distanceKm, durationMin, pickupName, deliveryName }
  const [monitoringRouteLegs, setMonitoringRouteLegs] = useState([]);
  const [vehicleHistory, setVehicleHistory] = useState(null); // { vehicle, data, loading }
  const [historyTrail, setHistoryTrail] = useState(null); // google Polyline path (dia inteiro)
  const [selectedTrips, setSelectedTrips] = useState(() => new Set()); // índices de viagens selecionadas (mostra só essas)
  const [playbackCursor, setPlaybackCursor] = useState(null); // { lat, lng, idx, heading, ... } reprodução do histórico
  const [playbackFollow, setPlaybackFollow] = useState(false); // câmara acompanha a viatura no replay
  // Seguir a viatura: a cada novo ponto do replay, centra suavemente o mapa
  // (só quando o "seguir" está ligado). Não altera o zoom — o operador manda.
  useEffect(() => {
    if (!playbackFollow || !playbackCursor || !mapRef.current) return;
    const { lat, lng } = playbackCursor;
    if (Number.isFinite(lat) && Number.isFinite(lng)) mapRef.current.panTo({ lat, lng });
  }, [playbackFollow, playbackCursor]);
  // Eventos do histórico para marcar no mapa e na barra de tempo:
  //  - excesso (velocidade > limite) agrupado em segmentos (1 marcador/início);
  //  - paragens longas (parado ≥ N min) no ponto onde parou.
  const HISTORY_SPEED_LIMIT = 90; // km/h — limiar de excesso no replay
  const HISTORY_STOP_MIN = 5;     // min — duração mínima para marcar paragem
  const historyEvents = useMemo(() => {
    const pts = vehicleHistory?.data?.points || [];
    const speeding = []; const stops = [];
    let inSpeed = false;
    for (let i = 0; i < pts.length; i++) {
      const s = Number(pts[i].speed) || 0;
      if (s > HISTORY_SPEED_LIMIT) {
        if (!inSpeed) { speeding.push({ idx: i, lat: Number(pts[i].lat), lng: Number(pts[i].lng), speed: Math.round(s), timestamp: pts[i].timestamp }); inSpeed = true; }
        else if (s > speeding[speeding.length - 1].speed) speeding[speeding.length - 1].speed = Math.round(s);
      } else inSpeed = false;
    }
    let j = 0;
    while (j < pts.length) {
      if ((Number(pts[j].speed) || 0) <= 3) {
        let k = j; while (k + 1 < pts.length && (Number(pts[k + 1].speed) || 0) <= 3) k++;
        const durMin = pts[k].timestamp && pts[j].timestamp ? (new Date(pts[k].timestamp) - new Date(pts[j].timestamp)) / 60000 : 0;
        if (durMin >= HISTORY_STOP_MIN) stops.push({ idx: j, lat: Number(pts[j].lat), lng: Number(pts[j].lng), durMin: Math.round(durMin), timestamp: pts[j].timestamp });
        j = k + 1;
      } else j++;
    }
    return { speeding, stops };
  }, [vehicleHistory]);
  // Trajeto do dia partido em troços NORMAL (azul) / EXCESSO (vermelho) —
  // como o EcooGPS, que pinta a vermelho onde a velocidade passa o limite.
  const historyTrailSegments = useMemo(() => {
    const pts = (vehicleHistory?.data?.points || [])
      .filter(p => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)));
    const segs = []; let cur = null;
    for (const p of pts) {
      const over = (Number(p.speed) || 0) > HISTORY_SPEED_LIMIT;
      if (!cur || cur.over !== over) {
        if (cur) cur.path.push({ lat: Number(p.lat), lng: Number(p.lng) }); // liga ao troço anterior
        cur = { over, path: [] };
        segs.push(cur);
      }
      cur.path.push({ lat: Number(p.lat), lng: Number(p.lng) });
    }
    return segs.filter(s => s.path.length > 1);
  }, [vehicleHistory]);
  // POIs perto do trajeto do dia — favoritos/geofences (o EcooGPS mostra os
  // POIs a partir das geofences). Filtra a ≤1,5 km do percurso, com amostragem
  // e teto para não pesar nem poluir o mapa.
  const historyPois = useMemo(() => {
    if (!vehicleHistory?.data || !Array.isArray(savedLocations) || !savedLocations.length) return [];
    const pts = (vehicleHistory.data.points || []).filter(p => Number.isFinite(Number(p.lat)));
    if (pts.length < 2) return [];
    const step = Math.max(1, Math.floor(pts.length / 120));
    const sample = []; for (let i = 0; i < pts.length; i += step) sample.push(pts[i]);
    const R = 6371, toR = x => x * Math.PI / 180;
    const dkm = (a, b, c, d) => { const dLat = toR(c - a), dLng = toR(d - b); const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a)) * Math.cos(toR(c)) * Math.sin(dLng / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(s)); };
    const near = [];
    for (const loc of savedLocations) {
      const la = Number(loc.latitude), ln = Number(loc.longitude);
      if (!Number.isFinite(la) || !Number.isFinite(ln)) continue;
      if (sample.some(p => dkm(la, ln, Number(p.lat), Number(p.lng)) <= 1.5)) near.push(loc);
      if (near.length >= 60) break;
    }
    return near;
  }, [vehicleHistory, savedLocations]);
  // Veículo destacado ao clicar no card "Em trânsito" — anel de foco temporário.
  const [focusedVehicleId, setFocusedVehicleId] = useState(null);
  const focusClearRef = useRef(null);
  // Histórico: métrica do gráfico — 'speed' (velocidade) ou 'battery' (bateria).
  const [historyMetric, setHistoryMetric] = useState('speed');
  // Painel de viagens (flutuante, arrastável) — posição {left, top} em px.
  const [histPanelPos, setHistPanelPos] = useState(null);
  const [histPanelSize, setHistPanelSize] = useState(null); // { width, height } — painel de Viagens redimensionável
  // Re-render quando um ícone de veículo (EcooGPS) acaba de carregar.
  const [, setIconTick] = useState(0);
  const [stopPhoto, setStopPhoto] = useState(null);
  const [showVehicles, setShowVehicles] = useState(true); // visíveis por defeito sem rota carregada; operador toggla quando quiser
  // Info por posição "lat,lng" — lida pelo renderer do cluster (donut)
  // para colorir o anel E para a animação aranha em hover (mostra
  // matrículas dos veículos do cluster). Reconstruído a cada render.
  const vehicleInfoByPos = useRef(new Map());
  // Hover sobre uma circunferência (cluster) → mostra "aranha" com cada
  // ponta a apontar para uma matrícula dos veículos agrupados.
  const [hoveredCluster, setHoveredCluster] = useState(null);
  // Timer de tolerância — mantém a aranha visível ~250 ms depois do
  // mouseout para o operador conseguir mover para uma ponta sem ela
  // desaparecer (também rearmado pelos próprios tips, mais abaixo).
  const spiderHideTimerRef = useRef(null);
  const cancelSpiderHide = useCallback(() => {
    if (spiderHideTimerRef.current) {
      clearTimeout(spiderHideTimerRef.current);
      spiderHideTimerRef.current = null;
    }
  }, []);
  const scheduleSpiderHide = useCallback(() => {
    cancelSpiderHide();
    spiderHideTimerRef.current = setTimeout(() => setHoveredCluster(null), 250);
  }, [cancelSpiderHide]);

  // Abre o popup detalhado do veículo (mesmo InfoWindow que o click no
  // marcador normal — incluindo o fetch on-demand do tacógrafo). Usado:
  //   1) pelo onClick do marcador individual no mapa;
  //   2) pelo onClick de uma "perna" da aranha — clicar na bolha da
  //      matrícula com a aranha aberta abre o popup sem fazer zoom.
  const openVehiclePopup = useCallback(async (v) => {
    if (!v) return;
    setSelectedVehicle(v);
    setSelectedVehicleTacho({ loading: true, data: null, vehicleId: v.id });
    // Posiciona a viatura na zona BAIXA do mapa para a popup (que abre para
    // cima) caber toda abaixo da barra de topo da app. Auto-pan do InfoWindow
    // está desligado (disableAutoPan), por isso controlamos nós o pan aqui.
    try {
      const map = mapRef.current;
      const pos = v.position;
      if (map && pos && window.google) {
        map.panTo(pos);
        const h = map.getDiv()?.offsetHeight || 760;
        const HEADER_CLEAR = 72;           // barra de topo + margem
        const POPUP_H = 480;               // altura estimada da popup (com tacógrafo)
        const TAIL = 24;
        // marcador alvo (px do topo): popup cabe entre a barra e o marcador
        let targetY = Math.min(HEADER_CLEAR + POPUP_H + TAIL, h - 48);
        const dy = targetY - h / 2;        // deslocar o marcador do centro para targetY
        if (Math.abs(dy) > 4) map.panBy(0, -dy); // conteúdo desce → popup fica abaixo da barra
      }
    } catch { /* pan é best-effort */ }
    try {
      const res = await fetch('/planner-api/drivers/tacho-drivers', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('fetch failed');
      const list = await res.json();
      let td = null;
      if (Array.isArray(list)) {
        const matches = list.filter(d => String(d.vehicleId) === String(v.id));
        const ts = (s) => { if (!s) return 0; const t = Date.parse(String(s).replace(' ', 'T') + 'Z'); return isNaN(t) ? 0 : t; };
        matches.sort((a, b) => ts(b.lastSeen || b.lastUpdate) - ts(a.lastSeen || a.lastUpdate));
        td = matches[0] || null;
      }
      setSelectedVehicleTacho({ loading: false, data: td, vehicleId: v.id });
    } catch {
      setSelectedVehicleTacho({ loading: false, data: null, vehicleId: v.id });
    }
  }, [token]);

  // Consumo médio (L/100km) do dia para a popup — viaturas CAN que enviam
  // attributes.consumption. Endpoint leve; só mostra o tile quando há dados.
  useEffect(() => {
    const id = selectedVehicle?.id;
    if (!id || !token) { setSelectedVehicleConsumption(null); return; }
    let cancelled = false;
    setSelectedVehicleConsumption({ vehicleId: id, loading: true });
    const cap = String(selectedVehicle?.fuelCapacity || '').replace(',', '.').replace(/[^\d.]/g, '');
    const capQ = cap ? `?capacity=${encodeURIComponent(cap)}` : '';
    fetch(`/planner-api/vehicles/${id}/consumption${capQ}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setSelectedVehicleConsumption({ vehicleId: id, loading: false, ...(d || {}) }); })
      .catch(() => { if (!cancelled) setSelectedVehicleConsumption({ vehicleId: id, loading: false }); });
    return () => { cancelled = true; };
  }, [selectedVehicle?.id, selectedVehicle?.fuelCapacity, token]);

  // Foto do veículo (upload do operador) — carrega quando a popup abre.
  useEffect(() => {
    const id = selectedVehicle?.id;
    if (!id || !token) { setSelectedVehiclePhoto(null); return; }
    let cancelled = false;
    setSelectedVehiclePhoto({ vehicleId: id, loading: true, url: null });
    fetch(`/planner-api/vehicles/${id}/photo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setSelectedVehiclePhoto({ vehicleId: id, loading: false, url: d?.photoUrl || null }); })
      .catch(() => { if (!cancelled) setSelectedVehiclePhoto({ vehicleId: id, loading: false, url: null }); });
    return () => { cancelled = true; };
  }, [selectedVehicle?.id, token]);

  const handleVehiclePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    const id = selectedVehicle?.id;
    if (!file || !id || !token) return;
    setSelectedVehiclePhoto(p => ({ ...(p || {}), vehicleId: id, uploading: true }));
    try {
      const dataUrl = await fileToResizedDataURL(file);
      const res = await fetch(`/planner-api/vehicles/${id}/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo: dataUrl }),
      });
      if (res.ok) {
        const d = await res.json();
        setSelectedVehiclePhoto({ vehicleId: id, loading: false, uploading: false, url: d.photoUrl });
      } else {
        setSelectedVehiclePhoto(p => ({ ...(p || {}), uploading: false }));
      }
    } catch (_) {
      setSelectedVehiclePhoto(p => ({ ...(p || {}), uploading: false }));
    }
  };

  const handleVehiclePhotoRemove = async () => {
    const id = selectedVehicle?.id;
    if (!id || !token) return;
    setSelectedVehiclePhoto(p => ({ ...(p || {}), uploading: true }));
    try { await fetch(`/planner-api/vehicles/${id}/photo`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); } catch (_) { /* ignora */ }
    setSelectedVehiclePhoto({ vehicleId: id, loading: false, uploading: false, url: null });
  };

  // Rota que cada veículo está a fazer agora (deviceId → {orderNumber, name}).
  // Poll leve a cada 45s para o pop-up estar sempre actualizado.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch('/planner-api/routes/active-by-vehicle', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const map = await res.json();
        if (!cancelled) setActiveRouteByVehicle(map || {});
      } catch { /* silencioso — pop-up cai para "sem rota" */ }
    };
    load();
    const iv = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [token]);
  // Opções do cluster — renderer próprio que desenha um donut com o anel
  // colorido por estado (igual ao EcooGPS). Memoizado uma vez: lê o estado
  // actual via ref, por isso o clusterer não é recriado a cada poll.
  const clustererOptions = useMemo(() => ({
    renderer: {
      render: ({ count, position, markers }) => {
        const counts = { green: 0, yellow: 0, red: 0 };
        const members = [];
        (markers || []).forEach(m => {
          const p = m.getPosition && m.getPosition();
          const key = p ? `${p.lat().toFixed(6)},${p.lng().toFixed(6)}` : null;
          const info = (key && vehicleInfoByPos.current.get(key)) || null;
          const s = info?.status || 'red';
          counts[s] = (counts[s] || 0) + 1;
          if (info) members.push(info);
        });
        const tooManyForSpider = count > SPIDER_MAX_MEMBERS;
        const marker = new window.google.maps.Marker({
          position,
          icon: buildClusterDonutIcon(counts, count),
          zIndex: 1000 + count,
          // Tooltip nativo do browser quando o cluster é demasiado grande
          // para a aranha — diz ao operador o nº de veículos e que tem
          // de fazer zoom para os ver. Para clusters pequenos não pomos
          // tooltip (a aranha mostra logo as matrículas).
          title: tooManyForSpider
            ? `${count} veículos — faz zoom para os ver`
            : undefined,
        });
        // Hover → aranha. Capturamos a posição actual do marker (não
        // a `position` original — o clusterer pode mover-se entre
        // renders). A lista de membros vai junto no estado. Para
        // clusters acima do limite (> SPIDER_MAX_MEMBERS), ignora o
        // hover — abriria uma aranha ilegível com matrículas todas
        // sobrepostas. O operador faz zoom in para os ver.
        marker.addListener('mouseover', () => {
          cancelSpiderHide();
          if (tooManyForSpider) return;
          const p = marker.getPosition();
          if (!p) return;
          setHoveredCluster({
            id: `${p.lat().toFixed(6)},${p.lng().toFixed(6)}`,
            center: { lat: p.lat(), lng: p.lng() },
            vehicles: members,
          });
        });
        marker.addListener('mouseout', scheduleSpiderHide);
        return marker;
      },
    },
  }), [cancelSpiderHide, scheduleSpiderHide]);
  const [showNoPosList, setShowNoPosList] = useState(false); // toggle the "no GPS fix" popover
  const [showTraffic, setShowTraffic] = useState(false);
  // HERE traffic incidents — accidents, road closures, road works,
  // congestions in the current map viewport. Fetched on toggle ON and
  // refreshed when the operator pans/zooms (idle event, debounced).
  const [showIncidents, setShowIncidents] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const incidentsFetchTimerRef = useRef(null);
  const [streetViewOpen, setStreetViewOpen] = useState(false);
  const [streetViewPos, setStreetViewPos] = useState(null);
  const streetViewRef = useRef(null);
  const svPanoramaRef = useRef(null);
  const [addFavMode, setAddFavMode] = useState(false);
  const [serviceMarkers, setServiceMarkers] = useState([]); // POI markers from RouteServicesPanel
  const [hoveredService, setHoveredService] = useState(null); // Service for InfoWindow on hover
  const [showLayerMenu, setShowLayerMenu] = useState(false);
  const [showLocationsOnMap, setShowLocationsOnMap] = useState(false);
  const [nearRouteDistance, setNearRouteDistance] = useState(null);
  const [singleVehicleId, setSingleVehicleId] = useState(null); // show only this vehicle // null = show all, number = km filter
  const [selectedLocationsOnMap, setSelectedLocationsOnMap] = useState(null); // null=show all, array=show specific
  const [showFavMenu, setShowFavMenu] = useState(false);
  // Replay animado do percurso real (rasto GPS) — só quando há trail carregado.
  const [replayOpen, setReplayOpen] = useState(false);
  // Limiar de zoom a partir do qual os NOMES dos favoritos aparecem no mapa.
  // Escolhido pelo operador no submenu do botão Favoritos e persistido. 0 =
  // sempre; 99 = nunca (só ao clicar no pin).
  const [favNameZoom, setFavNameZoom] = useState(() => {
    try { const raw = localStorage.getItem('favNameZoom'); const v = raw != null ? Number(raw) : 8; return Number.isFinite(v) ? v : 8; } catch { return 8; }
  });
  const setFavNameZoomPersist = (z) => { setFavNameZoom(z); try { localStorage.setItem('favNameZoom', String(z)); } catch {} };
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [mapTypeId, setMapTypeId] = useState('roadmap');
  const [searchPin, setSearchPin] = useState(null); // { lat, lng } from SearchBar
  const [searchPinOpen, setSearchPinOpen] = useState(false); // InfoWindow open
  // Limpa o pin de pesquisa ao sair do planeamento. Sem isto, o pin vermelho
  // (e o popup) ficavam no mapa ao mudar para Ordens/Frota e voltar ao
  // planeamento — só saíam com um F5. Cobre o caso de mudar de ecrã com o
  // popup ainda aberto; fechar o popup já remove o pin (onCloseClick).
  useEffect(() => {
    if (activeView !== 'planner') { setSearchPin(null); setSearchPinOpen(false); }
  }, [activeView]);
  const [geofences, setGeofences] = useState([]);
  const [showGeofences, setShowGeofences] = useState(true);
  // Mapa groupId → nome do grupo EcooGPS. Carregado uma vez por sessão
  // (são raros e mudam pouco). Usado na popup do veículo para mostrar
  // a que grupo pertence (ex.: "Stock Lisboa", "Viaturas activas").
  const [groupNameById, setGroupNameById] = useState({});
  const [geofenceAlerts, setGeofenceAlerts] = useState([]);
  const trafficLayerRef = useRef(null);
  // Ghost marker on polyline hover
  const [ghostMarker, setGhostMarker] = useState(null); // { lat, lng }
  // Selected route point (origin/dest/waypoint) popup
  const [selectedPoint, setSelectedPoint] = useState(null); // { type, name, pos, photo }
  const [pointPhoto, setPointPhoto] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  // On first map load, fit bounds para mostrar as viaturas. Quando a
  // frota está em mais do que um país (ex. Petrotec PT+AO), o operador
  // não quer ver o mundo inteiro — preferimos centrar no país com mais
  // viaturas. Subsequentes vehicle updates não re-fazem o fit (preserva
  // pan/zoom do utilizador).
  useEffect(() => {
    if (didInitialFitRef.current) return;
    if (!isLoaded || !mapRef.current) return;
    const isValidCoord = (v) =>
      v.position
      && typeof v.position.latitude === 'number' && typeof v.position.longitude === 'number'
      && v.position.latitude >= -90 && v.position.latitude <= 90
      && v.position.longitude >= -180 && v.position.longitude <= 180
      && !(v.position.latitude === 0 && v.position.longitude === 0);
    const withPos = (vehicles || []).filter(isValidCoord);
    if (withPos.length === 0) return;

    // Tenta inferir país dominante. Se a frota for multipaís, fitBounds
    // apenas às viaturas do país com mais viaturas — caso contrário o
    // mundo inteiro aparece com zoom 2.
    const dom = dominantCountry(withPos);
    const pts = (dom && dom.points.length > 0)
      ? dom.points
      : withPos.map(v => ({ lat: v.position.latitude, lng: v.position.longitude }));

    if (pts.length === 1) {
      mapRef.current.panTo({ lat: pts[0].lat, lng: pts[0].lng });
      mapRef.current.setZoom(12);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      pts.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
      mapRef.current.fitBounds(bounds, { top: 80, bottom: 80, left: 80, right: 80 });
      const capZoom = () => {
        const z = mapRef.current?.getZoom();
        if (z != null && z > 14) mapRef.current.setZoom(14);
      };
      const listener = mapRef.current.addListener('idle', () => {
        capZoom();
        listener.remove();
      });
    }
    didInitialFitRef.current = true;
  }, [isLoaded, vehicles]);

  const cr = route.computedRoute;

  // Route coordinates (raw)
  const routeCoords = useMemo(() => cr?.geometry?.coordinates || [], [cr]);

  // Route path for main polyline
  const routePath = useMemo(() => {
    return routeCoords.map(c => ({ lat: c[1], lng: c[0] }));
  }, [routeCoords]);

  // Versão decimada do routePath para Polylines do Google Maps quando
  // o número de vértices é exagerado. Rotas de 400+ km vêm com 10–15k
  // pontos da HERE/Google — desnecessário para o utilizador (sub-pixel
  // a qualquer zoom típico) e custa setPath caro. Decimação a ≤1500
  // pontos preserva a forma e acelera muito o re-render.
  const routePathDecimated = useMemo(() => {
    if (routePath.length <= 1500) return routePath;
    const step = Math.ceil(routePath.length / 1500);
    return routePath.filter((_, i) => i % step === 0 || i === routePath.length - 1);
  }, [routePath]);

  // Conectores de "última milha" nas pontas. O HERE encaixa (snap) a
  // origem/destino na estrada conhecida mais próxima; em zonas de dados
  // fracos (ex.: portos em Angola) essa estrada pode ficar ~100 m do ponto
  // real. Como o mapa de fundo é Google (grafo diferente), isso aparece
  // como o marco afastado do início/fim da linha. Desenhamos um conector
  // pontilhado marco↔rota (como o Google Maps faz) SÓ quando há folga real
  // — com provider que alinha com o fundo (Google) a folga é ~0 e nada é
  // desenhado, por isso é inócuo para os restantes tenants.
  const endpointConnectors = useMemo(() => {
    const out = [];
    if (routeCoords.length < 2) return out;
    const MIN_DEG = 0.00018; // ~20 m — abaixo disto não vale a pena ligar
    const far = (a, b) => a && b
      && (Math.abs(a[0] - b[0]) > MIN_DEG || Math.abs(a[1] - b[1]) > MIN_DEG);
    const oc = route.origin?.coordinates;
    const dc = route.destination?.coordinates;
    const first = routeCoords[0];
    const last = routeCoords[routeCoords.length - 1];
    if (oc && far(oc, first)) out.push([{ lat: oc[1], lng: oc[0] }, { lat: first[1], lng: first[0] }]);
    if (dc && far(dc, last))  out.push([{ lat: last[1], lng: last[0] }, { lat: dc[1], lng: dc[0] }]);
    return out;
  }, [routeCoords, route.origin, route.destination]);

  // Options memoizadas para as Polylines do modo de inspecção. Antes
  // eram criadas inline a cada render → react-google-maps fazia
  // setOptions a cada actualização (mesmo com o mesmo conteúdo) → o
  // Google Maps repintava o polyline. Constants estáveis = zero
  // trabalho extra no SDK do Google Maps.
  const INSPECT_MAIN_POLYLINE_OPTS = useMemo(() => ({
    strokeColor: '#2563eb',
    strokeOpacity: 0.85,
    strokeWeight: 6,
    // geodesic: false — arcos de grande-círculo entre vértices só
    // fazem diferença visual em rotas transcontinentais. Para
    // qualquer rota PT/ES/Angola, linhas rectas entre vértices são
    // indistinguíveis e o render é 30-50% mais rápido (sem
    // tessellation por segmento na SDK do Google Maps).
    geodesic: false,
    clickable: false,
    zIndex: 2,
  }), []);
  const INSPECT_OT_LEG_POLYLINE_OPTS = useMemo(() => ({
    strokeColor: '#f97316',
    strokeOpacity: 0.95,
    strokeWeight: 5,
    geodesic: false,
    clickable: false,
    zIndex: 500,
  }), []);
  // Polyline amarela do troço REAL da OT (GPS) — sobreposta ao trajecto
  // verde/vermelho do trail. Em rotas concluídas, o operador clica no
  // card da OT e quer ver o que o motorista REALMENTE fez, não o que
  // estava planeado. Por isso zIndex 502 (acima dos segmentos do trail).
  const INSPECT_OT_LEG_REAL_POLYLINE_OPTS = useMemo(() => ({
    strokeColor: '#facc15',
    strokeOpacity: 1,
    strokeWeight: 6,
    geodesic: false,
    clickable: false,
    zIndex: 502,
  }), []);

  // Rotas alternativas — geometria pré-convertida para lat/lng (mesmo
  // formato da rota principal). Pintadas a tracejado cinza por baixo
  // do polyline azul principal para o operador comparar caminhos.
  // Antes só o MapView (Mapbox) renderizava alternativas — o
  // MapViewGoogle (o mapa que de facto vemos) não tinha qualquer
  // referência a `route.alternatives`, ficando invisíveis na UI real.
  const alternativePaths = useMemo(() => {
    const alts = route?.alternatives || [];
    return alts.map(alt => {
      const coords = alt?.geometry?.coordinates || [];
      return coords.map(c => ({ lat: c[1], lng: c[0] }));
    }).filter(p => p.length > 1);
  }, [route?.alternatives]);

  // Ícones de veículo (EcooGPS) — re-render assim que cada PNG é
  // convertido em base64 e fica disponível para o marcador.
  useEffect(() => onVehicleIconLoaded(() => setIconTick(t => t + 1)), []);

  // Percurso REAL do veículo — carregado para o Redux quando a rota
  // aberta já está em trânsito ou concluída. Partilhado com o painel de
  // detalhes (RouteDetails), que mostra as métricas.
  useEffect(() => {
    const id = route?.dbId;
    const st = route?.dbStatus;
    if (id && token && (st === 'in_progress' || st === 'completed')) {
      dispatch(fetchActualTrail({ token, routeId: id }));
    } else {
      dispatch(clearActualTrail());
    }
  }, [route?.dbId, route?.dbStatus, token, dispatch]);

  // Fecha o replay se o rasto desaparecer (mudou de rota / limpou).
  useEffect(() => { if (!(actualTrail?.trail?.length > 1)) setReplayOpen(false); }, [actualTrail]);
  // O botão "Replay do percurso" vive na barra de topo (MapToolbar) e abre o
  // painel via evento — mantém o MapToolbar desacoplado do estado do replay.
  useEffect(() => {
    const onOpen = () => setReplayOpen(true);
    window.addEventListener('openRouteReplay', onOpen);
    return () => window.removeEventListener('openRouteReplay', onOpen);
  }, []);

  // Divide o percurso real em troços dentro/fora da rota planeada — os
  // troços fora (>250 m do plano) são desvios, destacados a vermelho.
  const actualTrailSegments = useMemo(
    () => analyzeTrail(actualTrail?.trail, routeCoords).segments,
    [actualTrail, routeCoords],
  );

  // Histórico do veículo — data de hoje (local) em YYYY-MM-DD.
  const todayStr = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  };
  // Carrega o histórico de um veículo para um INTERVALO de datas (De → Até).
  // `dayFrom`/`dayTo` em YYYY-MM-DD. Sem `dayTo` (ou igual) = um só dia.
  function loadVehicleHistory(vehicle, dayFrom, dayTo) {
    if (!vehicle?.id || !token) return;
    let df = dayFrom || todayStr();
    let dt = dayTo || df;
    if (dt < df) { const tmp = df; df = dt; dt = tmp; } // garante De ≤ Até
    setVehicleHistory({ vehicle, data: null, loading: true, day: df, dayTo: dt });
    setPlaybackCursor(null);
    const dayStart = new Date(`${df}T00:00:00`);
    const dayEnd = dt >= todayStr() ? new Date() : new Date(`${dt}T23:59:59`);
    const qs = `from=${encodeURIComponent(dayStart.toISOString())}&to=${encodeURIComponent(dayEnd.toISOString())}`;
    fetch(`/planner-api/vehicles/${vehicle.id}/history?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setVehicleHistory(prev => (prev && prev.vehicle?.id === vehicle.id)
          ? { ...prev, data, loading: false } : prev);
        if (data.points?.length > 1) {
          setHistoryTrail(data.points.map(p => ({ lat: p.lat, lng: p.lng })));
          setSelectedTrips(new Set());
          if (mapRef.current) {
            const bounds = new window.google.maps.LatLngBounds();
            data.points.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
            mapRef.current.fitBounds(bounds, 60);
          }
        } else {
          setHistoryTrail(null);
        }
      })
      .catch(() => setVehicleHistory(prev => prev ? { ...prev, loading: false } : null));
  }

  // Marcador móvel da reprodução do histórico — bolinha azul com seta de
  // rumo. Distinto dos marcadores das viaturas em tempo real.
  const playbackMarkerIcon = (heading) => {
    const h = ((Number(heading) || 0) % 360 + 360) % 360;
    const S = 34, c = S / 2;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${S}" height="${S}" viewBox="0 0 ${S} ${S}">`
      + `<defs><filter id="pbs" x="-50%" y="-50%" width="200%" height="200%"><feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="#000" flood-opacity="0.35"/></filter></defs>`
      + `<circle cx="${c}" cy="${c}" r="13" fill="#fff" filter="url(#pbs)"/>`
      + `<circle cx="${c}" cy="${c}" r="11" fill="#2563eb"/>`
      + `<g transform="rotate(${h} ${c} ${c})"><path d="M ${c} ${c - 7} L ${c + 4.6} ${c + 5.2} L ${c} ${c + 2.6} L ${c - 4.6} ${c + 5.2} Z" fill="#fff"/></g>`
      + `</svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(S, S),
      anchor: new window.google.maps.Point(c, c),
    };
  };

  // Marcador de reprodução com o ÍCONE DO VEÍCULO (2D EcooGPS), rodado pelo
  // rumo e colorido pelo ESTADO do momento (verde=condução, amarelo=ralenti,
  // vermelho=parado). Fallback à seta enquanto o PNG do tipo não carrega.
  const playbackVehicleIcon = (heading, status, type) => {
    const cat = vehicleIconCategory(type);
    const b64 = getVehicleIconDataUrl(cat, status, heading) || getVehicleIconDataUrl(cat, 'gray', heading);
    if (!b64) return playbackMarkerIcon(heading); // ainda a carregar → seta
    const h = vehicleIconRotation(cat, heading);
    const zf = Math.max(0.28, Math.min(1, mapZoom / 17 + 0.1));
    const S = Math.round(Math.max(20, 45 * vehicleIconZoom(cat) * zf));
    const box = Math.ceil(S * 1.5); // folga para a rotação
    const c = box / 2;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${box}" height="${box}" viewBox="0 0 ${box} ${box}">`
      + `<g transform="rotate(${h} ${c} ${c})">`
      + `<image href="${b64}" x="${c - S / 2}" y="${c - S / 2}" width="${S}" height="${S}"/>`
      + `</g></svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(box, box),
      anchor: new window.google.maps.Point(c, c),
    };
  };

  // Anel de destaque (halo) para o veículo clicado no card "Em trânsito".
  // Dois anéis concêntricos âmbar, centro transparente (o ícone do veículo
  // vê-se através). Ajuda a identificar a viatura de imediato.
  const focusRingIcon = () => {
    const S = 96, c = S / 2;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${S}" height="${S}" viewBox="0 0 ${S} ${S}">`
      + `<circle cx="${c}" cy="${c}" r="${c - 6}" fill="#f59e0b" fill-opacity="0.12" stroke="#f59e0b" stroke-opacity="0.35" stroke-width="2"/>`
      + `<circle cx="${c}" cy="${c}" r="${c - 20}" fill="none" stroke="#f59e0b" stroke-width="4"/>`
      + `</svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(S, S),
      anchor: new window.google.maps.Point(c, c),
    };
  };

  // Return-home leg detection — when the route's destination is the
  // driver's home (its name starts with "🏠"), we colour the last segment
  // of the polyline distinctly so the operator can spot at a glance that
  // it's the technician returning home rather than another customer
  // delivery. Threshold: from the closest coord to the last waypoint up
  // to the route end.
  const returnHomePath = useMemo(() => {
    if (!route?.destination?.name) return null;
    if (!String(route.destination.name).startsWith('🏠')) return null;
    const waypoints = route.waypoints || [];
    if (waypoints.length === 0 || routeCoords.length < 2) return null;
    // Last waypoint = the OT delivery just before the home leg
    const lastWp = waypoints[waypoints.length - 1];
    const lastCoords = lastWp?.coordinates;
    if (!Array.isArray(lastCoords) || lastCoords.length !== 2) return null;
    // Find the closest coord index to the last waypoint
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < routeCoords.length; i++) {
      const c = routeCoords[i];
      const dx = c[0] - lastCoords[0];
      const dy = c[1] - lastCoords[1];
      const d = dx * dx + dy * dy;
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    // Slice from that point to the end of the polyline (the home itself).
    if (bestIdx >= routeCoords.length - 1) return null;
    return routeCoords.slice(bestIdx).map(c => ({ lat: c[1], lng: c[0] }));
  }, [route, routeCoords]);

  // When viewing a consolidated route in the context of a specific OT, compute the
  // sub-segment of the polyline that represents that OT's pickup → delivery leg.
  // Works by finding the coordinate indices closest to the pickup and delivery points
  // of the OT (either the route.origin/destination or a waypoint with otId === monitoringOTId).
  const otLegPath = useMemo(() => {
    if (!monitoringOTId || routeCoords.length < 2) return null;
    const waypoints = route.waypoints || [];
    const hasAnyOTWaypoint = waypoints.some(w => w.otId);

    // Special pseudo-id for the "return to home" leg of a consolidated route.
    // pickup = last delivery waypoint, delivery = route destination.
    // For round-trip routes (origin == destination) we cannot use nearestIdx
    // for the destination because it would match the start of the polyline
    // (index 0). The polyline ALWAYS ends at the destination by construction,
    // so use coords.length - 1 directly.
    if (String(monitoringOTId) === '__return__') {
      const orderedIds = (monitoringOrderedIds || []).map(String).filter(id => id !== '__return__');
      const lastOTId = orderedIds[orderedIds.length - 1];
      const lastDeliveryWp = lastOTId
        ? waypoints.find(w => String(w.otId) === lastOTId && w.waypointKind === 'delivery')
        : null;
      const pickupCoord = lastDeliveryWp?.coordinates;
      if (!pickupCoord || routeCoords.length < 2) return null;
      const nearestIdx = (c) => {
        let best = 0, bestD = Infinity;
        for (let i = 0; i < routeCoords.length; i++) {
          const dx = routeCoords[i][0] - c[0];
          const dy = routeCoords[i][1] - c[1];
          const d = dx * dx + dy * dy;
          if (d < bestD) { bestD = d; best = i; }
        }
        return best;
      };
      const from = nearestIdx(pickupCoord);
      const to = routeCoords.length - 1; // polyline tail = route destination
      if (to - from < 2) return null;
      return routeCoords.slice(from, to + 1).map(c => ({ lat: c[1], lng: c[0] }));
    }

    // Build the full ordered stop list exactly like the panel + backend do:
    //   [origin, ...waypoints, destination]
    // The leg for an OT is "the stretch that ARRIVES at this OT's stop":
    // previous stop → this OT's delivery/service point. This mirrors
    // server/src/utils/routeLegs.js and — crucially — works for assistance
    // OTs whose single waypoint has waypointKind 'service' (no pickup/delivery
    // pair). The old code only looked for a previous 'delivery' waypoint, which
    // never matches a 'service' stop, so the pickup fell back to the route
    // origin and the orange leg spanned the whole route ("rasto").
    let pickupCoord = null;
    let deliveryCoord = null;
    if (hasAnyOTWaypoint) {
      const points = [
        route.origin?.coordinates ? { coordinates: route.origin.coordinates, otId: null } : null,
        ...waypoints.filter(w => w.coordinates),
        // Destino: preservar otId/waypointKind quando a OT terminal da
        // rota é o destino (típico em rotas consolidadas — a última OT
        // serve como destination, não waypoint). Sem isto, clicar no
        // card dessa OT não destacava o troço amarelo no mapa.
        route.destination?.coordinates ? {
          coordinates: route.destination.coordinates,
          otId: route.destination.otId || null,
          waypointKind: route.destination.waypointKind || null,
        } : null,
      ].filter(Boolean);
      const mine = points.filter(p => String(p.otId) === String(monitoringOTId));
      if (mine.length === 0) return null; // stale OT id — not on this route
      const pickupWp = mine.find(p => p.waypointKind === 'pickup');
      // Destino do troço = ÚLTIMA descarga da OT (em ordem de rota). O
      // combustível com entrega dividida tem 2 descargas (PA1 delivery + PA2
      // destino/fuel); se apanhássemos a PRIMEIRA descarga (PA1), o troço
      // PA1→PA2 ficava por destacar (azul). Para OT normal de 1 descarga o
      // reverse-find devolve a mesma paragem, portanto sem regressão.
      const deliveryWp = [...mine].reverse().find(p => p.waypointKind === 'delivery' || p.waypointKind === 'fuel')
        || mine.find(p => p.waypointKind === 'service')
        || mine[mine.length - 1];
      deliveryCoord = deliveryWp?.coordinates;
      if (pickupWp) {
        pickupCoord = pickupWp.coordinates;
      } else {
        // No explicit pickup → the leg starts at the PREVIOUS stop in the route
        // (the point immediately before this OT's stop), not the route origin.
        const myIdx = points.indexOf(deliveryWp);
        pickupCoord = myIdx > 0 ? points[myIdx - 1].coordinates : route.origin?.coordinates;
      }
    }

    // Last-resort fallbacks (single-OT route or no waypoint metadata at all)
    if (!pickupCoord) pickupCoord = route.origin?.coordinates;
    if (!deliveryCoord) deliveryCoord = route.destination?.coordinates;
    if (!pickupCoord || !deliveryCoord) return null;

    // Find nearest polyline index for a given [lng, lat]
    const nearestIdx = (c) => {
      let best = 0, bestD = Infinity;
      for (let i = 0; i < routeCoords.length; i++) {
        const dx = routeCoords[i][0] - c[0];
        const dy = routeCoords[i][1] - c[1];
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    };
    const a = nearestIdx(pickupCoord);
    const b = nearestIdx(deliveryCoord);
    const [from, to] = a <= b ? [a, b] : [b, a];
    if (to - from < 2) return null;
    const sliced = routeCoords.slice(from, to + 1);
    // Decima troços longos: rotas de 400+ km têm 10–15 mil vértices na
    // polyline planeada, o que faz a Polyline laranja do Google Maps
    // demorar 1–2 s a re-renderizar por cada clique de OT. Reduzir para
    // ≤400 pontos preserva a forma visível a zoom típico (distância
    // entre vértices ≤1 km) e o setPath do Google Maps fica
    // praticamente instantâneo. Para legs curtos não há perda.
    const MAX_POINTS = 400;
    const step = Math.max(1, Math.floor(sliced.length / MAX_POINTS));
    const out = step > 1
      ? sliced.filter((_, i) => i % step === 0 || i === sliced.length - 1)
      : sliced;
    return out.map(c => ({ lat: c[1], lng: c[0] }));
  }, [monitoringOTId, monitoringOrderedIds, routeCoords, route.waypoints, route.origin, route.destination]);

  // Slice do trail REAL (GPS) correspondente ao leg da OT seleccionada.
  // Mesma estratégia de match-por-proximidade que `otLegPath`, mas a
  // fonte é o trail GPS (verde+vermelho) em vez da geometria planeada.
  // Devolve null quando não há trail (OT ainda futura ou rota não
  // iniciada) — nesse caso só o `otLegPath` (laranja sobre o planeado)
  // continua a aparecer.
  const otLegPathReal = useMemo(() => {
    if (!monitoringOTId || !otLegPath) return null;
    const trail = actualTrail?.trail;
    if (!Array.isArray(trail) || trail.length < 2) return null;
    // Reaproveita pickup/delivery do otLegPath (primeiro e último ponto)
    const first = otLegPath[0];
    const last = otLegPath[otLegPath.length - 1];
    if (!first || !last) return null;
    const nearestTrailIdx = (target) => {
      let best = 0, bestD = Infinity;
      for (let i = 0; i < trail.length; i++) {
        const p = trail[i];
        const lat = Number(p.lat ?? p.latitude);
        const lng = Number(p.lng ?? p.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const dx = lng - target.lng;
        const dy = lat - target.lat;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    };
    const a = nearestTrailIdx(first);
    const b = nearestTrailIdx(last);
    const [from, to] = a <= b ? [a, b] : [b, a];
    if (to - from < 2) return null;
    const sliced = trail.slice(from, to + 1).map(p => ({
      lat: Number(p.lat ?? p.latitude),
      lng: Number(p.lng ?? p.longitude),
    })).filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    return sliced.length > 1 ? sliced : null;
  }, [monitoringOTId, otLegPath, actualTrail]);

  // Interpolate coordinate along route by progress ratio (0-1)
  const getCoordAtProgress = useCallback((ratio) => {
    if (routeCoords.length < 2) return null;
    const idx = Math.min(Math.round(ratio * (routeCoords.length - 1)), routeCoords.length - 1);
    const c = routeCoords[idx];
    return c ? { lat: c[1], lng: c[0] } : null;
  }, [routeCoords]);

  // Tachograph stops with positions calculated from progressRatio
  const stopsWithPos = useMemo(() => {
    if (!route.stops || routeCoords.length < 2) return [];
    return route.stops.map((stop, idx) => {
      let pos = null;
      if (stop.suggestedRestArea) {
        pos = { lat: stop.suggestedRestArea.lat, lng: stop.suggestedRestArea.lon };
      } else if (stop.progressRatio != null) {
        pos = getCoordAtProgress(stop.progressRatio);
      }
      return { ...stop, pos, idx };
    }).filter(s => s.pos);
  }, [route.stops, routeCoords, getCoordAtProgress]);

  // Road breakdown segments for colored polylines — use roadSegments from HERE API (startIdx/endIdx)
  const coloredSegments = useMemo(() => {
    // Prefer roadSegments (direct coord indices from HERE API)
    if (cr?.roadSegments && cr.roadSegments.length > 0 && routeCoords.length > 2) {
      return cr.roadSegments.map(seg => {
        const start = Math.max(0, Math.min(seg.startIdx, routeCoords.length - 1));
        const end = Math.max(start + 1, Math.min(seg.endIdx + 1, routeCoords.length));
        const path = routeCoords.slice(start, end).map(c => ({ lat: c[1], lng: c[0] }));
        return { path, color: ROAD_COLORS[seg.type] || ROAD_COLORS.local, type: seg.type };
      }).filter(s => s.path.length >= 2);
    }
    // Fallback: use routeSteps with distance-based mapping
    if (cr?.routeSteps && cr.routeSteps.length > 0 && routeCoords.length > 2) {
      const segments = [];
      let coordIdx = 0;
      for (const step of cr.routeSteps) {
        const type = step.type || 'local';
        const stepDistM = step.distance || 0;
        if (stepDistM <= 0) continue;
        const startIdx = coordIdx;
        let accumulated = 0;
        while (coordIdx < routeCoords.length - 1 && accumulated < stepDistM) {
          const dx = (routeCoords[coordIdx + 1][0] - routeCoords[coordIdx][0]) * 111000 * Math.cos(routeCoords[coordIdx][1] * Math.PI / 180);
          const dy = (routeCoords[coordIdx + 1][1] - routeCoords[coordIdx][1]) * 111000;
          accumulated += Math.sqrt(dx * dx + dy * dy);
          coordIdx++;
        }
        if (coordIdx > startIdx) {
          const path = routeCoords.slice(startIdx, coordIdx + 1).map(c => ({ lat: c[1], lng: c[0] }));
          if (path.length >= 2) segments.push({ path, color: ROAD_COLORS[type] || ROAD_COLORS.local, type });
        }
      }
      return segments;
    }
    return [];
  }, [cr, routeCoords]);

  // Fit bounds when route changes — zoom to see entire route (same padding as the
  // "Ver rota completa" button so the route never falls behind the sidebar/details panel)
  useEffect(() => {
    if (!mapRef.current || !isLoaded || routePath.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    routePath.forEach(p => bounds.extend(p));
    mapRef.current.fitBounds(bounds, { top: 80, bottom: 80, left: 420, right: 400 });
  }, [routePath, isLoaded]);

  // Fit bounds when entering / changing compare mode — include all polyline vertices
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;
    if (!Array.isArray(displayedSavedRoutes) || displayedSavedRoutes.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    let pts = 0;
    displayedSavedRoutes.forEach(r => {
      const coords = r.computedRoute?.geometry?.coordinates || [];
      // Sample coords to avoid hammering with 10k+ points
      const step = Math.max(1, Math.floor(coords.length / 200));
      for (let i = 0; i < coords.length; i += step) {
        bounds.extend({ lat: coords[i][1], lng: coords[i][0] });
        pts++;
      }
      if (r.origin?.coordinates) bounds.extend({ lat: r.origin.coordinates[1], lng: r.origin.coordinates[0] });
      if (r.destination?.coordinates) bounds.extend({ lat: r.destination.coordinates[1], lng: r.destination.coordinates[0] });
    });
    if (pts > 0) {
      mapRef.current.fitBounds(bounds, { top: 90, bottom: 90, left: 60, right: 60 });
    }
  }, [displayedSavedRoutes, isLoaded]);

  // Reset map view — quando o operador volta ao Planeamento sem rota
  // carregada (ou limpa a rota), centramos no país com mais viaturas em
  // operação. Fallback: tenantCenter (defaultCountry/timezone das prefs).
  useEffect(() => {
    if (!resetMapView || !mapRef.current || !isLoaded) return;
    setSearchPin(null);
    const dom = dominantCountry(vehiclesRef.current);
    if (dom && dom.points.length > 0 && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      dom.points.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
      // Se só houver um ponto, fitBounds dá zoom máximo — clampamos.
      if (dom.points.length === 1) {
        mapRef.current.panTo({ lat: dom.points[0].lat, lng: dom.points[0].lng });
        mapRef.current.setZoom(11);
      } else {
        mapRef.current.fitBounds(bounds, { top: 80, bottom: 80, left: 60, right: 60 });
      }
    } else {
      mapRef.current.panTo(tenantCenter);
      mapRef.current.setZoom(7);
    }
    dispatch(setResetMapView(false));
  }, [resetMapView, isLoaded, dispatch]);

  // Fetch photo when a route point (origin/dest/waypoint) is selected
  useEffect(() => {
    if (!selectedPoint?.pos || !mapRef.current || !isLoaded) { setPointPhoto(null); return; }
    setPointPhoto(null);
    try {
      const service = new window.google.maps.places.PlacesService(mapRef.current);
      service.nearbySearch({
        location: selectedPoint.pos,
        radius: 200,
        keyword: selectedPoint.name,
      }, (results, status) => {
        if (status === 'OK' && results?.[0]?.photos?.[0]) {
          setPointPhoto(results[0].photos[0].getUrl({ maxWidth: 300, maxHeight: 130 }));
        }
      });
    } catch { /* Places API may not be enabled */ }
  }, [selectedPoint, isLoaded]);

  // Fetch photo when a stop is selected via Google Places
  useEffect(() => {
    if (selectedStop == null || !mapRef.current || !isLoaded) { setStopPhoto(null); return; }
    const stop = stopsWithPos.find(s => s.idx === selectedStop);
    if (!stop?.pos) return;
    setStopPhoto(null);
    try {
      const service = new window.google.maps.places.PlacesService(mapRef.current);
      service.nearbySearch({
        location: stop.pos,
        radius: 500,
        type: 'gas_station',
      }, (results, status) => {
        if (status === 'OK' && results?.[0]?.photos?.[0]) {
          setStopPhoto(results[0].photos[0].getUrl({ maxWidth: 280, maxHeight: 120 }));
        }
      });
    } catch { /* Places API may not be enabled */ }
  }, [selectedStop, stopsWithPos, isLoaded]);

  // Hover stop from sidebar → open info window (no pan/zoom — keep full route visible)
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;
    if (hoveredStopIdx != null) {
      setSelectedStop(hoveredStopIdx);
    } else {
      setSelectedStop(null);
    }
  }, [hoveredStopIdx, stopsWithPos, isLoaded]);

  // Fetch geofences on mount
  useEffect(() => {
    if (!token) return;
    fetch('/planner-api/geofences', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setGeofences(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  // Fetch grupos EcooGPS para resolver nome na popup do veículo. Mesmo
  // endpoint usado pelo dialog "Filtrar grupos" e pelo Header — o backend
  // devolve sempre a lista canónica.
  useEffect(() => {
    if (!token) return;
    fetch('/planner-api/vehicles/groups', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.groups) return;
        const map = {};
        for (const g of data.groups) map[String(g.id)] = g.name;
        setGroupNameById(map);
      })
      .catch(() => {});
  }, [token]);

  // Check if route passes through geofences
  useEffect(() => {
    if (!cr?.geometry?.coordinates || geofences.length === 0) { setGeofenceAlerts([]); return; }
    const coords = cr.geometry.coordinates;
    const alerts = [];
    for (const gf of geofences) {
      // Accept any active geofence (isActive can be true, undefined, or missing for old records)
      if (gf.isActive === false) continue;
      const gfLat = Number(gf.lat), gfLng = Number(gf.lng);
      const radius = Number(gf.radius) || 1000;
      if (!gfLat || !gfLng) continue;
      // Check every 3rd coordinate for accuracy (small geofences need more points)
      let found = false;
      for (let i = 0; i < coords.length && !found; i += 3) {
        const [lng, lat] = coords[i];
        const dx = (lng - gfLng) * 111000 * Math.cos(lat * Math.PI / 180);
        const dy = (lat - gfLat) * 111000;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= radius) {
          alerts.push({ geofence: gf, coordIdx: i });
          found = true;
        }
      }
    }
    console.log('[Geofence] Check:', geofences.length, 'geofences,', coords.length, 'coords →', alerts.length, 'alerts');
    setGeofenceAlerts(alerts);
  }, [cr, geofences]);

  // Street View split panel
  useEffect(() => {
    if (!streetViewOpen || !streetViewRef.current || !isLoaded) return;
    const pos = streetViewPos || (mapRef.current ? mapRef.current.getCenter().toJSON() : tenantCenter);
    const svService = new window.google.maps.StreetViewService();
    const renderPano = (data) => {
      svPanoramaRef.current = new window.google.maps.StreetViewPanorama(streetViewRef.current, {
        position: data.location.latLng,
        pov: { heading: data.tiles?.centerHeading || 0, pitch: 0 },
        zoom: 1,
        addressControl: true,
        linksControl: true,
        panControl: false,
        enableCloseButton: false,
      });
    };
    // Fallback satélite: quando o Street View não tem cobertura (típico
    // em Angola e zonas rurais), renderizamos a mesma área em vista
    // híbrida (satélite + estradas) com zoom alto. O operador continua
    // a ter informação visual útil — telhados, ruas, acessos.
    const showSatelliteFallback = (reason) => {
      if (!streetViewRef.current) return;
      streetViewRef.current.innerHTML = '';
      const wrap = document.createElement('div');
      wrap.style.cssText = 'position:relative;width:100%;height:100%';
      const mapDiv = document.createElement('div');
      mapDiv.style.cssText = 'width:100%;height:100%';
      const banner = document.createElement('div');
      banner.style.cssText = 'position:absolute;top:10px;left:10px;right:10px;background:rgba(15,23,42,0.85);color:#fff;padding:8px 12px;border-radius:8px;font:600 12px/1.4 Roboto,sans-serif;display:flex;align-items:center;gap:8px;backdrop-filter:blur(6px);z-index:5;';
      banner.innerHTML = `<span style="font-size:14px">🛰️</span><span>Sem Street View aqui — vista satélite (zoom para detalhes)</span>`;
      wrap.appendChild(mapDiv);
      wrap.appendChild(banner);
      streetViewRef.current.appendChild(wrap);
      const fallbackMap = new window.google.maps.Map(mapDiv, {
        center: pos,
        zoom: 19,
        mapTypeId: 'hybrid',
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: false,
        gestureHandling: 'greedy',
      });
      // Marcador no ponto exacto para o operador saber o que foca
      new window.google.maps.Marker({ position: pos, map: fallbackMap });
    };
    // 1.º tenta cobertura oficial outdoor (evita panoramas dentro de edifícios).
    // 2.º se não houver, repete sem restrição de fonte e com raio maior — apanha
    //     photo spheres de utilizadores (ex.: Angola, sem cobertura do carro Google).
    // 3.º quando nenhum dos dois funciona, satélite zoom 19 como fallback.
    svService.getPanorama({ location: pos, radius: 500, source: 'outdoor' }, (data, status) => {
      if (status === 'OK' && data?.location?.latLng) { renderPano(data); return; }
      svService.getPanorama({ location: pos, radius: 1000 }, (data2, status2) => {
        if (!streetViewOpen || !streetViewRef.current) return;
        if (status2 === 'OK' && data2?.location?.latLng) renderPano(data2);
        else showSatelliteFallback();
      });
    });
    return () => { svPanoramaRef.current = null; };
  }, [streetViewOpen, streetViewPos, isLoaded]);

  // Listen for mapZoomTo events from SearchBar
  useEffect(() => {
    function handleZoomTo(e) {
      if (!mapRef.current || !isLoaded) return;
      const { lat, lng, viewport, placeId } = e.detail;
      setSearchPin({ lat, lng, name: e.detail.name || '', photo: null, rating: null, address: null });
      setSearchPinOpen(true);

      // Fetch place details (photo, rating) if placeId available
      if (placeId && window.google?.maps?.places?.Place) {
        (async () => {
          try {
            const place = new window.google.maps.places.Place({ id: placeId });
            await place.fetchFields({ fields: ['displayName', 'photos', 'rating', 'userRatingCount', 'formattedAddress'] });
            setSearchPin(prev => prev ? ({
              ...prev,
              photo: place.photos?.[0]?.getURI?.({ maxWidth: 400, maxHeight: 180 }) || null,
              rating: place.rating || null,
              totalRatings: place.userRatingCount || 0,
              address: place.formattedAddress || null,
              displayName: place.displayName || prev.name,
            }) : null);
          } catch { /* ignore — photo not available */ }
        })();
      }
      mapRef.current.panTo({ lat, lng });
      if (viewport) {
        // Calculate zoom from viewport size
        const latDiff = Math.abs(viewport.ne.lat - viewport.sw.lat);
        const lngDiff = Math.abs(viewport.ne.lng - viewport.sw.lng);
        const maxDiff = Math.max(latDiff, lngDiff);
        // Map diff to zoom: large area=low zoom, small area=high zoom
        let targetZoom;
        if (maxDiff > 2) targetZoom = 8;       // country
        else if (maxDiff > 0.5) targetZoom = 10; // region
        else if (maxDiff > 0.1) targetZoom = 12; // city
        else if (maxDiff > 0.02) targetZoom = 14; // neighbourhood
        else if (maxDiff > 0.005) targetZoom = 16; // street
        else targetZoom = 17;                      // building
        mapRef.current.setZoom(targetZoom);
      } else {
        mapRef.current.setZoom(15); // default zoom for POIs
      }
    }
    function handleClearPin() { setSearchPin(null); }
    function handleShowLocs(e) {
      const locs = e.detail;
      if (Array.isArray(locs) && locs.length > 0) {
        setSelectedLocationsOnMap(locs);
        setShowLocationsOnMap(true);
      }
    }
    function handleEnterAddFav() { setAddFavMode(true); }
    function handleFavAdded(e) {
      const { lat, lng } = e.detail || {};
      setShowLocationsOnMap(true);
      setSelectedLocationsOnMap(null);
      setNearRouteDistance(null);
      if (lat != null && lng != null && mapRef.current) {
        mapRef.current.panTo({ lat: Number(lat), lng: Number(lng) });
        if (mapRef.current.getZoom() < 14) mapRef.current.setZoom(15);
      }
    }
    function handlePanTo(e) {
      if (!mapRef.current || !isLoaded) return;
      const { lat, lng } = e.detail || {};
      if (lat != null && lng != null) mapRef.current.panTo({ lat, lng });
    }
    window.addEventListener('mapZoomTo', handleZoomTo);
    window.addEventListener('mapPanTo', handlePanTo);
    window.addEventListener('mapClearPin', handleClearPin);
    window.addEventListener('showLocationsOnMap', handleShowLocs);
    window.addEventListener('enterAddFavMode', handleEnterAddFav);
    window.addEventListener('favoriteAdded', handleFavAdded);
    return () => {
      window.removeEventListener('mapZoomTo', handleZoomTo);
      window.removeEventListener('mapPanTo', handlePanTo);
      window.removeEventListener('mapClearPin', handleClearPin);
      window.removeEventListener('showLocationsOnMap', handleShowLocs);
      window.removeEventListener('enterAddFavMode', handleEnterAddFav);
      window.removeEventListener('favoriteAdded', handleFavAdded);
    };
  }, [isLoaded]);

  // Street View close/address buttons are styled via CSS (#gmap-custom-styles)

  // Highlight route step from details panel → pan map + zoom to segment + draw highlight polyline
  useEffect(() => {
    if (!mapRef.current || !isLoaded || highlightedStepIdx == null) {
      setHighlightedSegment(null);
      return;
    }
    if (!cr?.routeSteps || routeCoords.length < 2) return;

    const totalDist = cr.distance || routeCoords.reduce((s, c, i) => {
      if (i === 0) return 0;
      const dx = (c[0] - routeCoords[i-1][0]) * 111000 * Math.cos(c[1] * Math.PI / 180);
      const dy = (c[1] - routeCoords[i-1][1]) * 111000;
      return s + Math.sqrt(dx * dx + dy * dy);
    }, 0);

    // Calculate start and end distance for this step
    let startDist = 0;
    for (let i = 0; i < highlightedStepIdx && i < cr.routeSteps.length; i++) {
      startDist += cr.routeSteps[i].distance || 0;
    }
    const currentStep = cr.routeSteps[highlightedStepIdx];
    if (!currentStep) return;
    const endDist = startDist + (currentStep.distance || 0);

    // Find coordinate indices for start and end of segment
    const startRatio = Math.min(startDist / Math.max(totalDist, 1), 1);
    const endRatio = Math.min(endDist / Math.max(totalDist, 1), 1);
    const startIdx = Math.min(Math.round(startRatio * (routeCoords.length - 1)), routeCoords.length - 1);
    const endIdx = Math.min(Math.round(endRatio * (routeCoords.length - 1)), routeCoords.length - 1);

    // Extract segment coordinates for highlight polyline
    const segCoords = routeCoords.slice(startIdx, endIdx + 1).map(c => ({ lat: c[1], lng: c[0] }));
    setHighlightedSegment(segCoords.length > 1 ? segCoords : null);

    // Fit bounds to the segment
    if (segCoords.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      segCoords.forEach(c => bounds.extend(c));
      mapRef.current.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
    } else {
      const midRatio = (startRatio + endRatio) / 2;
      const midIdx = Math.min(Math.round(midRatio * (routeCoords.length - 1)), routeCoords.length - 1);
      const c = routeCoords[midIdx];
      if (c) {
        mapRef.current.panTo({ lat: c[1], lng: c[0] });
        const stepKm = (currentStep.distance || 0) / 1000;
        const targetZoom = stepKm > 100 ? 8 : stepKm > 50 ? 9 : stepKm > 20 ? 10 : stepKm > 5 ? 12 : 13;
        mapRef.current.setZoom(targetZoom);
      }
    }
  }, [highlightedStepIdx, cr, routeCoords, isLoaded]);

  // Traffic layer toggle
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;
    if (showTraffic && !trafficLayerRef.current) {
      trafficLayerRef.current = new window.google.maps.TrafficLayer();
      trafficLayerRef.current.setMap(mapRef.current);
    } else if (!showTraffic && trafficLayerRef.current) {
      trafficLayerRef.current.setMap(null);
      trafficLayerRef.current = null;
    }
  }, [showTraffic, isLoaded]);

  // ── Traffic incidents (HERE) ────────────────────────────────
  // When toggled ON, we fetch HERE incidents for the current viewport and
  // refresh whenever the operator pans/zooms (debounced 500ms via the
  // map's `idle` event). Uses a ref-listener so we only attach when
  // `showIncidents` is true and detach cleanly when toggled off.
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;
    if (!showIncidents) {
      setIncidents([]);
      setSelectedIncident(null);
      return;
    }

    let cancelled = false;
    async function refreshIncidents() {
      if (!mapRef.current) return;
      const bounds = mapRef.current.getBounds();
      if (!bounds) return;
      setIncidentsLoading(true);
      try {
        // FASE 1 do incidents-aware: HERE + TomTom em paralelo.
        // Resultados agregados e deduplicados por proximidade (~50m)
        // — quando ambos os providers reportam o mesmo evento, fica
        // o do HERE (mais consistente em PT) com badge a indicar
        // "também reportado por TomTom".
        const [hereList, tomtomList] = await Promise.all([
          fetchIncidentsForViewport(bounds).catch(() => []),
          fetchIncidentsTomTomBbox(bounds).catch(() => []),
        ]);
        // Tag fontes (HERE não vinha com source — adicionamos aqui
        // para o popup poder mostrar o badge consistentemente).
        const taggedHere = (hereList || []).map(x => ({ ...x, source: 'here' }));
        const dedupRadius = 0.0005; // ≈ 55m em latitude
        const merged = [...taggedHere];
        for (const tt of (tomtomList || [])) {
          const dupe = merged.findIndex(x =>
            Math.abs(x.coordinates[0] - tt.coordinates[0]) < dedupRadius &&
            Math.abs(x.coordinates[1] - tt.coordinates[1]) < dedupRadius
          );
          if (dupe === -1) {
            merged.push(tt);
          } else {
            // Marcar o HERE existente como confirmado também pelo TomTom.
            merged[dupe] = { ...merged[dupe], alsoReportedBy: 'tomtom' };
          }
        }
        if (!cancelled) setIncidents(merged);
      } catch (err) {
        console.warn('[Incidents] aggregated fetch failed:', err.message);
        if (!cancelled) setIncidents([]);
      } finally {
        if (!cancelled) setIncidentsLoading(false);
      }
    }

    // Initial fetch as soon as toggle goes on.
    refreshIncidents();

    const idleListener = mapRef.current.addListener('idle', () => {
      if (incidentsFetchTimerRef.current) clearTimeout(incidentsFetchTimerRef.current);
      incidentsFetchTimerRef.current = setTimeout(refreshIncidents, 500);
    });

    return () => {
      cancelled = true;
      if (incidentsFetchTimerRef.current) clearTimeout(incidentsFetchTimerRef.current);
      if (idleListener) idleListener.remove();
    };
  }, [showIncidents, isLoaded]);

  // Helper: apply a picked coordinate to the current mapClickMode target
  // (origin / destination / waypoint-N / marker-X). Mirrors the left-click flow
  // but takes pre-computed lat/lng/name instead of the raw click event.
  const applyPickToTarget = useCallback((lat, lng, name) => {
    const coord = [lng, lat];
    if (mapClickMode === 'origin') {
      dispatch(setOrigin({ name, coordinates: coord }));
    } else if (mapClickMode === 'destination') {
      dispatch(setDestination({ name, coordinates: coord }));
    } else if (typeof mapClickMode === 'string' && mapClickMode.startsWith('waypoint-')) {
      const idx = parseInt(mapClickMode.split('-')[1], 10);
      if (!Number.isNaN(idx)) {
        dispatch(updateWaypoint({ index: idx, waypoint: { name, coordinates: coord } }));
      }
    } else {
      // Fallback: treat same as blank left-click
      if (!route.origin) {
        dispatch(setOrigin({ name, coordinates: coord }));
      } else if (!route.destination) {
        dispatch(setDestination({ name, coordinates: coord }));
      } else {
        dispatch(addWaypoint({ name, coordinates: coord }));
      }
    }
    dispatch(setNeedsRecalculation(true));
    dispatch(setMapClickMode(false));
  }, [mapClickMode, route.origin, route.destination, dispatch]);

  // Handle map click
  const onMapClick = useCallback((e) => {
    if (route.readOnly) return;
    if (!mapClickMode && !addFavMode) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const coord = [lng, lat];

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      const name = status === 'OK' && results[0] ? results[0].formatted_address : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

      if (addFavMode) {
        setAddFavMode(false);
        if (window.__favPickAddNewMode) {
          window.__favPickAddNewMode = false;
          window.dispatchEvent(new CustomEvent('favoritesOpenAddNew', { detail: { lat, lng, address: name } }));
          dispatch(setActiveView('favorites'));
        } else if (window.__favPickCallback) {
          window.__favPickCallback(lat, lng, name);
          window.__favPickCallback = null;
          dispatch(setActiveView('favorites')); // Reopen favorites view with coords filled
        }
        return;
      }

      if (!route.origin) {
        dispatch(setOrigin({ name, coordinates: coord }));
      } else if (!route.destination) {
        dispatch(setDestination({ name, coordinates: coord }));
      } else {
        dispatch(addWaypoint({ name, coordinates: coord }));
      }
      dispatch(setNeedsRecalculation(true));
      dispatch(setMapClickMode(false));
    });
  }, [mapClickMode, addFavMode, route.origin, route.destination, dispatch]);

  // Right-click while picking a point on the map → save as a preferred POI AND
  // apply it to the current pick target in one go. Quicker than "left-click →
  // guardar favorito separately". Prompts for a short name, then persists a
  // location with `isPreferred: true` so it shows up in future dropdowns.
  const onMapRightClick = useCallback((e) => {
    if (route.readOnly) return;
    if (!mapClickMode) return;
    if (mapClickMode === 'origin' || mapClickMode === 'destination'
        || (typeof mapClickMode === 'string' && mapClickMode.startsWith('waypoint-'))) {
      e.domEvent?.preventDefault?.();
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, async (results, status) => {
        const autoAddress = status === 'OK' && results[0] ? results[0].formatted_address : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        const suggested = autoAddress.split(',')[0] || 'Novo local';
        const name = window.prompt(
          'Nome do favorito (ficará guardado como local preferencial):',
          suggested
        );
        if (name == null) return; // cancelled
        const trimmed = String(name).trim() || suggested;
        try {
          const created = await dispatch(createLocation({
            token,
            location: {
              name: trimmed,
              type: 'poi',
              latitude: lat,
              longitude: lng,
              address: autoAddress,
              notes: '',
              isPreferred: true,
              includeInRoute: false,
            },
          })).unwrap();
          // Apply the newly saved favorite as the selected waypoint/origin/destination
          applyPickToTarget(lat, lng, created.name || trimmed);
        } catch (err) {
          console.error('[map right-click] save favorite failed:', err);
          // On failure, still apply the point so the operator doesn't lose the click
          applyPickToTarget(lat, lng, autoAddress);
        }
      });
    }
  }, [mapClickMode, route.readOnly, dispatch, token, applyPickToTarget]);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    try { const z = map.getZoom(); if (z != null) setMapZoom(z); } catch { /* noop */ }
    // Intercept native Street View — when Pegman is dropped, open our custom panel
    const sv = map.getStreetView();
    sv.addListener('visible_changed', () => {
      if (sv.getVisible()) {
        // Get the position where Pegman was dropped
        const pos = sv.getPosition();
        sv.setVisible(false); // Close native Street View
        if (pos) {
          setStreetViewPos({ lat: pos.lat(), lng: pos.lng() });
          setStreetViewOpen(true);
        }
      }
    });
  }, []);

  // Listen for fleet view events (focus vehicle, open history)
  useEffect(() => {
    function onFocusVehicle(e) {
      const { id, lat, lng } = e.detail || {};
      setShowVehicles(true);
      // Destaca o veículo clicado com um anel de foco (6s) — para se distinguir
      // logo qual é, mesmo com outras viaturas ao lado.
      if (id != null) {
        setFocusedVehicleId(id);
        if (focusClearRef.current) clearTimeout(focusClearRef.current);
        focusClearRef.current = setTimeout(() => setFocusedVehicleId(null), 6000);
      }
      function doFocus(targetLat, targetLng) {
        if (!mapRef.current) return false;
        const center = { lat: Number(targetLat), lng: Number(targetLng) };
        mapRef.current.setCenter(center);
        mapRef.current.setZoom(17);
        // Re-center after google maps settles to defeat any concurrent fitBounds
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.setCenter(center);
            mapRef.current.setZoom(17);
          }
        }, 300);
        return true;
      }
      function tryFocus(attempt) {
        if (lat && lng) return doFocus(lat, lng);
        if (id) {
          const v = vehiclesRef.current.find(veh => veh.id === id || String(veh.id) === String(id));
          if (v?.position) return doFocus(v.position.latitude, v.position.longitude);
        }
        if (attempt < 8) setTimeout(() => tryFocus(attempt + 1), 250);
        return false;
      }
      tryFocus(0);
    }
    function onVehicleHistoryEvent(e) {
      const { id, plate } = e.detail || {};
      if (!id || !token) return;
      setShowVehicles(true);
      setVehicleHistory({ vehicle: { id, plate }, data: null, loading: true, day: todayStr() });
      fetch(`/planner-api/vehicles/${id}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(data => {
        setVehicleHistory(prev => prev ? { ...prev, data, loading: false } : null);
        if (data.points?.length > 1) {
          setHistoryTrail(data.points.map(p => ({ lat: p.lat, lng: p.lng })));
          setSelectedTrips(new Set());
          if (mapRef.current) {
            const bounds = new window.google.maps.LatLngBounds();
            data.points.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
            mapRef.current.fitBounds(bounds, 60);
          }
        }
      }).catch(() => setVehicleHistory(prev => prev ? { ...prev, loading: false } : null));
    }
    function onFocusSingleVehicle(e) {
      const { id } = e.detail || {};
      setSingleVehicleId(id || null);
      setShowVehicles(true);
      if (id) {
        function tryFocus(attempt) {
          if (!mapRef.current) {
            if (attempt < 8) setTimeout(() => tryFocus(attempt + 1), 250);
            return false;
          }
          const v = vehiclesRef.current.find(veh => veh.id === id || String(veh.id) === String(id));
          if (v?.position) {
            mapRef.current.setCenter({ lat: v.position.latitude, lng: v.position.longitude });
            mapRef.current.setZoom(13);
            return true;
          }
          if (attempt < 8) setTimeout(() => tryFocus(attempt + 1), 250);
          return false;
        }
        tryFocus(0);
      }
    }
    // Follow simulation vehicle on map
    function onSimulationPosition(e) {
      const { lat, lng } = e.detail || {};
      if (mapRef.current && lat && lng && singleVehicleId) {
        mapRef.current.panTo({ lat, lng });
      }
    }
    function onResetMapView() {
      // Keep vehicles visible by default when landing on the planner — the operator
      // expects to see the live fleet straight away. Clear any single-vehicle focus
      // so the fit-bounds logic picks up all vehicles again.
      setShowVehicles(true);
      setSingleVehicleId(null);
      setVehicleHistory(null);
      setHistoryTrail(null);
      setMonitoringOTId(null);
      setMonitoringOrderedIds([]);
      setMonitoringRouteLegs([]);
      didInitialFitRef.current = false;
    }
    function onFitRouteBounds() {
      if (!mapRef.current || !routeCoords || routeCoords.length < 2) return;
      const bounds = new window.google.maps.LatLngBounds();
      // Sample route coords for performance
      const sampled = routeCoords.filter((_, i) => i % 50 === 0 || i === routeCoords.length - 1);
      sampled.forEach(c => bounds.extend({ lat: c[1], lng: c[0] }));
      mapRef.current.fitBounds(bounds, { top: 80, bottom: 80, left: 80, right: 80 });
    }
    function onOpenStreetView(e) {
      const { lat, lng } = e.detail || {};
      if (lat && lng) {
        setStreetViewPos({ lat, lng });
        setStreetViewOpen(true);
      }
    }
    // Event-driven toggle of vehicles layer — used by "Ver rota no mapa" to declutter the map.
    function onSetVehiclesVisibility(e) {
      if (e.detail && typeof e.detail.visible === 'boolean') {
        setShowVehicles(e.detail.visible);
      }
    }
    // "Highlight OT leg" — caller sends the orderId being inspected so we can paint that
    // specific pickup→delivery segment within a consolidated route. An optional orderedOTIds
    // array lets us locate OTs that don't own any waypoint (first/last). An optional
    // `routeLegs` array carries the per-OT breakdown for the bottom panel.
    function onHighlightOTLeg(e) {
      setMonitoringOTId(e.detail?.orderId || null);
      // Preservar os arrays existentes quando o evento NÃO os traz —
      // é o caso de cada clique de card em AppLayout, que só envia
      // orderId. Sem isto cada clique reset-ava orderedOTIds a [] e
      // bombava o useMemo de otLegPath a cada re-render. Só
      // substituímos quando o caller manda explicitamente novos arrays
      // (na activação do inspect-mode).
      if (Array.isArray(e.detail?.orderedOTIds)) {
        setMonitoringOrderedIds(e.detail.orderedOTIds);
      }
      if (Array.isArray(e.detail?.routeLegs)) {
        setMonitoringRouteLegs(e.detail.routeLegs);
      } else if (e.detail?.orderId == null && e.detail?.orderedOTIds == null) {
        // Sinal explícito de "limpar tudo" — só dado quando se sai do
        // inspect-mode (clearRoute, focusSingleVehicle null sem mais nada).
        setMonitoringRouteLegs([]);
        setMonitoringOrderedIds([]);
      }
    }
    window.addEventListener('openStreetView', onOpenStreetView);
    window.addEventListener('focusVehicle', onFocusVehicle);
    window.addEventListener('focusSingleVehicle', onFocusSingleVehicle);
    window.addEventListener('vehicleHistory', onVehicleHistoryEvent);
    window.addEventListener('simulationPosition', onSimulationPosition);
    window.addEventListener('resetMapView', onResetMapView);
    window.addEventListener('fitRouteBounds', onFitRouteBounds);
    window.addEventListener('setVehiclesVisibility', onSetVehiclesVisibility);
    window.addEventListener('highlightOTLeg', onHighlightOTLeg);
    return () => {
      window.removeEventListener('openStreetView', onOpenStreetView);
      window.removeEventListener('focusVehicle', onFocusVehicle);
      window.removeEventListener('focusSingleVehicle', onFocusSingleVehicle);
      window.removeEventListener('vehicleHistory', onVehicleHistoryEvent);
      window.removeEventListener('simulationPosition', onSimulationPosition);
      window.removeEventListener('resetMapView', onResetMapView);
      window.removeEventListener('fitRouteBounds', onFitRouteBounds);
      window.removeEventListener('setVehiclesVisibility', onSetVehiclesVisibility);
      window.removeEventListener('highlightOTLeg', onHighlightOTLeg);
    };
  }, [token]);

  // Locations filtered for map display (must be before early returns for hook rules)
  const locTypeConfig = {
    warehouse: { color: '#6366f1', emoji: '🏭' }, safe_point: { color: '#22c55e', emoji: '🛡️' },
    overnight: { color: '#8b5cf6', emoji: '🏨' }, fuel: { color: '#ef4444', emoji: '⛽' },
    poi: { color: '#f59e0b', emoji: '📍' }, rest_area: { color: '#7c3aed', emoji: '🅿️' },
    garage: { color: '#3b82f6', emoji: '🔧' }, other: { color: '#64748b', emoji: '📌' },
  };
  const displayedLocations = useMemo(() => {
    if (!showLocationsOnMap) return [];
    let locs = selectedLocationsOnMap || savedLocations;
    if (nearRouteDistance && routeCoords.length > 1) {
      const sampled = routeCoords.filter((_, i) => i % 20 === 0 || i === routeCoords.length - 1);
      const haversine = (lat1, lng1, lat2, lng2) => {
        const R = 6371, dLat = (lat2 - lat1) * Math.PI / 180, dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      };
      locs = locs.filter(loc => {
        const lat = Number(loc.latitude), lng = Number(loc.longitude);
        return sampled.some(c => haversine(lat, lng, c[1], c[0]) <= nearRouteDistance);
      });
    }
    return locs;
  }, [showLocationsOnMap, selectedLocationsOnMap, savedLocations, nearRouteDistance, routeCoords]);

  const hoveredLocCfg = hoveredLocation ? (locTypeConfig[hoveredLocation.type] || locTypeConfig.other) : null;
  const hoveredLocNetwork = hoveredLocation?.notes?.match(/Rede\s+(\w+)/i)?.[1] || '';
  const networkColors = { PRIO: '#22c55e', Shell: '#f59e0b', Repsol: '#ef4444', BP: '#22c55e', Galp: '#3b82f6', Cepsa: '#ef4444', Total: '#ef4444' };

  if (loadError) {
    return (
      <Box style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
        <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>Erro ao carregar Google Maps</span>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>A carregar mapa...</span>
      </Box>
    );
  }

  // Vehicle markers (always show simulated vehicles, real ones only when showVehicles).
  // Exclude vehicles with invalid coordinates (e.g. Teltonika sentinel -214.748365 meaning "no fix yet").
  const hasValidPos = (v) => {
    if (!v.position) return false;
    const lat = v.position.latitude, lng = v.position.longitude;
    if (typeof lat !== 'number' || typeof lng !== 'number') return false;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    if (lat === 0 && lng === 0) return false;
    return true;
  };
  // Vehicles that the operator owns but whose GPS never reported a valid fix
  const vehiclesWithoutPosition = (vehicles || []).filter(v => !hasValidPos(v) && !v._simulating);
  // When the planner has a route in context (loaded route or computed
  // route), hide the rest of the fleet so the operator focuses on the
  // route itself — too many GPS markers compete with the polyline and
  // the stops. The simulated vehicle (if any) and a vehicle pinned via
  // singleVehicleId still pass through.
  const routeInContext = !!(route?.computedRoute || route?.dbId);
  const vehicleMarkers = vehicles.filter(v => {
    if (!hasValidPos(v) && !v._simulating) return false;
    if (singleVehicleId) return String(v.id) === String(singleVehicleId) || v._simulating;
    // Histórico aberto → esconde a restante frota para se ver só a viatura
    // selecionada (o trilho + o marcador de reprodução). Volta ao fechar.
    if (vehicleHistory) return String(v.id) === String(vehicleHistory.vehicle?.id) || v._simulating;
    if (routeInContext) return v._simulating;
    return showVehicles || v._simulating;
  }).map(v => {
    const attr = v.position.attributes || {};
    const fmtMin = (m) => { if (!m && m !== 0) return '—'; const h = Math.floor(m / 60); const mm = m % 60; return h > 0 ? `${h}h${String(mm).padStart(2,'0')}` : `${mm}min`; };
    return {
      id: v.id, plate: v.plate, type: v.type, labels: v.labels || [],
      // Grupo EcooGPS — propagado para a popup mostrar a pílula com
      // o nome (resolvido pelo cache groupNameById no popup).
      groupId: v.groupId != null ? Number(v.groupId) : null,
      // Friendly device name from EcooGPS (e.g. "***Peugeot - Carlos").
      // Used as the popup title since it's typically more recognisable
      // than the bare licence plate.
      name: v.name || null,
      position: { lat: v.position.latitude, lng: v.position.longitude },
      speed: v.position.speed || 0,
      // Heading 0-360 (0=N, 90=E). Drives the PNG angle selection so
      // the car icon points where the vehicle is actually going.
      heading: v.position.course != null ? v.position.course : (attr.course != null ? attr.course : 0),
      // Freshness — quando o último fix GPS chegou. Usado para badge
      // de "última actualização" no canto da foto.
      lastSeen: v.position.fixTime || v.position.deviceTime || null,
      // motionTime do Traccar: ISO quando o estado motion=false começou
      // (= quando o carro parou). O EcooGPS native usa isto para o
      // "Estacionado há X". Pode ser null se o Traccar não tiver o campo
      // configurado para este device.
      motion: attr.motion ?? null,
      motionTime: attr.motionTime || null,
      ignition: attr.ignition,
      _simulating: v._simulating || false,
      _simProgress: v._simProgress || null,
      driver: attr.driverName || v.driverName || v.currentDriver?.name || null,
      driverPhone: v.currentDriver?.phone || v.phone || null,
      address: v.position.address,
      // Extra vehicle data
      // Tensão da bateria — `power` já vem em volts (igual ao EcooGPS,
      // que não divide). Antes dividia por 1000 → mostrava 0.0 V.
      battery: attr.power != null ? `${Number(attr.power).toFixed(1)} V` : null,
      // Bateria interna do dispositivo (%) — atributo `battery` do Traccar.
      batteryPct: attr.battery != null ? `${Math.round(Number(attr.battery))} %` : null,
      // Nível de combustível — prefere io89 (Fuel Level CAN/FMS, %) com
      // fallback a io9 (analógico). Confirmado io89 na frota Move Express.
      fuel: (() => { const p = attr.io89 != null ? attr.io89 : attr.io9; return p != null ? `${Math.round(Number(p))} %` : null; })(),
      // Capacidade do depósito (L) e consumo (L/100km) — atributos de config
      // do device no EcooGPS. Só aparecem na popup quando estão preenchidos.
      fuelCapacity: v.capacity || null,
      fuelPer100km: v.fuelPer100km || null,
      // Odómetro — mesma prioridade de campos do EcooGPS (getOdometerValue):
      // totalDistance / td / io87 / io192 / km. Antes só lia totalDistance.
      odometer: (() => {
        let km = 0;
        if (attr.totalDistance > 0) km = Math.round(attr.totalDistance / 1000);
        if (attr.td > 0) km = Math.round(attr.td);
        if (attr.io87 > 0) km = Math.round(attr.io87 / 1000);
        if (attr.io192 > 0) km = Math.round(attr.io192 / 1000);
        if (attr.km > 0) km = Math.round(attr.km / 1000);
        return km > 0 ? `${km.toLocaleString('pt-PT')} km` : null;
      })(),
      engineHours: attr.hours ? `${Math.round(attr.hours / 3600000)} h` : null,
      // Tachograph data
      tachoActivity: attr.io460 != null ? ['Descanso', 'Disponível', 'Trabalho', 'Condução'][attr.io460] || '—' : null,
      tachoCardInserted: attr.io449 != null ? (attr.io449 > 0 ? 'Sim' : 'Não') : null,
      tachoDriverName: attr.io563 || attr.driverName || null,
      tachoContinuousDrive: attr.io10544 != null ? fmtMin(attr.io10544) : null,
      tachoBreakAccum: attr.io10543 != null ? fmtMin(attr.io10543) : null,
      tachoDriveAccum: attr.io10542 != null ? fmtMin(attr.io10542) : null,
      tachoRestDaily: attr.io10534 != null ? fmtMin(attr.io10534) : null,
      tachoRestWeekly: attr.io10535 != null ? fmtMin(attr.io10535) : null,
      tachoNextBreak: attr.io10537 != null ? fmtMin(attr.io10537) : null,
      tachoBreakDuration: attr.io10538 != null ? fmtMin(attr.io10538) : null,
      tachoWeeklyDrive: attr.io10536 != null ? fmtMin(attr.io10536) : null,
      tachoCardNumber: attr.io87 ? String(attr.io87) : null,
      tachoOdometer: attr.km ? `${Math.round(attr.km)} km` : (attr.odometer ? `${Math.round(attr.odometer / 1000)} km` : null),
      hasTacho: attr.io460 != null || attr.io10534 != null || attr.io10544 != null,
    };
  });

  // Reconstrói o mapa posição→info a cada render — o renderer do cluster
  // lê-o para (1) colorir o anel do donut por estado e (2) mostrar a
  // animação aranha em hover com as matrículas dos veículos agrupados.
  // O nome do condutor vem de attr.driverName (IO563 tacógrafo) ou da
  // associação tacho_drivers do EcooGPS (currentDriver.name), já
  // consolidado no campo `v.driver`.
  vehicleInfoByPos.current = new Map(
    vehicleMarkers.map(v => {
      const status = v._simulating ? 'green' : v.speed > 3 ? 'green' : v.ignition ? 'yellow' : 'red';
      const lat = Number(v.position.lat);
      const lng = Number(v.position.lng);
      return [`${lat.toFixed(6)},${lng.toFixed(6)}`, {
        id: v.id,
        status,
        plate: v.plate || '',
        driver: v.driver || '',
        type: v.type || '',
        position: { lat, lng },
      }];
    })
  );

  // Respect the tenant's default country — for non-EU tenants (Angola, Moçambique…)
  // the per-road-class colours are confusing and the labels (Autoestrada, IP/IC,
  // Nacional…) don't match the local road network. Render a single uniform stroke.
  const tenantCountry = String(clientSettings?.defaultCountry || 'PT').toUpperCase();
  const useRoadClassColors = EU_ROAD_CLASS_COUNTRIES.has(tenantCountry);
  // OT monitoring mode: when the operator opened "Ver rota no mapa" from an
  // order, the per-leg highlight is the operationally-relevant cue. Showing
  // road-class colours on top of the leg highlight muddles the map — drop the
  // colours and use a uniform blue base, letting the orange leg pop.
  const inOTMonitoringMode = !!monitoringOTId;
  // Vista "ver rota no mapa" ativa (com painel de paragens) — a rota inteira
  // desenha-se em azul e o troço da paragem selecionada (monitoringOTId) fica
  // laranja por cima. Na abertura não há OT selecionada → tudo azul.
  const inRouteInspectMode = inOTMonitoringMode || (Array.isArray(monitoringRouteLegs) && monitoringRouteLegs.length > 0);
  const hasColorSegs = useRoadClassColors && coloredSegments.length > 0 && !inRouteInspectMode;

  return (
    <Box style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Map area — always full */}
      <Box style={{ width: '100%', height: '100%', position: 'relative' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={tenantCenter}
        zoom={(centerFromSettings(clientSettings) || {}).zoom || 7}
        options={{
          disableDefaultUI: true,
          zoomControl: false,
          streetViewControl: true,
          streetViewControlOptions: { position: window.google.maps.ControlPosition.RIGHT_BOTTOM },
          mapTypeControl: false,
          fullscreenControl: false,
          scaleControl: true,
          // Allow deep zoom + clickable POIs so labels, shops, transit stations etc. stay visible at max zoom
          maxZoom: 22,
          clickableIcons: true,
          gestureHandling: 'greedy',
          draggableCursor: (mapClickMode || addFavMode) ? 'crosshair' : undefined,
        }}
        onClick={onMapClick}
        onRightClick={onMapRightClick}
        onLoad={onLoad}
        onZoomChanged={() => { const z = mapRef.current?.getZoom?.(); if (z != null) setMapZoom(z); }}
      >
        {/* Route polyline — colored by road type when available. Outer dark
            halo gives the polyline a clear outline on any basemap. The
            inner white halo is ONLY drawn when there are colored segments
            (multi-color road-class view) — for the uniform blue case the
            white inner was making the route appear white-ish on most
            basemaps, so we drop it. */}
        {/* Rotas alternativas — tracejadas, azul-claro (estilo Google Maps),
            por baixo da principal (que fica a azul sólido). Aparecem por
            defeito nas rotas manuais (toggle "Mostrar alternativas" ligado)
            e também no modo "Comparar". */}
        {!inRouteInspectMode && alternativePaths.map((altPath, i) => (
          <Polyline
            key={`alt-${i}`}
            path={altPath}
            options={{
              strokeColor: '#60a5fa',
              strokeOpacity: 0,             // a "sólida" é invisível;
              strokeWeight: 6,
              geodesic: true,
              clickable: false,
              zIndex: -1,                   // sempre por baixo da principal
              icons: [{
                icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.85, strokeWeight: 4, scale: 3 },
                offset: '0', repeat: '12px',
              }],
            }}
          />
        ))}
        {!inRouteInspectMode && (hasColorSegs || routePath.length > 1) && (
          <Polyline
            key="route-halo-outer"
            path={hasColorSegs ? coloredSegments.flatMap(s => s.path) : routePath}
            options={{
              strokeColor: '#0f172a',
              strokeOpacity: 0.35,
              strokeWeight: hasColorSegs ? 10 : 9,
              geodesic: true,
              clickable: false,
              zIndex: 0,
            }}
          />
        )}
        {!inRouteInspectMode && hasColorSegs && (
          <Polyline
            key="route-halo-inner"
            path={coloredSegments.flatMap(s => s.path)}
            options={{
              strokeColor: '#ffffff',
              strokeOpacity: 0.9,
              strokeWeight: 8,
              geodesic: true,
              clickable: false,
              zIndex: 1,
            }}
          />
        )}
        {!inRouteInspectMode && (hasColorSegs ? (
          coloredSegments.map((seg, idx) => (
            <Polyline
              key={`seg-${idx}`}
              path={seg.path}
              options={{
                strokeColor: seg.color,
                strokeOpacity: 0.98,
                strokeWeight: 6,
                geodesic: true,
                zIndex: seg.type === 'motorway' ? 4 : seg.type === 'highway' ? 3 : 2,
                clickable: true,
              }}
              onMouseMove={(e) => { if (e.latLng) setGhostMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
              onMouseOut={() => setGhostMarker(null)}
              onClick={(e) => {
                if (!e.latLng) return;
                setGhostMarker(null);
                const lat = e.latLng.lat(), lng = e.latLng.lng();
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                  const name = status === 'OK' && results[0] ? results[0].formatted_address : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                  dispatch(addWaypoint({ name, coordinates: [lng, lat] }));
                  dispatch(setNeedsRecalculation(true));
                });
              }}
            />
          ))
        ) : routePath.length > 1 && (
          <Polyline
            path={routePath}
            options={{
              strokeColor: UNIFORM_ROUTE_COLOR,
              strokeOpacity: 0.98,
              strokeWeight: 6,
              geodesic: true,
              clickable: true,
              zIndex: 5,
            }}
            onMouseMove={(e) => { if (e.latLng) setGhostMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
            onMouseOut={() => setGhostMarker(null)}
            onClick={(e) => {
              if (!e.latLng) return;
              setGhostMarker(null);
              const lat = e.latLng.lat(), lng = e.latLng.lng();
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                const name = status === 'OK' && results[0] ? results[0].formatted_address : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                dispatch(addWaypoint({ name, coordinates: [lng, lat] }));
                dispatch(setNeedsRecalculation(true));
              });
            }}
          />
        ))}

        {/* Modo "ver rota / inspecionar OT": a rota INTEIRA fica em azul suave
            (sem halos nem cores de tipo de estrada) e o troço da OT selecionada
            é desenhado a laranja por cima (otLegPath). Assim vê-se a rota toda
            e destaca-se só o troço do card escolhido. */}
        {inRouteInspectMode && routePathDecimated.length > 1 && (
          <Polyline
            path={routePathDecimated}
            options={INSPECT_MAIN_POLYLINE_OPTS}
          />
        )}

        {/* Return-home leg — tracejado azul sobre a rota principal. Antes
            era roxo e SÓ aparecia fora do inspect mode. O operador queixou-se
            de que o regresso desaparece quando inspeciona — mantemos sempre.
            Cor azul (#2563eb) reforça a ideia de "rota planeada" (mesma
            paleta do azul base) mas tracejado distingue-o como leg de
            regresso à casa/base. */}
        {returnHomePath && returnHomePath.length > 1 && (
          <Polyline
            path={returnHomePath}
            options={{
              strokeColor: '#2563eb',
              strokeOpacity: 0,
              icons: [{
                icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4, strokeColor: '#2563eb' },
                offset: '0',
                repeat: '14px',
              }],
              strokeWeight: 4,
              geodesic: true,
              clickable: false,
              zIndex: 6,
            }}
          />
        )}

        {/* OT leg highlight — shown when the operator is viewing one OT of a consolidated route.
            Renders in bold orange on top of the main polyline so it reads as "this is your OT". */}
        {otLegPath && otLegPath.length > 1 && (
          <Polyline
            path={otLegPath}
            options={INSPECT_OT_LEG_POLYLINE_OPTS}
          />
        )}

        {/* OT leg REAL (GPS) — quando há trail de execução, desenha por
            cima do verde/vermelho do trail o que o motorista realmente
            fez para chegar a esta OT. Mais informativo do que o leg
            planeado (laranja, em cima do azul) quando o motorista se
            desviou. zIndex 502 garante que fica acima do trail. */}
        {otLegPathReal && otLegPathReal.length > 1 && (
          <Polyline
            path={otLegPathReal}
            options={INSPECT_OT_LEG_REAL_POLYLINE_OPTS}
          />
        )}

        {/* Highlighted route segment (when clicking a step in details panel) */}
        {highlightedSegment && highlightedSegment.length > 1 && (
          <Polyline
            path={highlightedSegment}
            options={{
              strokeColor: '#f43f5e',
              strokeOpacity: 1,
              strokeWeight: 4,
              geodesic: true,
              zIndex: 10,
              clickable: false,
            }}
          />
        )}

        {/* Vehicle history trail */}
        {/* Anel de destaque sobre o veículo clicado no card "Em trânsito" */}
        {focusedVehicleId != null && (() => {
          const fv = vehicles.find(v => String(v.id) === String(focusedVehicleId));
          const pos = fv?.position;
          if (!pos || pos.latitude == null || pos.longitude == null) return null;
          return (
            <Marker
              position={{ lat: Number(pos.latitude), lng: Number(pos.longitude) }}
              zIndex={999}
              clickable={false}
              icon={focusRingIcon()}
            />
          );
        })()}

        {/* Trajeto do histórico. Com viagens selecionadas → mostra só essas,
            cada uma com a sua cor (+ início/fim). Sem seleção → dia inteiro. */}
        {selectedTrips.size > 0 && vehicleHistory?.data?.trips
          ? [...selectedTrips].map((ti) => {
              const trip = vehicleHistory.data.trips[ti];
              const path = (trip?.points || [])
                .filter(p => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
                .map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
              if (path.length < 2) return null;
              const color = tripColor(ti);
              return (
                <React.Fragment key={`trip-${ti}`}>
                  <Polyline path={path} options={{
                    strokeColor: color, strokeOpacity: 0.95, strokeWeight: 5, geodesic: true, zIndex: 8, clickable: false,
                    icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.6, fillColor: color, fillOpacity: 1, strokeWeight: 0 }, offset: '100%', repeat: '130px' }],
                  }} />
                  <Marker position={path[0]} zIndex={380} clickable={false}
                    icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: '#fff', fillOpacity: 1, strokeColor: color, strokeWeight: 3 }}
                    label={{ text: String(ti + 1), color, fontSize: '9px', fontWeight: '800' }} />
                  <Marker position={path[path.length - 1]} zIndex={380} clickable={false}
                    icon={{ path: 'M -4 -4 H 4 V 4 H -4 Z', scale: 1.4, fillColor: color, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1.5 }} />
                </React.Fragment>
              );
            })
          : (historyTrail && historyTrail.length > 1 && (
          <>
            {/* Contorno branco por baixo — realça a linha como no EcooGPS */}
            <Polyline path={historyTrail} options={{ strokeColor: '#ffffff', strokeOpacity: 1, strokeWeight: 8, geodesic: true, zIndex: 7, clickable: false }} />
            {/* Troços: azul (normal) / vermelho (excesso de velocidade), com setas */}
            {historyTrailSegments.map((seg, i) => (
              <Polyline key={`seg-${i}`} path={seg.path} options={{
                strokeColor: seg.over ? '#dc2626' : '#2563eb',
                strokeOpacity: 1, strokeWeight: seg.over ? 5.5 : 4.5, geodesic: true,
                zIndex: seg.over ? 9 : 8, clickable: false,
                icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.6, fillColor: seg.over ? '#7f1d1d' : '#1e3a8a', fillOpacity: 1, strokeWeight: 0 }, offset: '100%', repeat: '110px' }],
              }} />
            ))}
          </>
        ))}

        {/* Reprodução do histórico — troço já percorrido (azul forte) +
            marcador móvel orientado pelo rumo. */}
        {playbackCursor && vehicleHistory?.data?.points?.length > 1 && (() => {
          const pts = vehicleHistory.data.points;
          const traveled = pts.slice(0, playbackCursor.idx + 1)
            .filter(p => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
            .map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
          return (
            <>
              {traveled.length > 1 && (
                <Polyline
                  path={traveled}
                  options={{
                    strokeColor: '#1d4ed8', strokeOpacity: 1, strokeWeight: 6,
                    geodesic: true, zIndex: 9, clickable: false,
                  }}
                />
              )}
              {(() => {
                // Estado (cor) pela velocidade do ponto actual da reprodução.
                const cp = pts[playbackCursor.idx] || {};
                const spd = Number(cp.speed) || 0;
                const status = spd > 5 ? 'green' : (spd > 0.6 ? 'yellow' : 'red');
                return (
                  <Marker
                    position={{ lat: playbackCursor.lat, lng: playbackCursor.lng }}
                    zIndex={400}
                    clickable={false}
                    icon={playbackVehicleIcon(playbackCursor.heading, status, vehicleHistory.vehicle?.type)}
                  />
                );
              })()}
              {/* Balão sobre o marcador — cartão com rosa-dos-ventos + hora/velocidade/rumo */}
              {(() => {
                const cp = pts[playbackCursor.idx] || {};
                const hd = ((Number(playbackCursor.heading) || 0) % 360 + 360) % 360;
                const card = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'][Math.round(hd / 45) % 8];
                const spd = Math.round(Number(cp.speed) || 0);
                const hhmm = cp.timestamp ? new Date(cp.timestamp).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
                return (
                  <InfoWindow
                    position={{ lat: playbackCursor.lat, lng: playbackCursor.lng }}
                    options={{ disableAutoPan: true, pixelOffset: window.google?.maps ? new window.google.maps.Size(0, -34) : undefined }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '3px 6px 2px', fontFamily: 'Roboto, sans-serif' }}>
                      <CompassRose heading={hd} lang={lang} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, borderLeft: '1px solid #eef2f7', paddingLeft: 11 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1, color: spd > 3 ? '#15803d' : '#b91c1c' }}>
                          {spd} <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8' }}>km/h</span>
                        </div>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          🕐 {hhmm}
                        </div>
                        <div style={{ fontSize: 9.5, fontWeight: 800, color: '#2563eb', letterSpacing: 0.3 }}>{card} · {Math.round(hd)}°</div>
                      </div>
                    </div>
                  </InfoWindow>
                );
              })()}
            </>
          );
        })()}

        {/* Eventos do histórico no mapa — paragens (P) e excessos de velocidade (⚡).
            Só durante o histórico; informativos (tooltip com detalhe). */}
        {vehicleHistory?.data && historyEvents.stops.map((s, i) => {
          // Duração compacta na etiqueta (como no EcooGPS): 12m / 1h20.
          const durLabel = s.durMin < 60 ? `${s.durMin}m` : `${Math.floor(s.durMin / 60)}h${String(s.durMin % 60).padStart(2, '0')}`;
          return (
            <Marker key={`hstop-${i}`} position={{ lat: s.lat, lng: s.lng }} zIndex={360}
              title={`Paragem · ${durLabel}`}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE, scale: 9,
                fillColor: '#475569', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2,
              }}
              label={{ text: durLabel, color: '#fff', fontSize: '8px', fontWeight: '800' }}
            />
          );
        })}
        {vehicleHistory?.data && historyEvents.speeding.map((s, i) => (
          <Marker key={`hspeed-${i}`} position={{ lat: s.lat, lng: s.lng }} zIndex={370}
            title={`Excesso · ${s.speed} km/h`}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE, scale: 6,
              fillColor: '#ef4444', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2,
            }}
          />
        ))}
        {/* POIs perto do trajeto (favoritos/geofences) — nome no hover.
            Nome visível quando o mapa está com zoom suficiente. */}
        {vehicleHistory?.data && historyPois.map((loc) => (
          <React.Fragment key={`hpoi-${loc.id}`}>
            <Marker position={{ lat: Number(loc.latitude), lng: Number(loc.longitude) }} zIndex={330}
              title={loc.name || 'POI'}
              icon={{
                path: 'M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z',
                fillColor: '#0d9488', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1.4,
                scale: 1.1, anchor: new window.google.maps.Point(12, 22),
              }}
            />
            {loc.name && mapZoom >= favNameZoom && (
              <Marker position={{ lat: Number(loc.latitude), lng: Number(loc.longitude) }} zIndex={331}
                icon={buildFavNameIcon(loc.name)} clickable={false} />
            )}
          </React.Fragment>
        ))}

        {/* Percurso REAL do veículo (rota em trânsito / concluída) —
            verde dentro da rota planeada, vermelho nos desvios.
            Sobreposto ao trajecto planeado (azul). */}
        {actualTrailSegments.map((seg, i) => (seg.path.length > 1 ? (
          <Polyline
            key={`actual-${i}`}
            path={seg.path}
            options={{
              strokeColor: seg.off ? '#dc2626' : '#16a34a',
              strokeOpacity: 1,
              strokeWeight: seg.off ? 7 : 5,
              geodesic: true,
              zIndex: seg.off ? 61 : 60,
              clickable: false,
            }}
          />
        ) : null))}

        {/* Conectores pontilhados marco↔rota nas pontas (ver memo acima).
            Ligam visualmente a partida/chegada ao ponto onde o HERE encaixou
            a rota na estrada. Pontilhado (não sólido) para se perceber que é
            um conector e não uma via conduzível. */}
        {endpointConnectors.map((path, i) => (
          <Polyline
            key={`endpoint-connector-${i}`}
            path={path}
            options={{
              strokeOpacity: 0,
              clickable: false,
              zIndex: 3,
              icons: [{
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 2.2,
                  fillColor: '#2563eb',
                  fillOpacity: 0.9,
                  strokeWeight: 0,
                },
                offset: '0',
                repeat: '9px',
              }],
            }}
          />
        ))}

        {/* Origin / destination markers.
            Round-trip detection: when the origin and destination share the
            same coordinates (typical for assistance routes with return_home
            =true), render a SINGLE composite marker — half-green / half-red
            with both flags stacked — so the operator sees clearly that the
            driver returns to the start. Otherwise render two separate pins. */}
        {(() => {
          const oc = route.origin?.coordinates;
          const dc = route.destination?.coordinates;
          const sameSpot = oc && dc
            && Math.abs(Number(oc[0]) - Number(dc[0])) < 0.0001
            && Math.abs(Number(oc[1]) - Number(dc[1])) < 0.0001;
          if (sameSpot) {
            const ic = buildRoundTripIcon({});
            return (
              <Marker
                position={{ lat: oc[1], lng: oc[0] }}
                icon={{
                  url: ic.url,
                  scaledSize: new window.google.maps.Size(ic._w, ic._h),
                  anchor: new window.google.maps.Point(ic._w / 2, ic._h),
                }}
                zIndex={600}
                onClick={() => setSelectedPoint({
                  type: 'roundtrip',
                  name: `${route.origin.name || 'Partida'} ↻ ${route.destination.name || 'Regresso'}`,
                  pos: { lat: oc[1], lng: oc[0] },
                  color: '#0f172a',
                  label: 'Partida & Regresso',
                })}
              />
            );
          }
          // Distinct origin and destination — render both pins.
          return (
            <>
              {oc && (() => {
                const ic = buildEndpointIcon({ color: '#16a34a', glyph: '⚐' });
                return (
                  <Marker
                    position={{ lat: oc[1], lng: oc[0] }}
                    icon={{
                      url: ic.url,
                      scaledSize: new window.google.maps.Size(ic._w, ic._h),
                      anchor: new window.google.maps.Point(ic._w / 2, ic._h),
                    }}
                    zIndex={600}
                    onClick={() => setSelectedPoint({ type: 'origin', name: route.origin.name, pos: { lat: oc[1], lng: oc[0] }, color: '#16a34a', label: 'Partida' })}
                  />
                );
              })()}
              {dc && (() => {
                const ic = buildEndpointIcon({ color: '#dc2626', glyph: '⚑' });
                return (
                  <Marker
                    position={{ lat: dc[1], lng: dc[0] }}
                    icon={{
                      url: ic.url,
                      scaledSize: new window.google.maps.Size(ic._w, ic._h),
                      anchor: new window.google.maps.Point(ic._w / 2, ic._h),
                    }}
                    zIndex={600}
                    onClick={() => setSelectedPoint({ type: 'destination', name: route.destination.name, pos: { lat: dc[1], lng: dc[0] }, color: '#dc2626', label: 'Chegada' })}
                  />
                );
              })()}
            </>
          );
        })()}

        {/* Waypoint markers — teardrop pins com o número da paragem. As
            paragens cuja OT já foi entregue ficam VERDES e mostram o número
            DA PARAGEM + ✓ (ex.: "2✓") — assim o operador vê de relance qual
            paragem é e que já está concluída. */}
        {(route.waypoints || []).map((wp, idx) => {
          if (!wp.coordinates) return null;
          const done = wp.otStatus === 'delivered';
          const pinColor = done ? '#16a34a' : '#f59e0b';
          return (
            <Marker
              key={`wp-${idx}`}
              position={{ lat: wp.coordinates[1], lng: wp.coordinates[0] }}
              label={{
                text: done ? `${idx + 1}✓` : String(idx + 1),
                color: done ? '#ffffff' : '#b45309',
                fontWeight: '800', fontSize: done ? '11px' : '13px',
              }}
              icon={{
                path: PIN_PATH,
                fillColor: pinColor, fillOpacity: 1,
                strokeColor: '#ffffff', strokeWeight: 2.5,
                scale: 1.15,
                anchor: new window.google.maps.Point(12, 32),
                labelOrigin: new window.google.maps.Point(12, 11),
              }}
              zIndex={done ? 540 : 550}
              onClick={() => setSelectedPoint({ type: 'waypoint', name: wp.name, pos: { lat: wp.coordinates[1], lng: wp.coordinates[0] }, color: pinColor, label: `Ponto ${idx + 1}${done ? ' · entregue' : ''}` })}
            />
          );
        })}

        {/* ── Comparison mode — multiple saved routes overlaid for visual compare ── */}
        {Array.isArray(displayedSavedRoutes) && displayedSavedRoutes.length > 0 && (() => {
          const COMPARE_COLORS = ['#ef4444', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#10b981', '#6366f1', '#f97316'];
          return displayedSavedRoutes.map((r, idx) => {
            const color = COMPARE_COLORS[idx % COMPARE_COLORS.length];
            const coords = r.computedRoute?.geometry?.coordinates || [];
            const path = coords.map(c => ({ lat: c[1], lng: c[0] }));
            const label = r.name || `Rota ${idx + 1}`;
            return (
              <React.Fragment key={`cmp-${r.dbId || idx}`}>
                {path.length > 1 && (
                  <Polyline
                    path={path}
                    options={{
                      strokeColor: color,
                      strokeOpacity: 0.85,
                      strokeWeight: 5,
                      geodesic: false,
                      clickable: false,
                      zIndex: 200 + idx,
                    }}
                  />
                )}
                {r.origin?.coordinates && (
                  <Marker
                    position={{ lat: r.origin.coordinates[1], lng: r.origin.coordinates[0] }}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 9,
                      fillColor: color,
                      fillOpacity: 1,
                      strokeColor: '#fff',
                      strokeWeight: 2.5,
                    }}
                    label={{ text: String(idx + 1), color: '#fff', fontSize: '11px', fontWeight: 'bold' }}
                    title={`${label} — Origem: ${r.origin.name || ''}`}
                  />
                )}
                {r.destination?.coordinates && (
                  <Marker
                    position={{ lat: r.destination.coordinates[1], lng: r.destination.coordinates[0] }}
                    icon={{
                      path: 'M-7-7 L7-7 L7 7 L-7 7 Z',
                      fillColor: color,
                      fillOpacity: 1,
                      strokeColor: '#fff',
                      strokeWeight: 2.5,
                      scale: 1,
                      rotation: 45,
                    }}
                    title={`${label} — Destino: ${r.destination.name || ''}`}
                  />
                )}
              </React.Fragment>
            );
          });
        })()}

        {/* Route point InfoWindow (origin/dest/waypoint) */}
        {selectedPoint && (
          <InfoWindow
            position={selectedPoint.pos}
            onCloseClick={() => { setSelectedPoint(null); setPointPhoto(null); }}
          >
            <div style={{ padding: 0, minWidth: 260, fontFamily: 'Roboto, sans-serif', overflow: 'hidden' }}>
              {pointPhoto && (
                <div style={{ width: '100%', height: 110, overflow: 'hidden' }}>
                  <img src={pointPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}
              <div style={{ padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: selectedPoint.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>{selectedPoint.label.charAt(0)}</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: selectedPoint.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {selectedPoint.label}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedPoint.name}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>
                    {selectedPoint.pos.lat.toFixed(5)}, {selectedPoint.pos.lng.toFixed(5)}
                  </div>
                  {/* Pegman — clica para ver Street View no local exacto do
                      ponto. Mesmo ícone 🧍 usado nos favoritos/paragens do
                      tacógrafo para consistência visual. */}
                  <span
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('openStreetView', {
                        detail: { lat: selectedPoint.pos.lat, lng: selectedPoint.pos.lng },
                      }));
                    }}
                    title="Ver Street View do local"
                    style={{
                      cursor: 'pointer', fontSize: 18,
                      opacity: 0.75, transition: 'opacity 0.15s, transform 0.15s',
                      userSelect: 'none',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.transform = 'scale(1.15)'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = 0.75; e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    🧍
                  </span>
                </div>
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Ghost marker on polyline hover — draggable to create waypoint (hidden in readOnly) */}
        {ghostMarker && !route.readOnly && (
          <Marker
            position={ghostMarker}
            draggable={true}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#3b82f6',
              fillOpacity: 0.6,
              strokeColor: '#fff',
              strokeWeight: 2.5,
            }}
            cursor="grab"
            zIndex={999}
            title="Arrastar para criar ponto intermédio"
            onDragStart={() => {
              // Keep ghost visible during drag
            }}
            onDragEnd={(e) => {
              if (!e.latLng) return;
              const lat = e.latLng.lat(), lng = e.latLng.lng();
              setGhostMarker(null);
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                const name = status === 'OK' && results[0] ? results[0].formatted_address : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                dispatch(addWaypoint({ name, coordinates: [lng, lat] }));
                dispatch(setNeedsRecalculation(true));
              });
            }}
          />
        )}

        {/* Tachograph stop markers */}
        {stopsWithPos.map((stop) => (
          <Marker
            key={`stop-${stop.idx}`}
            position={stop.pos}
            onClick={() => setSelectedStop(selectedStop === stop.idx ? null : stop.idx)}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 14, fillColor: '#fff', fillOpacity: 1,
              strokeColor: '#f59e0b', strokeWeight: 3,
            }}
            label={{ text: `P${stop.idx + 1}`, color: '#f59e0b', fontWeight: '800', fontSize: '10px' }}
            zIndex={500}
            title={stop.location || stop.suggestedRestArea?.name || `Paragem ${stop.idx + 1}`}
          />
        ))}

        {/* Stop info window */}
        {selectedStop !== null && stopsWithPos.find(s => s.idx === selectedStop) && (() => {
          const stop = stopsWithPos.find(s => s.idx === selectedStop);
          const breakH = Math.floor(stop.duration / 60);
          const breakM = stop.duration % 60;
          const driveH = Math.floor(stop.drivingMinBefore / 60);
          const driveM = stop.drivingMinBefore % 60;
          return (
            <InfoWindow
              position={stop.pos}
              onCloseClick={() => setSelectedStop(null)}
            >
              <div style={{ padding: 0, minWidth: 260, fontFamily: 'Roboto, sans-serif', overflow: 'hidden' }}>
                {/* Photo */}
                {stopPhoto && (
                  <div style={{ width: '100%', height: 100, overflow: 'hidden', borderRadius: '0 0 0 0' }}>
                    <img src={stopPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                {/* Content */}
                <div style={{ padding: 8 }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>P{stop.idx + 1}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                      {stop.location || `Paragem ${stop.idx + 1}`}
                    </div>
                    <div style={{ fontSize: 10, color: '#8b5cf6', fontWeight: 600 }}>
                      Regulamento EU 561/2006
                    </div>
                  </div>
                  <span onClick={() => {
                      const svLat = stop.suggestedRestArea?.lat || stop.pos.lat;
                      const svLng = stop.suggestedRestArea?.lon || stop.pos.lng;
                      setSelectedStop(null);
                      setStreetViewPos({ lat: svLat, lng: svLng });
                      setStreetViewOpen(true);
                    }}
                    title="Street View"
                    style={{ cursor: 'pointer', fontSize: 14, flexShrink: 0, marginLeft: 4, opacity: 0.7, transition: 'opacity 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
                    🧍
                  </span>
                </div>

                {/* Details grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 11 }}>
                  <div style={{ color: '#94a3b8' }}>Km da rota:</div>
                  <div style={{ color: '#1e293b', fontWeight: 600 }}>{stop.km} km</div>

                  <div style={{ color: '#94a3b8' }}>Condução:</div>
                  <div style={{ color: '#1e293b', fontWeight: 600 }}>{driveH}h{driveM > 0 ? `${driveM}min` : ''}</div>

                  <div style={{ color: '#94a3b8' }}>Pausa:</div>
                  <div style={{ color: '#8b5cf6', fontWeight: 600 }}>{breakH > 0 ? `${breakH}h` : ''}{breakM}min</div>

                  {stop.arrivalTime && (<>
                    <div style={{ color: '#94a3b8' }}>Chegada aprox.:</div>
                    <div style={{ color: '#22c55e', fontWeight: 600 }}>{stop.arrivalTime}</div>
                  </>)}

                  {stop.tachoAdjusted && (<>
                    <div style={{ color: '#94a3b8' }}>Nota:</div>
                    <div style={{ color: '#f59e0b', fontWeight: 500, fontSize: 10 }}>Ajustado ao tacógrafo do motorista</div>
                  </>)}
                </div>

                {/* Suggested rest area */}
                {stop.suggestedRestArea && (
                  <div style={{ marginTop: 8, padding: '6px 8px', background: '#eff6ff', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>⛽</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8' }}>
                        {stop.suggestedRestArea.name}
                      </div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>
                        Área de serviço sugerida · Km {Math.round(stop.suggestedRestArea.distAlongRouteKm)}
                      </div>
                    </div>
                    {stop.suggestedRestArea.lat && stop.suggestedRestArea.lon && (
                      <span onClick={() => {
                        setSelectedStop(null);
                        setStreetViewPos({ lat: stop.suggestedRestArea.lat, lng: stop.suggestedRestArea.lon });
                        setStreetViewOpen(true);
                      }} style={{ cursor: 'pointer', fontSize: 16, flexShrink: 0, padding: '2px 4px', borderRadius: 4, background: '#dbeafe' }} title="Street View">
                        🧍
                      </span>
                    )}
                  </div>
                )}

              </div>{/* end content padding */}
              </div>{/* end outer */}
            </InfoWindow>
          );
        })()}

        {/* Aranha em hover — quando o operador passa o rato por cima de
            um cluster donut, mostramos N "braços" a apontar para fora,
            cada um terminando numa pequena bolha com a matrícula. Os
            offsets são em PIXELS (constante visual a qualquer zoom). */}
        {hoveredCluster && (() => {
          const map = mapRef.current;
          if (!map) return null;
          const N = hoveredCluster.vehicles.length;
          if (N === 0) return null;
          const radiusPx = N <= 6 ? 92 : N <= 12 ? 118 : 145;
          return hoveredCluster.vehicles.map((v, i) => {
            const angle = (i / N) * 2 * Math.PI - Math.PI / 2; // 1ª ponta para cima
            const dx = Math.cos(angle) * radiusPx;
            const dy = Math.sin(angle) * radiusPx;
            const tip = pixelOffsetToLatLng(map, hoveredCluster.center, dx, dy);
            if (!tip) return null;
            const tipIcon = buildSpiderTipIcon(v.plate, v.status);
            return (
              <React.Fragment key={`spider-${hoveredCluster.id}-${v.id || i}`}>
                <Polyline
                  path={[hoveredCluster.center, tip]}
                  options={{
                    strokeColor: '#0f172a',
                    strokeOpacity: 0.55,
                    strokeWeight: 1.6,
                    clickable: false,
                    zIndex: 9998,
                  }}
                />
                <Marker
                  position={tip}
                  icon={{
                    url: tipIcon.url,
                    scaledSize: new window.google.maps.Size(tipIcon.w, tipIcon.h),
                    anchor: new window.google.maps.Point(tipIcon.w / 2, tipIcon.h / 2),
                  }}
                  zIndex={9999}
                  title={v.driver ? `${v.plate} · ${v.driver}` : v.plate}
                  onClick={() => {
                    // Em vez de abrir o popup, faz zoom in até à posição
                    // do veículo seleccionado. O operador depois decide
                    // se quer abrir os detalhes clicando no marcador.
                    // Zoom 17 desfaz os clusters em quase todos os casos
                    // (cada veículo passa a ter o seu próprio marcador);
                    // Math.max preserva o zoom actual se já estiver mais
                    // alto, para não dar a sensação de "zoom out".
                    cancelSpiderHide();
                    setHoveredCluster(null);
                    if (map && v.position && Number.isFinite(v.position.lat) && Number.isFinite(v.position.lng)) {
                      try {
                        map.panTo(v.position);
                        const target = Math.max(map.getZoom() || 12, 17);
                        map.setZoom(target);
                      } catch { /* map not ready */ }
                    }
                  }}
                  onMouseOver={cancelSpiderHide}
                  onMouseOut={scheduleSpiderHide}
                />
              </React.Fragment>
            );
          });
        })()}

        {/* Vehicle markers — agrupados num cluster donut (estilo EcooGPS):
            anel colorido por estado (movimento/ralenti/parado) + nº no centro. */}
        <GoogleMarkerClusterer options={clustererOptions}>
          {(clusterer) => (
            <>
              {vehicleMarkers.map(v => {
          // Marcador composto: DUAS bolhas (condutor em cima, matrícula
          // em baixo) ligadas ao ícone do tipo de veículo por duas
          // pernas a 45°. Se não há condutor conhecido, mostra só a
          // matrícula em cima (comportamento anterior). O nome do
          // condutor vem de v.driver — já consolidado de IO563 do
          // tacógrafo OU da tabela tacho_drivers do EcooGPS (RFID).
          const status = v._simulating ? 'green'
            : v.speed > 3 ? 'green'
            : v.ignition ? 'yellow'
            : 'red';
          const palettes = {
            green:  { bg: '#22c55e', dot: '#22c55e', text: '#fff' },
            yellow: { bg: '#eab308', dot: '#eab308', text: '#fff' },
            red:    { bg: '#ef4444', dot: '#ef4444', text: '#fff' },
          };
          const pal = palettes[status];
          const plate = (v.plate || '').toUpperCase();
          // Condutor (tacógrafo OU RFID EcooGPS). Truncado para caber
          // sem o marcador ficar gigante; primeiros 2 nomes só.
          const driverFull = (v.driver || '').trim();
          const driverShort = driverFull
            ? driverFull.split(/\s+/).slice(0, 2).join(' ').slice(0, 18).toUpperCase()
            : '';
          // Veículo desativado no EcooGPS — mostra-se SEMPRE com a bolha de
          // cima como selo "DESATIVADO" (substitui o condutor, que para um
          // veículo desativado não é relevante) e a matrícula em baixo. O
          // SVG todo recebe opacidade 0.5 para deixar claro que é só visual.
          const isDisabled = !!v.disabled;
          const topLabel = isDisabled ? 'DESATIVADO' : driverShort;
          const hasDriver = !!topLabel; // mantém a estrutura de 2 bolhas quando disabled
          // charW e bubbleH escalados para acompanhar o aumento de
          // font-size (10 → 11) na nova bolha branca — sem isto o
          // texto ficava cortado nos lados e baixo.
          const charW = 6.5;
          const padX = 7;
          const wFor = s => Math.max(50, Math.min(150, padX * 2 + s.length * charW));
          // Mesma largura nas duas bolhas (alinhamento visual limpo).
          const bubbleW = hasDriver
            ? Math.max(wFor(plate), wFor(topLabel))
            : wFor(plate);
          const bubbleH = 18;
          const legV = 16;            // componente da perna a 45°
          const pad = 4;
          const gid = `g${v.id}`;
          const shadow = `<defs><filter id="${gid}s" x="-40%" y="-20%" width="180%" height="150%"><feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="#000" flood-opacity="0.28"/></filter></defs>`;
          // Bolha — fillColor + texto. Topo (condutor) usa slate-700,
          // fundo (matrícula) usa a cor de estado.
          const DRIVER_BG = '#334155';
          const escapeXmlInline = s => String(s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&apos;');

          // ── Versão ORIGINAL (rectângulo colorido + texto branco) ────
          // Preservada como comentário para reverter rapidamente caso
          // a versão "sem rectângulo, texto preto" não funcione bem em
          // todos os cenários (mapa satélite, fundos escuros, etc).
          // Para reverter: descomentar este bloco e comentar o seguinte.
          //
          // const bubbleSvg = (bx, by, fillColor, text) =>
          //   `<g filter="url(#${gid}s)"><rect x="${bx}" y="${by}" width="${bubbleW}" height="${bubbleH}" rx="4" ry="4" fill="${fillColor}" fill-opacity="0.82"/></g>`
          //   + `<text x="${bx + bubbleW / 2}" y="${by + bubbleH / 2 + 0.5}" text-anchor="middle" dominant-baseline="central" font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="9.5" font-weight="700" fill="#fff" letter-spacing="0.3">${escapeXmlInline(text)}</text>`;

          // ── Versão "sem rectângulo, texto preto + halo branco" ──────
          // Preservada como comentário — esta versão funcionava sobre
          // fundos escuros mas ficava a confundir-se com os nomes de
          // ── Versão ACTUAL — texto BOLD com halo branco (sem rectângulo) ─
          // Técnica de cartografia (Google Maps usa-a nas labels de
          // estradas/lugares): `paint-order="stroke"` desenha primeiro
          // um halo branco fino à volta do glifo e depois o preenchimento
          // escuro por cima. Resultado: a label lê-se sobre qualquer
          // fundo (cidades, estradas, satélite) sem ocupar espaço com um
          // rectângulo. Bold a 700 + halo a 3.5 dão contraste suficiente.
          // `fillColor` mantido na signature para não tocar nos callers
          // (úteis para reverter para a versão colorida original).
          // eslint-disable-next-line no-unused-vars
          const bubbleSvg = (bx, by, fillColor, text) =>
            `<text x="${bx + bubbleW / 2}" y="${by + bubbleH / 2 + 0.5}" text-anchor="middle" dominant-baseline="central"`
            + ` font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="11" font-weight="700" letter-spacing="0.3"`
            + ` paint-order="stroke" stroke="#ffffff" stroke-width="3.5" stroke-linejoin="round" fill="#0f172a">${escapeXmlInline(text)}</text>`;

          // Versão alternativa (rectângulo branco semi-transparente) — comentada.
          // Para reverter à caixa, comenta o bloco acima e descomenta este.
          //
          // const bubbleSvg = (bx, by, fillColor, text) =>
          //   `<g filter="url(#${gid}s)"><rect x="${bx}" y="${by}" width="${bubbleW}" height="${bubbleH}" rx="4" ry="4" fill="#ffffff" fill-opacity="0.85"/></g>`
          //   + `<text x="${bx + bubbleW / 2}" y="${by + bubbleH / 2 + 0.5}" text-anchor="middle" dominant-baseline="central"`
          //   + ` font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="11" font-weight="600" letter-spacing="0.3"`
          //   + ` fill="#0f172a">${escapeXmlInline(text)}</text>`;

          // Helper da "perna" que liga o veículo à label. Dois rectos
          // sobrepostos (halo branco + linha escura) para a perna ser
          // visível em qualquer fundo de mapa — antes era um único
          // stroke 1.4 que mal se notava sobre estradas/zonas claras.
          const legLine = (x1, y1, x2, y2) =>
            `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#ffffff" stroke-width="3.5" stroke-linecap="round"/>`
            + `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#0f172a" stroke-width="2" stroke-linecap="round"/>`;

          // Ícone EcooGPS do tipo de veículo (2D), base64 — null/undefined
          // enquanto carrega ou se falhar.
          const iconCat = vehicleIconCategory(v.type);
          const iconB64 = getVehicleIconDataUrl(iconCat, status, v.heading);

          let composedIconUrl, svgW, svgH, anchorX, anchorY;
          if (iconB64) {
            // Ícone do veículo no centro, com bolhas a 45° para cima
            // (condutor) e para baixo (matrícula).
            // Tamanho igual ao EcooGPS: base 45 × zoom da categoria, ESCALADO
            // pelo zoom do mapa (getIconSize = mapZoom/17 + 0.1, limitado a 1).
            // Sem esta escala os 3D (zoom 2.0-2.5) ficavam enormes.
            const zf = Math.max(0.28, Math.min(1, mapZoom / 17 + 0.1));
            const S = Math.round(Math.max(18, 45 * vehicleIconZoom(iconCat) * zf));
            const half = S / 2;
            const diag = Math.ceil(half * 1.42); // folga para a rotação
            const cx = diag + pad;
            const cy = Math.max(diag, legV + bubbleH) + pad;
            const bx = cx + legV;
            const topBy = cy - legV - bubbleH;          // condutor (em cima)
            const bottomBy = cy + legV;                  // matrícula (em baixo)
            // Altura — tem de fitar o ícone E (se houver) a bolha de baixo.
            const bottomReach = hasDriver ? (legV + bubbleH) : 0;
            svgW = Math.max(cx + diag, bx + bubbleW) + pad;
            svgH = cy + Math.max(diag, bottomReach) + pad;
            anchorX = cx; anchorY = cy;
            // Rotação do ícone: 2D roda pelo rumo completo; 3D usa frame já
            // orientado + só a correcção fina; 3D estático não roda.
            const heading = vehicleIconRotation(iconCat, v.heading);
            // Selo "DESATIVADO" — pill cinzenta com texto branco. Visualmente
            // distinto do estilo halo das outras labels, para se ler logo o
            // estado especial do veículo (mesmo com a opacidade reduzida).
            const disabledBadgeSvg = (bxx, byy) =>
              `<rect x="${bxx}" y="${byy}" width="${bubbleW}" height="${bubbleH}" rx="9" ry="9" fill="#475569" filter="url(#${gid}s)"/>`
              + `<text x="${bxx + bubbleW / 2}" y="${byy + bubbleH / 2 + 0.5}" text-anchor="middle" dominant-baseline="central"`
              + ` font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="9" font-weight="800" letter-spacing="0.5" fill="#ffffff">DESATIVADO</text>`;
            let body = legLine(cx, cy, bx, cy - legV);
            if (isDisabled) {
              body += disabledBadgeSvg(bx, topBy)
                + legLine(cx, cy, bx, cy + legV)
                + bubbleSvg(bx, bottomBy, pal.bg, plate);
            } else if (hasDriver) {
              body += bubbleSvg(bx, topBy, DRIVER_BG, driverShort)
                + legLine(cx, cy, bx, cy + legV)
                + bubbleSvg(bx, bottomBy, pal.bg, plate);
            } else {
              // Sem condutor — bolha única (matrícula) em cima, como antes.
              body += bubbleSvg(bx, topBy, pal.bg, plate);
            }
            body += `<image href="${iconB64}" x="${cx - half}" y="${cy - half}" width="${S}" height="${S}" transform="rotate(${heading} ${cx} ${cy})"/>`;
            // Quando desativado, todo o composto (ícone + linhas + labels +
            // selo) recebe opacidade reduzida para se distinguir dos ativos.
            if (isDisabled) {
              body = `<g opacity="0.55">${body}</g>`;
            }
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}">${shadow}${body}</svg>`;
            composedIconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
          } else {
            // Fallback — marcador-bolinha (comportamento anterior).
            const dotR = 7;
            const dotBorder = 2.5;
            const dotOuter = dotR + dotBorder;
            const dotCx = pad + dotOuter;
            const legTopX = dotCx + legV;
            const bubbleY = pad;
            const legTopY = bubbleY + bubbleH;
            const dotCy = legTopY + legV;
            svgW = legTopX + bubbleW + pad;
            svgH = dotCy + dotOuter + pad;
            anchorX = dotCx; anchorY = dotCy;
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}">`
              + shadow
              + legLine(dotCx, dotCy, legTopX, legTopY)
              + bubbleSvg(legTopX, bubbleY, pal.bg, plate)
              + `<circle cx="${dotCx}" cy="${dotCy}" r="${dotOuter}" fill="#fff" filter="url(#${gid}s)"/>`
              + `<circle cx="${dotCx}" cy="${dotCy}" r="${dotR}" fill="${pal.dot}"/>`
              + `</svg>`;
            composedIconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
          }
          return (
              <Marker
                key={`v-${v.id}`}
                position={v.position}
                clusterer={clusterer}
                onClick={() => openVehiclePopup(v)}
                icon={{
                  url: composedIconUrl,
                  scaledSize: new window.google.maps.Size(svgW, svgH),
                  // Âncora na posição GPS exacta do veículo.
                  anchor: new window.google.maps.Point(anchorX, anchorY),
                }}
                zIndex={v.speed > 3 ? 200 : v._simulating ? 300 : 100}
                title={`${v.plate} · ${v.speed > 0 ? Math.round(v.speed) + ' km/h' : 'Parado'}`}
              />
          );
        })}
            </>
          )}
        </GoogleMarkerClusterer>

        {/* Vehicle info window — full details + tachograph */}
        {selectedVehicle && ReactDOM.createPortal(
          // Painel do veículo como OVERLAY FIXO e CENTRADO (portal p/ body), em vez
          // de InfoWindow ancorado ao marcador — assim nunca cola ao topo nem fica
          // atrás da toolbar de pesquisa. Fecha pelo × próprio do conteúdo.
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 1400,
            width: (selectedVehicleTacho?.data || selectedVehicleTacho?.loading) ? 920 : 520,
            maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 96px)', overflow: 'auto',
            background: '#fff', borderRadius: 14, boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
          }}>
            <div className="veh-popup-scroll" style={{ position: 'relative', display: 'flex', gap: 0, fontFamily: 'Roboto, sans-serif', minWidth: (selectedVehicleTacho?.data || selectedVehicleTacho?.loading) ? 890 : 500 }}>
              {/* Botão de fechar PRÓPRIO — o × nativo do InfoWindow fica no topo
                  da bolha e pode ficar tapado pela barra de topo da app; este
                  fica sempre acessível no canto do conteúdo. */}
              <button type="button"
                onClick={() => { setSelectedVehicle(null); setSelectedVehicleTacho(null); }}
                title={lang === 'pt' ? 'Fechar' : 'Close'} aria-label={lang === 'pt' ? 'Fechar' : 'Close'}
                style={{
                  position: 'absolute', top: 6, right: 6, zIndex: 20,
                  width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
                  border: '1px solid #e2e8f0', background: '#fff', color: '#475569',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 17, lineHeight: 1, fontWeight: 700, padding: 0,
                  boxShadow: '0 2px 6px rgba(15,23,42,0.18)',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                ✕
              </button>
              {/* Left column — Vehicle info (layout EcooGPS) */}
              <div style={{ flex: 1, padding: 0, minWidth: 480, borderRight: (selectedVehicleTacho?.data || selectedVehicleTacho?.loading) ? '1px solid #e2e8f0' : 'none' }}>
                {/* Topo: foto (esq.) + identificação (dir.) — cabeçalho com o
                    mesmo fundo do wizard "Nova Ordem de Combustível". */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 14px 12px', background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 60%)', borderBottom: '1px solid #fed7aa' }}>
                {(selectedVehicle.position?.lat || selectedVehiclePhoto?.url) && (
                  <div style={{ position: 'relative', width: 150, height: 128, flexShrink: 0, overflow: 'hidden', borderRadius: 12, background: '#e2e8f0', boxShadow: '0 2px 6px rgba(15,23,42,0.18)', border: '2px solid #fff' }}>
                    {selectedVehiclePhoto?.url ? (
                      <img src={selectedVehiclePhoto.url} alt="Foto do veículo" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : selectedVehicle.position?.lat ? (
                      <StreetViewThumbnail
                        lat={selectedVehicle.position.lat}
                        lng={selectedVehicle.position.lng}
                        heading={selectedVehicle.heading || 0}
                        apiKey={GOOGLE_KEY}
                        width={320}
                        height={320}
                      />
                    ) : null}
                    {/* Foto do veículo — adicionar / substituir / remover */}
                    <input ref={vehiclePhotoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleVehiclePhotoUpload} />
                    <button type="button" title={selectedVehiclePhoto?.url ? 'Substituir foto' : 'Adicionar foto do veículo'} disabled={selectedVehiclePhoto?.uploading}
                      onClick={() => vehiclePhotoInputRef.current && vehiclePhotoInputRef.current.click()}
                      style={{ position: 'absolute', left: 6, bottom: 6, width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.72)', color: '#fff', cursor: selectedVehiclePhoto?.uploading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, padding: 0 }}>
                      {selectedVehiclePhoto?.uploading ? '…' : '📷'}
                    </button>
                    {selectedVehiclePhoto?.url && !selectedVehiclePhoto?.uploading && (
                      <button type="button" title="Remover foto" onClick={handleVehiclePhotoRemove}
                        style={{ position: 'absolute', left: 37, bottom: 6, width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(220,38,38,0.82)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, padding: 0 }}>
                        🗑️
                      </button>
                    )}
                    {/* Freshness badge — bottom-right canto da imagem.
                        Mostra há quanto tempo o último fix chegou. */}
                    {selectedVehicle.lastSeen && (() => {
                      const ageMs = Date.now() - new Date(selectedVehicle.lastSeen).getTime();
                      if (!Number.isFinite(ageMs) || ageMs < 0) return null;
                      const ageMin = Math.round(ageMs / 60000);
                      let label;
                      if (ageMin < 1) label = 'agora';
                      else if (ageMin < 60) label = `${ageMin}m`;
                      else if (ageMin < 1440) label = `${Math.round(ageMin / 60)}h`;
                      else label = `${Math.round(ageMin / 1440)}d`;
                      return (
                        <span style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(15,23,42,0.78)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                )}

                <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
                  {/* Header: nome amigável (Peugeot - Carlos) ou plate fallback.
                      paddingRight deixa espaço para o botão X de fechar (canto). */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, paddingRight: 64 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: selectedVehicle.speed > 3 ? '#22c55e' : selectedVehicle.ignition ? '#f59e0b' : '#ef4444' }} />
                    <strong style={{ fontSize: 14.5, fontWeight: 800, color: '#9a3412', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedVehicle.name || selectedVehicle.plate}
                    </strong>
                    <span onClick={() => {
                        const pos = selectedVehicle.position;
                        if (!pos) return;
                        setSelectedVehicle(null);
                        setSelectedVehicleTacho(null);
                        setStreetViewPos({ lat: pos.lat, lng: pos.lng });
                        setStreetViewOpen(true);
                      }}
                      title="Abrir Street View"
                      style={{ cursor: 'pointer', fontSize: 14, flexShrink: 0, opacity: 0.7 }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
                      🧍
                    </span>
                  </div>
                  {/* Subtítulo estilo wizard: matrícula · grupo (laranja, maiúsculas) */}
                  {(() => {
                    const showPlate = selectedVehicle.name && selectedVehicle.plate && selectedVehicle.name !== selectedVehicle.plate;
                    const grp = Number(selectedVehicle.groupId) > 0
                      ? (groupNameById[String(selectedVehicle.groupId)] || `Grupo #${selectedVehicle.groupId}`) : null;
                    const parts = [showPlate ? selectedVehicle.plate : null, grp].filter(Boolean);
                    if (!parts.length) return null;
                    return (
                      <div style={{ fontSize: 10, color: '#c2410c', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.3, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {parts.join(' · ')}
                      </div>
                    );
                  })()}
                  {/* Etiquetas do dispositivo (EcooGPS) — "nome|cor" */}
                  {Array.isArray(selectedVehicle.labels) && selectedVehicle.labels.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 5 }}>
                      {selectedVehicle.labels.map((lbl, i) => {
                        const [nm, hex] = String(lbl).split('|');
                        const color = /^[0-9a-fA-F]{6}$/.test(hex || '') ? `#${hex}` : '#64748b';
                        return (
                          <span key={i} style={{
                            fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 7,
                            background: `${color}1f`, color, border: `1px solid ${color}55`,
                          }}>{nm || lbl}</span>
                        );
                      })}
                    </div>
                  )}
                  {/* Motorista + telefone (clicável quando há) */}
                  {selectedVehicle.driver && (
                    <div style={{ fontSize: 11, color: '#1e293b', marginBottom: 3 }}>
                      👤 {selectedVehicle.driver}
                      {selectedVehicle.driverPhone && (
                        <a href={`tel:${selectedVehicle.driverPhone}`}
                          style={{ marginLeft: 6, color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
                          📞 {selectedVehicle.driverPhone}
                        </a>
                      )}
                    </div>
                  )}
                </div>{/* fim coluna identificação */}
                </div>{/* fim topo (foto + identificação) */}
                <div style={{ padding: '9px 14px 12px' }}>
                  {/* Rota que o veículo está a fazer agora (nº + nome). */}
                  {(() => {
                    const ar = activeRouteByVehicle[String(selectedVehicle.id)];
                    if (!ar) return null;
                    const num = ar.orderNumber;
                    const nm = ar.routeName;
                    const showName = nm && nm !== num;
                    const inTransit = ar.status === 'in_transit';
                    const stLabel = inTransit ? (lang === 'pt' ? 'EM TRÂNSITO' : 'IN TRANSIT') : (lang === 'pt' ? 'ATRIBUÍDA' : 'ASSIGNED');
                    // Gradiente por estado: verde (em trânsito) / laranja (atribuída).
                    const grad = inTransit
                      ? 'linear-gradient(135deg, #16a34a, #15803d)'
                      : 'linear-gradient(135deg, #fb923c, #f97316)';
                    return (
                      <div className="route-active-badge"
                        onClick={() => {
                          if (ar.otId != null) dispatch(setPendingOrderTimelineId({ id: ar.otId, orderNumber: ar.orderNumber || ar.customRef || null }));
                          dispatch(navigateTo('orders'));
                          setSelectedVehicle(null); setSelectedVehicleTacho(null);
                        }}
                        title={lang === 'pt' ? 'Abrir a Ordem e a cronologia' : 'Open the order and its timeline'}
                        style={{
                        margin: '2px 0 7px', padding: '7px 10px', borderRadius: 8,
                        background: grad, color: '#fff', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap',
                      }}>
                        <span className="route-active-dot" style={{
                          width: 9, height: 9, borderRadius: '50%', background: '#fff',
                          flexShrink: 0, boxShadow: '0 0 0 2px rgba(255,255,255,0.35)',
                        }} />
                        <span style={{ fontSize: 13 }}>🛣️</span>
                        <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '-0.01em' }}>
                          {lang === 'pt' ? 'Rota' : 'Route'}: {num || nm || '—'}
                        </span>
                        {showName && <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>· {nm}</span>}
                        <span style={{ fontSize: 8.5, fontWeight: 800, color: '#fff', background: 'rgba(255,255,255,0.22)', padding: '2px 7px', borderRadius: 999, letterSpacing: 0.4 }}>{stLabel}</span>
                        {/* Chegada REAL ao P.A. (da cronologia: arrivedPaAt) */}
                        {(ar.arrivedPaAt || ar.arrivedPa2At) && (() => {
                          const d = new Date(ar.arrivedPaAt || ar.arrivedPa2At);
                          if (isNaN(d.getTime())) return null;
                          const s = d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                          return <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.95)', whiteSpace: 'nowrap' }}>🏁 Chegou às {s}</span>;
                        })()}
                      </div>
                    );
                  })()}
                  {/* Nome / Referência da OT (customRef) — quando a viatura está numa OT */}
                  {(() => {
                    const ar = activeRouteByVehicle[String(selectedVehicle.id)];
                    if (!ar || !ar.customRef) return null;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, margin: '-2px 0 7px 2px', fontSize: 11, fontWeight: 700, color: '#9a3412' }}>
                        <span style={{ fontSize: 12 }}>🏷️</span>
                        <span style={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', fontSize: 9, letterSpacing: 0.4 }}>{lang === 'pt' ? 'Ref.' : 'Ref.'}</span>
                        <span>{ar.customRef}</span>
                      </div>
                    );
                  })()}
                  {/* Combustível — P.A. de destino da OT actual (1 ou 2 na
                      entrega dividida). Só para OT fuel (Move Express). */}
                  {(() => {
                    const ar = activeRouteByVehicle[String(selectedVehicle.id)];
                    if (!ar || ar.serviceType !== 'fuel') return null;
                    if (!ar.deliveryName && !ar.deliveryName2) return null;
                    // Link "abrir destino no Maps": por coordenadas se as houver,
                    // senão pesquisa pelo nome (fallback).
                    const destMapsUrl = (lat, lng, name) =>
                      (lat != null && lng != null)
                        ? `https://www.google.com/maps?q=${lat},${lng}`
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name || '')}`;
                    const FlagLink = ({ lat, lng, name }) => (
                      <a href={destMapsUrl(lat, lng, name)} target="_blank" rel="noopener noreferrer"
                        title={lang === 'pt' ? 'Abrir destino no Maps' : 'Open destination in Maps'}
                        onClick={e => e.stopPropagation()}
                        style={{
                          marginLeft: 'auto', flexShrink: 0, textDecoration: 'none', fontSize: 15, lineHeight: 1,
                          display: 'inline-flex', alignItems: 'center', cursor: 'pointer',
                          filter: 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.28))',
                          transform: 'translateZ(0)', transition: 'transform 0.15s ease',
                        }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.22) rotate(-6deg)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1) rotate(0deg)'}>
                        🏁
                      </a>
                    );
                    return (
                      <div style={{
                        margin: '0 0 7px', padding: '6px 9px', borderRadius: 8,
                        background: '#fff7ed', border: '1px solid #fed7aa',
                        display: 'flex', flexDirection: 'column', gap: 4,
                      }}>
                        {ar.deliveryName && (
                          <div style={{ fontSize: 11, color: '#7c2d12', display: 'flex', gap: 5, alignItems: 'center' }}>
                            <span>⛽</span>
                            <span style={{ fontWeight: 800, color: '#9a3412' }}>{ar.splitDelivery ? (lang === 'pt' ? 'P.A. 1' : 'Point 1') : (lang === 'pt' ? 'Destino' : 'Destination')}:</span>
                            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ar.deliveryName}</span>
                            <FlagLink lat={ar.deliveryLat} lng={ar.deliveryLng} name={ar.deliveryName} />
                          </div>
                        )}
                        {ar.splitDelivery && ar.deliveryName2 && (
                          <div style={{ fontSize: 11, color: '#7c2d12', display: 'flex', gap: 5, alignItems: 'center' }}>
                            <span>⛽</span>
                            <span style={{ fontWeight: 800, color: '#9a3412' }}>{lang === 'pt' ? 'P.A. 2' : 'Point 2'}:</span>
                            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ar.deliveryName2}</span>
                            <FlagLink lat={ar.deliveryLat2} lng={ar.deliveryLng2} name={ar.deliveryName2} />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Localização — morada + coordenadas (copiar) + abrir no Maps.
                      À direita, rosa-dos-ventos com o sentido de marcha. */}
                  <div style={{ background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 8, padding: '6px 9px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {selectedVehicle.address && <div style={{ fontSize: 10.5, color: '#334155', marginBottom: 3, lineHeight: 1.35 }}>📍 {selectedVehicle.address}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span
                          title="Clica para copiar as coordenadas"
                          onClick={() => { try { navigator.clipboard.writeText(`${selectedVehicle.position.lat.toFixed(6)}, ${selectedVehicle.position.lng.toFixed(6)}`); } catch (_) {} }}
                          style={{ fontSize: 9.5, color: '#64748b', fontFamily: 'monospace', cursor: 'pointer' }}>
                          {selectedVehicle.position.lat.toFixed(6)}, {selectedVehicle.position.lng.toFixed(6)} 📋
                        </span>
                        <a
                          href={`https://www.google.com/maps?q=${selectedVehicle.position.lat},${selectedVehicle.position.lng}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ marginLeft: 'auto', fontSize: 9.5, fontWeight: 800, color: '#2563eb', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 7px' }}>
                          🗺️ Abrir no Maps
                        </a>
                      </div>
                      {/* Data e hora — sempre visível. Mostra a CHEGADA REAL ao
                          P.A. (da cronologia) quando existe, o "parado desde"
                          (≈ chegada quando estacionado) e a última posição GPS.
                          Datas completas (com ano) — pedido Move Express. */}
                      {(() => {
                        const fmtFull = (iso) => { if (!iso) return null; const d = new Date(iso); return isNaN(d.getTime()) ? null : d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
                        const arObj = activeRouteByVehicle[String(selectedVehicle.id)];
                        const arr = fmtFull(arObj?.arrivedPaAt || arObj?.arrivedPa2At);
                        const park = fmtFull(selectedVehicle.motionTime);
                        const pos = fmtFull(selectedVehicle.lastSeen);
                        if (!arr && !park && !pos) return null;
                        return (
                          <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 2, fontSize: 10, fontWeight: 700 }}>
                            {arr && <span style={{ color: '#15803d' }}>🏁 {lang === 'pt' ? 'Chegada ao P.A.' : 'Arrived at point'}: {arr}</span>}
                            {park && <span style={{ color: '#64748b' }}>🅿️ {lang === 'pt' ? 'Parado desde' : 'Stopped since'}: {park}</span>}
                            {pos && <span style={{ color: '#94a3b8' }}>🕐 {lang === 'pt' ? 'Última posição' : 'Last position'}: {pos}</span>}
                          </div>
                        );
                      })()}
                    </div>
                    <CompassRose heading={selectedVehicle.heading || 0} lang={lang} />
                  </div>
                {/* Estado + métricas — cartão coeso com stat tiles */}
                {(() => {
                  const NO_SIGNAL_MIN = 20; // só "sem sinal" se o fix tiver ≥20 min
                  const fmtAge = (min) => min < 60 ? `${min} min` : min < 1440 ? `${Math.round(min / 60)} h` : `${Math.round(min / 1440)} d`;
                  const speed = selectedVehicle.speed || 0;
                  const moving = speed > 3;
                  const fixAge = selectedVehicle.lastSeen
                    ? Math.round((Date.now() - new Date(selectedVehicle.lastSeen).getTime()) / 60000) : null;
                  const noSignal = !moving && fixAge != null && fixAge >= NO_SIGNAL_MIN;
                  let sText, sColor, sBg, sIcon;
                  if (moving) { sText = `Em condução · ${Math.round(speed)} km/h`; sColor = '#15803d'; sBg = '#dcfce7'; sIcon = '🚚'; }
                  else if (noSignal) { sText = `Sem sinal GPS · há ${fmtAge(fixAge)}`; sColor = '#b91c1c'; sBg = '#fee2e2'; sIcon = '📡'; }
                  else {
                    const dur = selectedVehicle.motionTime
                      ? Math.round((Date.now() - new Date(selectedVehicle.motionTime).getTime()) / 60000) : null;
                    const durStr = dur != null && dur > 0 ? ` há ${fmtAge(dur)}` : '';
                    if (selectedVehicle.ignition) { sText = `Ralenti${durStr}`; sColor = '#b45309'; sBg = '#fef3c7'; sIcon = '🟠'; }
                    else { sText = `Estacionado${durStr}`; sColor = '#dc2626'; sBg = '#fef2f2'; sIcon = '🅿️'; }
                  }
                  // Nível de combustível (io9/io89 → "70 %") para a mini-barra.
                  const fuelPct = (() => {
                    if (!selectedVehicle.fuel) return null;
                    const m = /(\d+(?:\.\d+)?)/.exec(String(selectedVehicle.fuel));
                    return m ? Math.max(0, Math.min(100, Number(m[1]))) : null;
                  })();
                  // Máximo do depósito (L), restante (máximo × %), consumo (L/100km).
                  const parseNum = (v) => {
                    if (v == null || v === '') return null;
                    const n = Number(String(v).replace(',', '.').replace(/[^\d.]/g, ''));
                    return Number.isFinite(n) && n > 0 ? n : null;
                  };
                  const capacityL = parseNum(selectedVehicle.fuelCapacity);
                  const remainingL = (capacityL != null && fuelPct != null) ? Math.round(capacityL * fuelPct / 100) : null;
                  const consumo = parseNum(selectedVehicle.fuelPer100km);
                  const Tile = ({ icon, label, value, color }) => (
                    <div style={{ background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 8, padding: '5px 8px' }}>
                      <div style={{ fontSize: 8.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ fontSize: 9 }}>{icon}</span>{label}
                      </div>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: color || '#0f172a', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
                    </div>
                  );
                  return (
                    <>
                      {/* Estado + data/hora (desde / última atualização) na MESMA linha,
                          data à direita — poupa altura para os botões de baixo. */}
                      {(() => {
                        const fmtDT = (iso) => { if (!iso) return null; const d = new Date(iso); return isNaN(d.getTime()) ? null : d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); };
                        const since = fmtDT(selectedVehicle.motionTime);
                        const upd = fmtDT(selectedVehicle.lastSeen);
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 7 }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, padding: '4px 11px', borderRadius: 999, background: sBg, color: sColor }}>
                              <span style={{ fontSize: 11 }}>{sIcon}</span>{sText}
                            </div>
                            {(since || upd) && (
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end', fontSize: 10, fontWeight: 700, color: '#64748b' }}>
                                {since && <span>🕐 desde {since}</span>}
                                {upd && <span>🔄 atualizado {upd}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5, marginBottom: 2 }}>
                        <Tile icon="⚡" label="Velocidade" value={`${Math.round(speed)} km/h`} color={moving ? '#16a34a' : '#0f172a'} />
                        {selectedVehicle.battery && <Tile icon="🔋" label="Tensão" value={selectedVehicle.battery} color="#16a34a" />}
                        {selectedVehicle.batteryPct && <Tile icon="🔌" label="Bateria" value={selectedVehicle.batteryPct} color="#16a34a" />}
                        {selectedVehicle.fuel && (
                          <div style={{ background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 8, padding: '5px 8px' }}>
                            <div style={{ fontSize: 8.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 3 }}>
                              <span style={{ fontSize: 9 }}>⛽</span>Combustível
                            </div>
                            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#2563eb', marginTop: 1 }}>{selectedVehicle.fuel}</div>
                            {fuelPct != null && (
                              <div style={{ height: 4, borderRadius: 3, background: '#e2e8f0', marginTop: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${fuelPct}%`, height: '100%', background: fuelPct < 20 ? '#ef4444' : fuelPct < 40 ? '#f59e0b' : '#22c55e' }} />
                              </div>
                            )}
                          </div>
                        )}
                        {capacityL != null && <Tile icon="🛢️" label="Máximo" value={`${capacityL.toLocaleString('pt-PT')} L`} />}
                        {remainingL != null && <Tile icon="⛽" label="Restante" value={`${remainingL.toLocaleString('pt-PT')} L`} color="#2563eb" />}
                        {consumo != null && <Tile icon="📉" label="Consumo" value={`${consumo.toLocaleString('pt-PT')} L/100km`} />}
                        {/* Consumo MÉDIO real (CAN) do dia — Σ consumption ÷ distância. Só CAN com dados. */}
                        {(() => {
                          const c = selectedVehicleConsumption;
                          if (!c || String(c.vehicleId) !== String(selectedVehicle.id) || !c.hasConsumption || c.avgConsumptionL100 == null) return null;
                          const est = c.method === 'fuel_level' ? '~' : '';
                          return <Tile icon="📊" label="Consumo médio (7d)" value={`${est}${c.avgConsumptionL100.toLocaleString('pt-PT')} L/100km`} color="#7c3aed" />;
                        })()}
                        {selectedVehicle.odometer && <Tile icon="🛣️" label="Odómetro" value={selectedVehicle.odometer} />}
                        {selectedVehicle.engineHours && <Tile icon="⏱️" label="Horas motor" value={selectedVehicle.engineHours} />}
                        <Tile icon={selectedVehicle.ignition ? '🟢' : '⚪'} label="Ignição" value={selectedVehicle.ignition ? 'Ligada' : 'Desligada'} color={selectedVehicle.ignition ? '#16a34a' : '#64748b'} />
                      </div>
                    </>
                  );
                })()}
                {/* Atalhos rápidos: usar a posição actual do veículo
                    como origem / destino da rota em planeamento manual.
                    Só aparecem nesse contexto — fora dele iam confundir. */}
                {canQuickPickEndpoints && selectedVehicle.position && (
                  <div style={{ marginTop: 8, paddingTop: 7, borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: 8.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                      Planear rota com este ponto
                    </div>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button onClick={() => {
                        applyVehicleEndpoint('origin', selectedVehicle);
                        setSelectedVehicle(null);
                        setSelectedVehicleTacho(null);
                      }} title="Usar a posição actual do veículo como ORIGEM da rota"
                        onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                        onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}
                        style={{
                          flex: 1, border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 0', cursor: 'pointer',
                          background: '#eff6ff', color: '#1d4ed8', fontSize: 10.5, fontWeight: 700, transition: 'background 0.15s',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                        }}>
                        <NearMeIcon style={{ fontSize: 13 }} />
                        Origem
                      </button>
                      <button onClick={() => {
                        applyVehicleEndpoint('destination', selectedVehicle);
                        setSelectedVehicle(null);
                        setSelectedVehicleTacho(null);
                      }} title="Usar a posição actual do veículo como DESTINO da rota"
                        onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fef2f2'}
                        style={{
                          flex: 1, border: '1px solid #fecaca', borderRadius: 8, padding: '6px 0', cursor: 'pointer',
                          background: '#fef2f2', color: '#b91c1c', fontSize: 10.5, fontWeight: 700, transition: 'background 0.15s',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                        }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#b91c1c"><path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/></svg>
                        Destino
                      </button>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 5, marginTop: 8, paddingTop: 7, borderTop: '1px solid #f1f5f9' }}>
                  <button onClick={() => {
                    const veh = selectedVehicle;
                    setSelectedVehicle(null);
                    loadVehicleHistory(veh, todayStr());
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                    onMouseLeave={e => e.currentTarget.style.background = '#eef2ff'}
                    style={{
                      flex: 1, border: '1px solid #c7d2fe', borderRadius: 8, padding: '6px 0', cursor: 'pointer',
                      background: '#eef2ff', color: '#4338ca', fontSize: 10.5, fontWeight: 700, transition: 'background 0.15s',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    }}>
                    <HistoryIcon style={{ fontSize: 13 }} /> Histórico
                  </button>
                  <button onClick={() => {
                    if (mapRef.current && selectedVehicle.position) {
                      mapRef.current.panTo(selectedVehicle.position);
                      mapRef.current.setZoom(16);
                    }
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = '#dcfce7'}
                    onMouseLeave={e => e.currentTarget.style.background = '#f0fdf4'}
                    style={{
                      flex: 1, border: '1px solid #bbf7d0', borderRadius: 8, padding: '6px 0', cursor: 'pointer',
                      background: '#f0fdf4', color: '#15803d', fontSize: 10.5, fontWeight: 700, transition: 'background 0.15s',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    }}>
                    🎯 Seguir
                  </button>
                </div>
                </div>{/* fim do padding interno da coluna esquerda */}
              </div>
              {/* Right column — Tacógrafo, layout estilo EcooGPS, dados on-demand de /tacho-drivers */}
              {(() => {
                const isCurrent = selectedVehicleTacho && String(selectedVehicleTacho.vehicleId) === String(selectedVehicle.id);
                const td = isCurrent && selectedVehicleTacho.data ? selectedVehicleTacho.data : null;
                const loading = isCurrent && selectedVehicleTacho.loading;
                if (!td && !loading) return null;
                // Format minutes → HH:MM (zero-padded), like EcooGPS
                const fmtHM = (m) => {
                  if (m == null || m === '') return '—';
                  const n = Number(m);
                  if (isNaN(n)) return '—';
                  const h = Math.floor(n / 60);
                  const mm = n % 60;
                  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
                };
                const activityColor = td?.activityName === 'Condução' ? '#22c55e'
                  : td?.activityName === 'Descanso' ? '#3b82f6'
                  : td?.activityName === 'Trabalho' ? '#f59e0b' : '#94a3b8';
                const labelStyle = { color: '#64748b', fontSize: 10.5, paddingRight: 8 };
                const valueStyle = { fontWeight: 600, color: '#0f172a', fontSize: 10.5, textAlign: 'right', whiteSpace: 'nowrap' };
                const row = (label, value, color) => (
                  <>
                    <div style={labelStyle}>{label}:</div>
                    <div style={{ ...valueStyle, color: color || valueStyle.color }}>{value}</div>
                  </>
                );
                return (
                  <div style={{ flex: 1, padding: '8px 12px', minWidth: 440, background: '#fafbfc' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 8, paddingBottom: 5, borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' }}>
                      TACÓGRAFO
                    </div>
                    {loading && (
                      <div style={{ fontSize: 11, color: '#94a3b8', padding: '8px 0' }}>A carregar dados do tacógrafo…</div>
                    )}
                    {!loading && td && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 8px' }}>
                        {row('Actividade actual', td.activityName || '—', activityColor)}
                        {row('Hora do tacógrafo', td.fixTime || td.lastSeen || '—')}
                        {row('Odómetro', td.odometerKm != null ? `${td.odometerKm.toLocaleString('pt-PT')} km` : '—')}
                        {row('Cartão inserido', td.cardInserted ? (td.tachoCard || 'Sim') : 'Não')}
                        {td.name && (
                          <>
                            <div />
                            <div style={{ ...valueStyle, color: '#3b82f6', fontWeight: 700 }}>{td.name}</div>
                          </>
                        )}
                        <div style={{ gridColumn: '1 / -1', height: 1, background: '#e2e8f0', margin: '6px 0' }} />
                        {row('Tempo de condução contínua', fmtHM(td.continuousDrive))}
                        {row('Tempo de pausa acumulada', fmtHM(td.breakAccum))}
                        {row('Tempo de condução acumulada', fmtHM(td.driveAccumMin))}
                        {row('Tempo de condução diária', fmtHM(td.dailyDriveMin))}
                        {row('Tempo de condução semanal', fmtHM(td.weeklyDriveMin))}
                        {row('Tempo restante próxima pausa semanal', fmtHM(td.nextBreakWeekly))}
                        {row('Tempo restante repouso diário', fmtHM(td.dailyRest))}
                        {row('Tempo restante repouso semanal', fmtHM(td.weeklyRest))}
                        {row('Tempo restante próxima pausa', fmtHM(td.nextBreak), '#f59e0b')}
                        {row('Repouso mínimo diário', fmtHM(td.minDailyRest))}
                        {row('Repouso mínimo semanal', fmtHM(td.minWeeklyRest))}
                        {row('Duração próxima pausa/descanso', fmtHM(td.nextRestDuration))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>,
          document.body
        )}

        {/* Service markers from RouteServicesPanel */}
        {serviceMarkers.map(svc => (
          <Marker
            key={`svc-${svc.id}`}
            position={{ lat: svc.lat, lng: svc.lng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: svc.color || '#3b82f6',
              fillOpacity: 0.9,
              strokeColor: '#fff',
              strokeWeight: 2.5,
            }}
            onMouseOver={() => setHoveredService(svc)}
            onMouseOut={() => setHoveredService(null)}
            onClick={() => {
              setHoveredService(svc);
              if (mapRef.current) {
                mapRef.current.panTo({ lat: svc.lat, lng: svc.lng });
                if (mapRef.current.getZoom() < 14) mapRef.current.setZoom(14);
              }
            }}
          />
        ))}

        {/* Service InfoWindow on hover */}
        {hoveredService && (
          <InfoWindow
            position={{ lat: hoveredService.lat, lng: hoveredService.lng }}
            onCloseClick={() => setHoveredService(null)}
            options={{ disableAutoPan: true, pixelOffset: new window.google.maps.Size(0, -12) }}
          >
            <div style={{ padding: 0, minWidth: 220, maxWidth: 260, fontFamily: 'Roboto, sans-serif', overflow: 'hidden' }}>
              {hoveredService.photo && (
                <div style={{ width: '100%', height: 100, overflow: 'hidden' }}>
                  <img src={hoveredService.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}
              <div style={{ padding: '8px 10px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>
                  {hoveredService.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  {hoveredService.rating && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>
                      ★ {hoveredService.rating.toFixed(1)}
                      <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}> ({hoveredService.totalRatings || 0})</span>
                    </span>
                  )}
                  {hoveredService.isOpen !== null && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px',
                      background: hoveredService.isOpen ? '#dcfce7' : '#fef2f2',
                      color: hoveredService.isOpen ? '#16a34a' : '#dc2626',
                    }}>
                      {hoveredService.isOpen ? 'Aberto' : 'Fechado'}
                    </span>
                  )}
                </div>
                {hoveredService.address && (
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>{hoveredService.address}</div>
                )}
                <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>
                  Km {hoveredService.km} da rota
                </div>
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Saved locations on map */}
        {/* Status chip while incidents toggle is on. Confirms the API
            call worked even when the viewport happens to have zero
            incidents (otherwise the operator can't tell empty-state
            apart from a silent failure). */}
        {showIncidents && (
          <Box
            style={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 6,
              padding: '6px 12px',
              borderRadius: 999,
              background: incidentsLoading
                ? 'rgba(15,23,42,0.85)'
                : (incidents.length > 0 ? 'rgba(220,38,38,0.95)' : 'rgba(34,197,94,0.95)'),
              color: '#fff',
              fontSize: '0.7rem',
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(15,23,42,0.20)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              pointerEvents: 'none',
            }}
          >
            {incidentsLoading
              ? <>⏳ A consultar HERE…</>
              : incidents.length > 0
                ? <>⚠ {incidents.length} {incidents.length === 1 ? 'incidente' : 'incidentes'} nesta zona</>
                : <>✓ Sem incidentes nesta zona</>
            }
          </Box>
        )}

        {/* Traffic incidents markers — accidents (🚨), congestions (🟠),
            road works (🚧), road closures (⛔), other (⚠️). Pulled from
            HERE Traffic API for the current viewport. */}
        {showIncidents && incidents.map(inc => {
          const cfg = inc.typeName || INCIDENT_TYPE_MAP[inc.type] || INCIDENT_TYPE_MAP[4];
          return (
            <Marker
              key={`inc-${inc.id}`}
              position={{ lat: inc.coordinates[1], lng: inc.coordinates[0] }}
              icon={{
                path: 'M12 2L1 21h22L12 2z',
                fillColor: cfg.color,
                fillOpacity: 0.95,
                strokeColor: '#fff',
                strokeWeight: 1.5,
                scale: 1.3,
                anchor: new window.google.maps.Point(12, 21),
                labelOrigin: new window.google.maps.Point(12, 14),
              }}
              label={{ text: cfg.emoji, fontSize: '11px' }}
              zIndex={500}
              onClick={() => setSelectedIncident(inc)}
            />
          );
        })}

        {selectedIncident && (
          <InfoWindow
            position={{ lat: selectedIncident.coordinates[1], lng: selectedIncident.coordinates[0] }}
            onCloseClick={() => setSelectedIncident(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -22), maxWidth: 320 }}
          >
            <div style={{ minWidth: 240, fontFamily: 'Roboto, sans-serif' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px',
                background: `${selectedIncident.typeName.color}15`,
                borderLeft: `4px solid ${selectedIncident.typeName.color}`,
                borderRadius: '4px 4px 0 0', marginBottom: 6,
              }}>
                <span style={{ fontSize: 22 }}>{selectedIncident.typeName.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    {selectedIncident.typeName.label}
                    {selectedIncident.roadClosed && (
                      <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '1px 6px', borderRadius: 4 }}>
                        VIA FECHADA
                      </span>
                    )}
                  </div>
                  {selectedIncident.criticality && (
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                      Severidade: {({ critical: 'crítica', major: 'alta', minor: 'baixa', lowImpact: 'baixa' })[selectedIncident.criticality] || selectedIncident.criticality}
                    </div>
                  )}
                  {/* Badge da fonte — operador percebe imediatamente
                      qual provider reportou o incidente. Quando ambos
                      reportam o mesmo (alsoReportedBy), aparece um
                      badge de confirmação dupla — sinal de alta
                      confiança. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                    {selectedIncident.source === 'tomtom' ? (
                      <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: '#dc2626', padding: '2px 6px', borderRadius: 4, letterSpacing: '0.04em' }}>
                        TOMTOM
                      </span>
                    ) : (
                      <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: '#0ea5e9', padding: '2px 6px', borderRadius: 4, letterSpacing: '0.04em' }}>
                        HERE
                      </span>
                    )}
                    {selectedIncident.alsoReportedBy && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '2px 6px', borderRadius: 4, letterSpacing: '0.04em' }}>
                        ✓ confirmado por {selectedIncident.alsoReportedBy.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ padding: '0 10px 8px' }}>
                <div style={{ fontSize: 12, color: '#1e293b', marginBottom: 4 }}>
                  {selectedIncident.summary || selectedIncident.description}
                </div>
                {selectedIncident.summary && selectedIncident.description !== selectedIncident.summary && (
                  <div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>
                    {selectedIncident.description}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 10, color: '#64748b' }}>
                  {selectedIncident.startTime && (
                    <span>🕐 Início: {new Date(selectedIncident.startTime).toLocaleString('pt-PT')}</span>
                  )}
                  {selectedIncident.endTime && (
                    <span>⏳ Fim previsto: {new Date(selectedIncident.endTime).toLocaleString('pt-PT')}</span>
                  )}
                  {selectedIncident.lastUpdated && (
                    <span style={{ fontSize: 9, color: '#94a3b8' }}>
                      Actualizado: {new Date(selectedIncident.lastUpdated).toLocaleString('pt-PT')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </InfoWindow>
        )}

        {displayedLocations.map(loc => {
          const cfg = locTypeConfig[loc.type] || locTypeConfig.other;
          return (
          <React.Fragment key={`loc-wrap-${loc.id}`}>
            {/* Nome do favorito ELEVADO com um traço a ligar ao pin (igual
                aos nomes dos condutores nas viaturas). Só a partir do limiar de
                zoom escolhido pelo operador (submenu Favoritos) — a nível de
                país/região os nomes sobrepunham-se todos. */}
            {loc.name && mapZoom >= favNameZoom && (
              <Marker
                key={`loc-name-${loc.id}`}
                position={{ lat: Number(loc.latitude), lng: Number(loc.longitude) }}
                clickable={false}
                icon={buildFavNameIcon(loc.name)}
                zIndex={(loc.isPreferred ? 200 : 100) + 1}
              />
            )}
            <Marker
              key={`loc-${loc.id}`}
              position={{ lat: Number(loc.latitude), lng: Number(loc.longitude) }}
              icon={{
                path: 'M12 0C5.37 0 0 5.37 0 12c0 9 12 20 12 20s12-11 12-20C24 5.37 18.63 0 12 0z',
                fillColor: cfg.color,
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
                scale: 1.2,
                anchor: new window.google.maps.Point(12, 32),
                labelOrigin: new window.google.maps.Point(12, 11),
              }}
              label={{ text: cfg.emoji, fontSize: '13px' }}
              zIndex={loc.isPreferred ? 200 : 100}
              draggable={true}
              onClick={() => {
                setHoveredLocation(loc);
              }}
              onDragEnd={(e) => {
                // User has physically moved a saved favourite on the map.
                // Persist the new lat/lng straight to the database and
                // reverse-geocode in the background to keep the address
                // string in sync with the new pin location.
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                  const newAddress = (status === 'OK' && results && results[0])
                    ? results[0].formatted_address
                    : loc.address;
                  dispatch(updateLocation({
                    token,
                    id: loc.id,
                    data: {
                      ...loc,
                      latitude: lat,
                      longitude: lng,
                      address: newAddress,
                    },
                  }));
                  // Refresh InfoWindow if this favourite is the one open
                  setHoveredLocation(prev => prev?.id === loc.id
                    ? { ...prev, latitude: lat, longitude: lng, address: newAddress }
                    : prev);
                });
              }}
            />
          </React.Fragment>
          );
        })}

        {/* Hovered location InfoWindow — detailed */}
        {hoveredLocation && showLocationsOnMap && hoveredLocCfg && (
          <InfoWindow
            position={{ lat: Number(hoveredLocation.latitude), lng: Number(hoveredLocation.longitude) }}
            onCloseClick={() => setHoveredLocation(null)}
            options={{ disableAutoPan: true, pixelOffset: new window.google.maps.Size(0, -14), maxWidth: 300 }}
          >
            <div style={{ padding: 0, minWidth: 240, fontFamily: 'Roboto, sans-serif' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: `${hoveredLocCfg.color}12`, borderRadius: '4px 4px 0 0', marginBottom: 6, borderLeft: `4px solid ${hoveredLocCfg.color}` }}>
                <span style={{ fontSize: 20 }}>{hoveredLocCfg.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{hoveredLocation.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: hoveredLocCfg.color, background: `${hoveredLocCfg.color}18`, padding: '1px 6px', borderRadius: 4 }}>
                      {({ warehouse: 'Armazém', safe_point: 'Ponto Seguro', overnight: 'Pernoita', fuel: 'Combustível', poi: 'POI', rest_area: 'Área Descanso', garage: 'Oficina', other: 'Outro' })[hoveredLocation.type] || 'Outro'}
                    </span>
                    {hoveredLocation.isPreferred && <span style={{ fontSize: 10, fontWeight: 700, color: '#eab308', background: '#fef9c3', padding: '1px 6px', borderRadius: 4 }}>★ Preferencial</span>}
                    {hoveredLocNetwork && <span style={{ fontSize: 10, fontWeight: 700, color: networkColors[hoveredLocNetwork] || '#64748b', background: `${(networkColors[hoveredLocNetwork] || '#64748b')}15`, padding: '1px 6px', borderRadius: 4 }}>{hoveredLocNetwork}</span>}
                  </div>
                </div>
              </div>
              <div style={{ padding: '0 10px 6px' }}>
                {hoveredLocation.address && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: '#475569', marginBottom: 4 }}>
                    <span style={{ flexShrink: 0, marginTop: 1 }}>📍</span>
                    <span>{hoveredLocation.address}</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>
                    📐 {Number(hoveredLocation.latitude).toFixed(5)}, {Number(hoveredLocation.longitude).toFixed(5)}
                  </span>
                  {/* Pegman — mesmo padrão usado nos pontos da rota e
                      paragens do tacógrafo. Abre o Street View na posição
                      do favorito. */}
                  <span
                    onClick={() => {
                      const lat = Number(hoveredLocation.latitude);
                      const lng = Number(hoveredLocation.longitude);
                      setStreetViewPos({ lat, lng });
                      setStreetViewOpen(true);
                      setHoveredLocation(null);
                    }}
                    title="Ver Street View do local"
                    style={{
                      cursor: 'pointer', fontSize: 18,
                      opacity: 0.75, transition: 'opacity 0.15s, transform 0.15s',
                      userSelect: 'none',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.transform = 'scale(1.15)'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = 0.75; e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    🧍
                  </span>
                </div>
                {hoveredLocation.notes && (
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, padding: '4px 8px', background: '#f8fafc', borderRadius: 6, borderLeft: '2px solid #e2e8f0' }}>
                    {hoveredLocation.notes}
                  </div>
                )}
                {/* Atalhos rápidos para popular Origem / Destino da rota
                    em planeamento manual. Só aparecem nesse contexto —
                    evita ruído quando o operador está só a consultar. */}
                {canQuickPickEndpoints && (
                  <div style={{ display: 'flex', gap: 6, borderTop: '1px solid #e8eaed', paddingTop: 8, marginTop: 8 }}>
                    <div
                      onClick={() => {
                        const lat = Number(hoveredLocation.latitude);
                        const lng = Number(hoveredLocation.longitude);
                        applyQuickPickEndpoint('origin', { name: hoveredLocation.name, coordinates: [lng, lat] });
                        setHoveredLocation(null);
                      }}
                      title="Usar este favorito como origem da rota"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                        borderRadius: 16, background: '#e8f0fe', cursor: 'pointer',
                        fontSize: 11, color: '#1a73e8', fontWeight: 600,
                      }}>
                      <NearMeIcon style={{ fontSize: 13 }} />
                      Origem
                    </div>
                    <div
                      onClick={() => {
                        const lat = Number(hoveredLocation.latitude);
                        const lng = Number(hoveredLocation.longitude);
                        applyQuickPickEndpoint('destination', { name: hoveredLocation.name, coordinates: [lng, lat] });
                        setHoveredLocation(null);
                      }}
                      title="Usar este favorito como destino da rota"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                        borderRadius: 16, background: '#e8f0fe', cursor: 'pointer',
                        fontSize: 11, color: '#1a73e8', fontWeight: 600,
                      }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="#1a73e8"><path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/></svg>
                      Destino
                    </div>
                  </div>
                )}
                {/* Editar favorito — vai direto para a edição deste favorito
                    (abre a gestão de favoritos no formulário já preenchido).
                    Disponível em qualquer vista, não só no planeamento. */}
                <div style={{
                  display: 'flex', justifyContent: 'center',
                  borderTop: canQuickPickEndpoints ? 'none' : '1px solid #e8eaed',
                  paddingTop: canQuickPickEndpoints ? 0 : 8, marginTop: 8,
                }}>
                  <div
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('favoritesOpenEdit', { detail: { loc: hoveredLocation } }));
                      dispatch(setActiveView('favorites'));
                      setHoveredLocation(null);
                    }}
                    title="Editar este favorito"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                      borderRadius: 16, background: '#f1f5f9', cursor: 'pointer',
                      fontSize: 11, color: '#475569', fontWeight: 600,
                    }}>
                    <EditLocationIcon style={{ fontSize: 14 }} />
                    Editar favorito
                  </div>
                </div>
                <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center', marginTop: 6, fontStyle: 'italic' }}>
                  💡 Arraste o pin para actualizar a localização
                </div>
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Search pin — Google style red marker with InfoWindow */}
        {searchPin && (
          <>
            <Marker
              position={{ lat: searchPin.lat, lng: searchPin.lng }}
              icon={{
                path: 'M12 0C5.37 0 0 5.37 0 12c0 9 12 20 12 20s12-11 12-20C24 5.37 18.63 0 12 0z',
                fillColor: '#EA4335',
                fillOpacity: 1,
                strokeColor: '#B31412',
                strokeWeight: 1,
                scale: 1.2,
                anchor: new window.google.maps.Point(12, 32),
              }}
              zIndex={1000}
              onClick={() => setSearchPinOpen(o => !o)}
              animation={window.google.maps.Animation.DROP}
            />
            {searchPinOpen && (
              <InfoWindow
                position={{ lat: searchPin.lat, lng: searchPin.lng }}
                onCloseClick={() => { setSearchPinOpen(false); setSearchPin(null); }}
                options={{ pixelOffset: new window.google.maps.Size(0, -36), maxWidth: 320 }}
              >
                <div style={{ padding: 0, minWidth: 220, maxWidth: 300, fontFamily: 'Roboto, sans-serif', overflow: 'hidden' }}>
                  {/* Photo */}
                  {searchPin.photo && (
                    <div style={{ width: '100%', height: 130, overflow: 'hidden', margin: '-8px -8px 0' }}>
                      <img src={searchPin.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                  <div style={{ padding: searchPin.photo ? '10px 0 4px' : '4px 0' }}>
                    {/* Name */}
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#202124', marginBottom: 2 }}>
                      {searchPin.displayName || searchPin.name || 'Local selecionado'}
                    </div>
                    {/* Rating */}
                    {searchPin.rating && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#202124' }}>{searchPin.rating.toFixed(1)}</span>
                        <span style={{ color: '#fbbc04', fontSize: 13 }}>{'★'.repeat(Math.round(searchPin.rating))}</span>
                        {searchPin.totalRatings > 0 && (
                          <span style={{ fontSize: 11, color: '#70757a' }}>({searchPin.totalRatings})</span>
                        )}
                      </div>
                    )}
                    {/* Address */}
                    {searchPin.address && (
                      <div style={{ fontSize: 12, color: '#70757a', marginBottom: 4 }}>{searchPin.address}</div>
                    )}
                    {/* Coordinates */}
                    <div style={{ fontSize: 11, color: '#9aa0a6', marginBottom: 8 }}>
                      {searchPin.lat.toFixed(6)}, {searchPin.lng.toFixed(6)}
                    </div>
                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 6, borderTop: '1px solid #e8eaed', paddingTop: 8, flexWrap: 'wrap' }}>
                      <div
                        onClick={() => {
                          dispatch(setOrigin({ name: searchPin.displayName || searchPin.name, coordinates: [searchPin.lng, searchPin.lat] }));
                          dispatch(setSidebarVisible(true));
                          dispatch(setNeedsRecalculation(true));
                          setSearchPin(null); setSearchPinOpen(false);
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                          borderRadius: 16, background: '#e8f0fe', cursor: 'pointer',
                          fontSize: 11, color: '#1a73e8', fontWeight: 500,
                        }}>
                        <NearMeIcon style={{ fontSize: 13 }} />
                        Origem
                      </div>
                      <div
                        onClick={() => {
                          dispatch(setDestination({ name: searchPin.displayName || searchPin.name, coordinates: [searchPin.lng, searchPin.lat] }));
                          dispatch(setSidebarVisible(true));
                          dispatch(setNeedsRecalculation(true));
                          setSearchPin(null); setSearchPinOpen(false);
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                          borderRadius: 16, background: '#e8f0fe', cursor: 'pointer',
                          fontSize: 11, color: '#1a73e8', fontWeight: 500,
                        }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="#1a73e8"><path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/></svg>
                        Destino
                      </div>
                      {/* Pegman — mesmo padrão visual usado nos pontos
                          da rota e nos favoritos: emoji discreto a 18px
                          com hover scale, sem fundo nem texto. Mantém
                          consistência visual em todos os popups do mapa. */}
                      <span
                        onClick={() => {
                          setStreetViewPos({ lat: searchPin.lat, lng: searchPin.lng });
                          setStreetViewOpen(true);
                          setSearchPinOpen(false);
                        }}
                        title="Ver Street View do local"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          padding: '0 6px', cursor: 'pointer',
                          fontSize: 18, opacity: 0.75,
                          transition: 'opacity 0.15s, transform 0.15s',
                          userSelect: 'none',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.transform = 'scale(1.15)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = 0.75; e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        🧍
                      </span>
                      {/* Favorito — abre o editor de favoritos com as
                          coordenadas e morada pré-preenchidas. Mesmo
                          padrão usado pelo addFavMode (clique direito →
                          guardar favorito) — emite o evento
                          'favoritesOpenAddNew' que ManageFavoritesDialog
                          escuta para abrir em modo "novo" com a localização
                          já preenchida. */}
                      <div
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('favoritesOpenAddNew', {
                            detail: {
                              lat: searchPin.lat,
                              lng: searchPin.lng,
                              address: searchPin.address || searchPin.displayName || searchPin.name || '',
                              name: searchPin.displayName || searchPin.name || '',
                            },
                          }));
                          dispatch(setActiveView('favorites'));
                          // Mantém o pin no mapa — só fecha o popup. Sem
                          // isto, ao voltar atrás dos favoritos o
                          // operador perdia o contexto da pesquisa.
                          setSearchPinOpen(false);
                        }}
                        title="Guardar este local como favorito"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                          borderRadius: 16, background: '#fef9c3', cursor: 'pointer',
                          fontSize: 11, color: '#854d0e', fontWeight: 500,
                        }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="#eab308">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                        </svg>
                        Favorito
                      </div>
                    </div>
                  </div>
                </div>
              </InfoWindow>
            )}
          </>
        )}

        {/* Geofence circles */}
        {showGeofences && geofences.filter(gf => gf.isActive && gf.type === 'circle').map(gf => {
          const isTriggered = geofenceAlerts.some(a => a.geofence.id === gf.id);
          return (
            <Circle
              key={`gf-${gf.id}`}
              center={{ lat: Number(gf.lat), lng: Number(gf.lng) }}
              radius={gf.radius || 1000}
              options={{
                fillColor: isTriggered ? '#ef4444' : (gf.color || '#3b82f6'),
                fillOpacity: isTriggered ? 0.25 : 0.1,
                strokeColor: isTriggered ? '#ef4444' : (gf.color || '#3b82f6'),
                strokeOpacity: 0.7,
                strokeWeight: isTriggered ? 3 : 2,
                clickable: true,
              }}
              onClick={() => {
                if (mapRef.current) {
                  mapRef.current.panTo({ lat: Number(gf.lat), lng: Number(gf.lng) });
                  mapRef.current.setZoom(13);
                }
              }}
            />
          );
        })}
        {/* Geofence labels */}
        {showGeofences && geofences.filter(gf => gf.isActive && gf.type === 'circle').map(gf => (
          <Marker
            key={`gf-label-${gf.id}`}
            position={{ lat: Number(gf.lat), lng: Number(gf.lng) }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 0,
            }}
            label={{
              text: gf.name || 'Geofence',
              fontSize: '10px',
              fontWeight: '600',
              color: gf.color || '#3b82f6',
            }}
          />
        ))}
      </GoogleMap>

      {/* Replay do percurso real — o botão de abrir vive na barra de topo
          (MapToolbar, junto a Distância/Duração/Consulta) e dispara o evento
          'openRouteReplay'. Aqui fica o painel de controlo (play/pausa,
          velocidade, barra de tempo) + o veículo animado com o ÍCONE do tipo
          de veículo da OT. */}
      {replayOpen && actualTrail?.trail?.length > 1 && (
        <RouteReplayGoogle
          trail={actualTrail.trail}
          map={mapRef.current}
          lang={lang}
          vehicleIconUrl={getVehicleIconDataUrl(
            vehicleIconCategory(route?.routeOptions?.vehicleSubType || route?.routeOptions?.vehicleType || 'truck'),
            'green',
          )}
          onClose={() => setReplayOpen(false)}
        />
      )}

      {/* Histórico — cartões de viagem a FLUTUAR à esquerda sobre o mapa
          (estilo EcooGPS): "Atividade total" + um cartão por viagem, com o
          condutor. Clicar num cartão foca o troço no mapa. */}
      {vehicleHistory?.data && !vehicleHistory.loading && (() => {
        const trips = vehicleHistory.data.trips || [];
        const totalMin = trips.reduce((s, t) => s + (t.durationMin || 0), 0);
        const fmtDur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 > 0 ? String(m % 60).padStart(2, '0') : ''}` : `${m}min`);
        const histDriver = (vehicles.find(x => String(x.id) === String(vehicleHistory.vehicle?.id))?.driver
          || vehicleHistory.vehicle?.driver || '').trim();
        // Arranca abaixo da barra de pesquisa (senão a barra de arrasto fica
        // por baixo dela e é difícil de apanhar). Depois é livre (arrastável).
        const DEFAULT_POS = { left: 12, top: 76 };
        const pos = histPanelPos || DEFAULT_POS;
        // Tamanho redimensionável (grip no canto inferior direito). Default:
        // largura 296, altura ~60% do ecrã. O operador pode encolher para
        // libertar os controlos de reprodução do painel inferior.
        const DEFAULT_SIZE = { width: 296, height: Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.6) };
        const size = histPanelSize || DEFAULT_SIZE;
        const startResize = (e) => {
          e.preventDefault(); e.stopPropagation();
          const startX = e.clientX, startY = e.clientY;
          const base = histPanelSize || DEFAULT_SIZE;
          const move = (ev) => setHistPanelSize({
            width: Math.max(240, Math.min(window.innerWidth - pos.left - 8, base.width + (ev.clientX - startX))),
            height: Math.max(180, Math.min(window.innerHeight - pos.top - 8, base.height + (ev.clientY - startY))),
          });
          const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
        };
        // Arrastar o painel pela barra do título — delta-based (robusto): a nova
        // posição = posição base + deslocamento do ponteiro desde o clique.
        const startDrag = (e) => {
          e.preventDefault();
          const startX = e.clientX, startY = e.clientY;
          const base = histPanelPos || DEFAULT_POS;
          const move = (ev) => setHistPanelPos({
            left: Math.max(0, base.left + (ev.clientX - startX)),
            top: Math.max(0, base.top + (ev.clientY - startY)),
          });
          const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
        };
        return (
          <Box style={{
            position: 'absolute', left: pos.left, top: pos.top, zIndex: 701,
            width: size.width, height: size.height, maxWidth: '92vw', maxHeight: '88vh',
            display: 'flex', flexDirection: 'column',
            background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0',
            boxShadow: '0 10px 30px rgba(15,23,42,0.24)', overflow: 'hidden',
          }}>
            {/* Barra do título — ARRASTÁVEL (clica e arrasta para mover o painel) */}
            <Box onPointerDown={startDrag} title="Arrastar para mover" style={{
              flexShrink: 0, cursor: 'grab', userSelect: 'none', touchAction: 'none',
              background: 'linear-gradient(180deg, #e2e8f0, #eef2f6)',
              borderBottom: '1px solid #cbd5e1', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ color: '#94a3b8', fontSize: 15, letterSpacing: -2 }}>⠿⠿</span>
              <span style={{ fontSize: 13 }}>🚛</span>
              <Typography style={{ fontSize: '0.72rem', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Viagens · {vehicleHistory.vehicle?.plate || ''}
              </Typography>
              <Typography style={{ marginLeft: 'auto', fontSize: '0.56rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                arrastar
              </Typography>
            </Box>
            {/* Grip de redimensionar — puxa o canto inferior direito para
                encolher/aumentar (liberta os controlos de reprodução em baixo) */}
            <Box onPointerDown={startResize} title="Redimensionar (arrasta o canto)" style={{
              position: 'absolute', right: 0, bottom: 0, width: 20, height: 20, zIndex: 6,
              cursor: 'nwse-resize', touchAction: 'none',
              display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: 3,
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12" style={{ opacity: 0.6 }}>
                <path d="M11 4 L4 11 M11 8 L8 11" stroke="#64748b" strokeWidth="1.3" fill="none" strokeLinecap="round" />
              </svg>
            </Box>
            {/* Corpo com scroll (barra tipo elevador) */}
            <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Atividade total — FIXA no topo (não faz scroll) */}
            <Box style={{ background: '#0f172a', color: '#fff', borderRadius: 12, padding: '10px 14px', flexShrink: 0 }}>
              <Typography style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#94a3b8' }}>
                Atividade total: {trips.length} viagem{trips.length === 1 ? '' : 's'}
              </Typography>
              <Box style={{ display: 'flex', gap: 14, marginTop: 4, alignItems: 'baseline' }}>
                <Typography style={{ fontSize: '0.9rem', fontWeight: 800 }}>⚡ {vehicleHistory.data.maxSpeed} <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#cbd5e1' }}>km/h</span></Typography>
                <Typography style={{ fontSize: '0.9rem', fontWeight: 800 }}>⏱ {fmtDur(totalMin)}</Typography>
                <Typography style={{ fontSize: '0.9rem', fontWeight: 800 }}>🛣 {vehicleHistory.data.totalDistanceKm} <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#cbd5e1' }}>km</span></Typography>
              </Box>
            </Box>
            {/* Seleção de viagens para o mapa — todas / nenhuma */}
            {trips.length > 1 && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {selectedTrips.size > 0 ? `${selectedTrips.size} selecionada${selectedTrips.size === 1 ? '' : 's'}` : 'Mostrar no mapa'}
                </Typography>
                <Box style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
                  <button onClick={() => {
                    const n = new Set(trips.map((_, i) => i));
                    setSelectedTrips(n);
                    if (mapRef.current) {
                      const b = new window.google.maps.LatLngBounds();
                      trips.forEach(t => (t.points || []).forEach(p => { if (Number.isFinite(Number(p.lat))) b.extend({ lat: Number(p.lat), lng: Number(p.lng) }); }));
                      if (!b.isEmpty()) mapRef.current.fitBounds(b, { top: 40, bottom: 240, left: 320, right: 40 });
                    }
                  }}
                    style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, padding: '2px 9px', cursor: 'pointer', fontSize: '0.6rem', fontWeight: 800, color: '#334155' }}>Todas</button>
                  <button onClick={() => setSelectedTrips(new Set())}
                    disabled={selectedTrips.size === 0}
                    style={{ border: '1px solid #e2e8f0', background: selectedTrips.size === 0 ? '#f8fafc' : '#fff', borderRadius: 6, padding: '2px 9px', cursor: selectedTrips.size === 0 ? 'default' : 'pointer', fontSize: '0.6rem', fontWeight: 800, color: selectedTrips.size === 0 ? '#cbd5e1' : '#334155' }}>Nenhuma</button>
                </Box>
              </Box>
            )}
            {/* Lista de viagens — ELEVADOR próprio (scroll independente do resto) */}
            <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginRight: -4, paddingRight: 4 }}>
            {trips.length === 0 && (
              <Box style={{ background: '#f8fafc', borderRadius: 12, padding: 14, textAlign: 'center', border: '1px solid #f1f5f9' }}>
                <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Sem viagens neste período</Typography>
              </Box>
            )}
            {trips.map((trip, idx) => {
              const startT = trip.startTime ? new Date(trip.startTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
              const endT = trip.endTime ? new Date(trip.endTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
              const durStr = fmtDur(trip.durationMin || 0);
              const sel = selectedTrips.has(idx);
              const color = tripColor(idx);
              return (
                <Box key={idx}
                  onClick={() => {
                    const n = new Set(selectedTrips);
                    if (n.has(idx)) n.delete(idx); else n.add(idx);
                    setSelectedTrips(n);
                    if (mapRef.current && n.size > 0) {
                      const b = new window.google.maps.LatLngBounds();
                      n.forEach(ti => (trips[ti]?.points || []).forEach(p => {
                        if (Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng))) b.extend({ lat: Number(p.lat), lng: Number(p.lng) });
                      }));
                      if (!b.isEmpty()) mapRef.current.fitBounds(b, { top: 40, bottom: 240, left: 320, right: 40 });
                    }
                  }}
                  title={sel ? 'Clica para retirar do mapa' : 'Clica para mostrar no mapa'}
                  style={{ background: sel ? `${color}14` : '#f8fafc', borderRadius: 12, padding: '9px 12px', cursor: 'pointer', border: `1px solid ${sel ? color : '#e8edf3'}`, borderLeftWidth: 4, borderLeftColor: color, flexShrink: 0, transition: 'all .15s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#eff6ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = sel ? `${color}14` : '#f8fafc'; }}
                >
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Box style={{ background: sel ? color : '#fef3c7', color: sel ? '#fff' : '#92400e', borderRadius: 7, padding: '2px 8px', fontSize: '0.62rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {sel && <span style={{ fontSize: '0.7rem' }}>✓</span>}Viagem {idx + 1}
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                      <Typography style={{ fontSize: '0.68rem', fontWeight: 800, color: '#f59e0b' }}>{trip.maxSpeed} km/h</Typography>
                      <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#64748b' }}>{durStr}</Typography>
                      <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3b82f6' }}>{trip.distance} km</Typography>
                    </Box>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                    <Box style={{ width: 9, height: 9, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                    <Typography style={{ fontSize: '0.63rem', color: '#0f172a', fontWeight: 600, flexShrink: 0 }}>{startT}</Typography>
                    <Typography style={{ fontSize: '0.6rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trip.startAddress || ''}</Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Box style={{ width: 9, height: 9, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                    <Typography style={{ fontSize: '0.63rem', color: '#0f172a', fontWeight: 600, flexShrink: 0 }}>{endT}</Typography>
                    <Typography style={{ fontSize: '0.6rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trip.endAddress || ''}</Typography>
                  </Box>
                  {histDriver && (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, paddingTop: 5, borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: 11 }}>👤</span>
                      <Typography style={{ fontSize: '0.62rem', color: '#475569', fontWeight: 600 }}>{histDriver}</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
            </Box>{/* fim do elevador de viagens */}
            </Box>
          </Box>
        );
      })()}

      {/* Vehicle history panel — bottom bar */}
      {vehicleHistory && (
        <Box style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 700,
          background: '#fff', borderTop: '2px solid #bfdbfe',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)', maxHeight: '52%',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header bar — cinza claro fosco p/ diferenciar do resto (estilo EcooGPS) */}
          <Box style={{
            padding: '8px 16px', background: '#eef2f6', borderBottom: '1px solid #dbe2ea',
            display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
          }}>
            <Box style={{ width: 28, height: 28, borderRadius: 8, background: '#eff6ff', border: '1px solid #dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography style={{ fontSize: 14 }}>🚛</Typography>
            </Box>
            {/* Combo de VEÍCULO — trocar de viatura sem sair da dock (estilo EcooGPS) */}
            {(() => {
              const df = vehicleHistory.day || todayStr();
              const dt = vehicleHistory.dayTo || df;
              const preset = detectPreset(df, dt);
              const sel = { border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px', fontSize: '0.7rem', fontWeight: 700, color: '#1e293b', background: '#fff', cursor: 'pointer', maxWidth: 220 };
              const inp = { border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 6px', fontSize: '0.66rem', fontWeight: 600, color: '#1e293b', cursor: 'pointer' };
              const fleet = [...(vehicles || [])].sort((a, b) => String(a.plate || a.name || '').localeCompare(String(b.plate || b.name || '')));
              return (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <select
                    value={String(vehicleHistory.vehicle?.id || '')}
                    onChange={(e) => { const v = fleet.find(x => String(x.id) === e.target.value); if (v) loadVehicleHistory(v, df, dt); }}
                    style={sel}
                  >
                    {fleet.map(v => (
                      <option key={v.id} value={String(v.id)}>{v.plate || v.name || `#${v.id}`}</option>
                    ))}
                  </select>
                  {/* Combo de PERÍODO — presets rápidos */}
                  <select
                    value={preset}
                    onChange={(e) => { const r = periodRange(e.target.value); if (r) loadVehicleHistory(vehicleHistory.vehicle, r.df, r.dt); }}
                    style={{ ...sel, fontWeight: 600 }}
                  >
                    {PERIOD_OPTIONS.map(o => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                  {/* De → Até (visível sempre; obrigatório em "Escolher datas") */}
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 700 }}>De</span>
                    <input type="date" value={df} max={dt}
                      onChange={(e) => e.target.value && loadVehicleHistory(vehicleHistory.vehicle, e.target.value, dt)} style={inp} />
                    <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>→</span>
                    <input type="date" value={dt} min={df} max={todayStr()}
                      onChange={(e) => e.target.value && loadVehicleHistory(vehicleHistory.vehicle, df, e.target.value)} style={inp} />
                  </Box>
                </Box>
              );
            })()}
            {/* Summary badges in header */}
            {vehicleHistory.data && !vehicleHistory.loading && (
              <Box style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                {[
                  { label: 'Viagens', value: vehicleHistory.data.totalTrips },
                  { label: 'Distância', value: `${vehicleHistory.data.totalDistanceKm} km` },
                  { label: 'Vel. máx', value: `${vehicleHistory.data.maxSpeed} km/h` },
                ].map((s, i) => (
                  <Box key={i} style={{ padding: '3px 10px', borderRadius: 8, background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                    <Typography style={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>{s.label}</Typography>
                    <Typography style={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a' }}>{s.value}</Typography>
                  </Box>
                ))}
              </Box>
            )}
            {/* Exportar o percurso do período — GPX / CSV / PDF */}
            {vehicleHistory.data && !vehicleHistory.loading && (vehicleHistory.data.points?.length > 0) && (() => {
              const exp = { border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: '0.62rem', fontWeight: 800, color: '#334155', height: 26, display: 'inline-flex', alignItems: 'center', gap: 4 };
              const drv = (vehicles.find(x => String(x.id) === String(vehicleHistory.vehicle?.id))?.driver || vehicleHistory.vehicle?.driver || '').trim();
              return (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 12 }}>
                  <span style={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Exportar</span>
                  <button title="Exportar GPX (SIG/Google Earth)" style={exp} onClick={() => exportHistoryGPX(vehicleHistory.data, vehicleHistory.vehicle)}>GPX</button>
                  <button title="Exportar CSV (folha de cálculo)" style={exp} onClick={() => exportHistoryCSV(vehicleHistory.data, vehicleHistory.vehicle)}>CSV</button>
                  <button title="Exportar PDF (resumo do período)" style={exp} onClick={() => exportHistoryPDF(vehicleHistory.data, vehicleHistory.vehicle, drv)}>PDF</button>
                </Box>
              );
            })()}
            <button onClick={() => { setVehicleHistory(null); setHistoryTrail(null); }}
              style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#64748b', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 12 }}>
              ✕
            </button>
          </Box>

          {vehicleHistory.loading ? (
            <Box style={{ padding: 24, textAlign: 'center' }}>
              <Typography style={{ fontSize: '0.78rem', color: '#94a3b8' }}>A carregar histórico...</Typography>
            </Box>
          ) : vehicleHistory.data ? (
            <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            {/* Fase B — gráfico de velocidade + faixas de estado */}
            {vehicleHistory.data.points?.length > 1 && (
              <Box style={{ flexShrink: 0, padding: '6px 12px 2px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8, alignItems: 'stretch' }}>
                {/* Toggle do gráfico: Velocidade / Bateria / RPM / Combustível
                    (coluna à esquerda, estilo EcooGPS). RPM e Combustível só
                    aparecem quando a telemetria os traz (attributes.rpm / .fuel). */}
                <Box style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 2 }}>
                  {(() => {
                    const pts = vehicleHistory.data.points || [];
                    const hasRpm = pts.some(p => Number(p.attributes?.rpm) > 0);
                    const hasFuel = pts.some(p => { const a = p.attributes || {}; return a.fuel != null || a.io89 != null || a.io9 != null; });
                    return [
                      { key: 'speed', icon: '⏱', title: 'Velocidade' },
                      { key: 'battery', icon: '🔋', title: 'Bateria' },
                      ...(hasRpm ? [{ key: 'rpm', icon: '🌀', title: 'RPM' }] : []),
                      ...(hasFuel ? [{ key: 'fuel', icon: '⛽', title: 'Combustível' }] : []),
                    ];
                  })().map(m => {
                    const active = historyMetric === m.key;
                    return (
                      <button key={m.key} title={m.title} onClick={() => setHistoryMetric(m.key)}
                        style={{
                          width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 15,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: `1.5px solid ${active ? '#2563eb' : '#e2e8f0'}`,
                          background: active ? '#eff6ff' : '#fff',
                          boxShadow: active ? '0 1px 4px rgba(37,99,235,0.2)' : 'none',
                        }}>{m.icon}</button>
                    );
                  })}
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <VehicleHistoryChart
                    points={vehicleHistory.data.points}
                    metric={historyMetric}
                    cursorT={playbackCursor?.timestamp ? new Date(playbackCursor.timestamp).getTime() : null}
                  />
                  {/* Fase C — reprodução do percurso */}
                  <VehicleHistoryPlayback
                    points={vehicleHistory.data.points}
                    onCursor={setPlaybackCursor}
                    follow={playbackFollow}
                    onToggleFollow={() => setPlaybackFollow(v => !v)}
                    events={historyEvents}
                  />
                </Box>
              </Box>
            )}
            </Box>
          ) : (
            <Box style={{ padding: 16, textAlign: 'center' }}>
              <Typography style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Erro ao carregar histórico</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Geofence alerts banner */}
      {geofenceAlerts.length > 0 && (
        <Box style={{
          position: 'absolute', bottom: 24, left: 16, zIndex: 600,
          background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: 12,
          padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 6px 20px rgba(239,68,68,0.2)',
          maxWidth: '50%',
        }}>
          <Box style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', flexShrink: 0,
            boxShadow: '0 0 0 3px rgba(239,68,68,0.3)', animation: 'pulse 1.5s infinite' }} />
          <Typography style={{ fontSize: '0.76rem', fontWeight: 700, color: '#dc2626' }}>
            ⚠ Rota passa por {geofenceAlerts.length} zona{geofenceAlerts.length > 1 ? 's' : ''} de geofencing:{' '}
            <span style={{ fontWeight: 500 }}>{geofenceAlerts.map(a => a.geofence.name).join(', ')}</span>
          </Typography>
          <IconButton size="small" onClick={() => setGeofenceAlerts([])} style={{ color: '#ef4444', padding: 2, marginLeft: 4 }}>
            <CloseIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      )}

      {/* Road type legend — horizontal toolbar top right */}
      {hasColorSegs && (
        <Box style={{
          position: 'absolute', top: 68, right: 56, zIndex: 110,
          background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
          borderRadius: 10, padding: '5px 14px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          {[
            { color: ROAD_COLORS.motorway, label: 'Autoestrada' },
            { color: ROAD_COLORS.highway, label: 'IP / IC' },
            { color: ROAD_COLORS.national, label: 'Nacional' },
            { color: ROAD_COLORS.local, label: 'Local' },
          ].map(l => (
            <Box key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Box style={{ width: 18, height: 4, borderRadius: 3, background: l.color }} />
              <span style={{ fontSize: '0.58rem', color: '#475569', fontWeight: 600 }}>{l.label}</span>
            </Box>
          ))}
        </Box>
      )}

      {/* MapToolbar */}
      <MapToolbar />

      {/* Monitor "Em trânsito" — janela flutuante com as OT em curso, cor e
          classificação em tempo real. Módulo `transit_monitor` (Planeamento).
          Só no PLANEAMENTO: o mapa fica montado por baixo das outras vistas, mas
          esta janela não deve aparecer no Dashboard/Ordens/Frota/etc. */}
      {activeView === 'planner' && (
        <ModuleGate module="transit_monitor">
          <TransitMonitor lang={lang} />
        </ModuleGate>
      )}

      {/* Painel de acesso rápido à Frota — canto superior esquerdo, por baixo
          da barra de pesquisa. Só no PLANEAMENTO e quando a sidebar está
          fechada (mesma lógica de visibilidade da barra de pesquisa, para não
          se sobreporem). Arrastável + recolhível (estado em localStorage). */}
      {activeView === 'planner' && !sidebarVisible && clientSettings?.fleetQuickPanel !== false && (
        <FleetQuickPanel lang={lang} />
      )}

      {/* Route Services Panel — Google Places.
          Gateado pelo módulo `pois` ("POIs na Rota"): é ESTA a funcionalidade
          que o operador reconhece como POIs. Antes o módulo não gateava nada
          (o RouteForm calculava um `hasPois` que nunca usava). */}
      <ModuleGate module="pois">
      <RouteServicesPanel
        mapRef={mapRef}
        onServicesLoaded={(svcs) => setServiceMarkers(svcs)}
        onMarkerClick={(svc) => {
          if (mapRef.current) {
            mapRef.current.panTo({ lat: svc.lat, lng: svc.lng });
            if (mapRef.current.getZoom() < 14) mapRef.current.setZoom(14);
          }
        }}
      />
      </ModuleGate>

      {/* Compare-mode legend — floating at top-left when overlaying multiple saved routes */}
      {Array.isArray(displayedSavedRoutes) && displayedSavedRoutes.length > 0 && (() => {
        const COMPARE_COLORS = ['#ef4444', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#10b981', '#6366f1', '#f97316'];
        return (
          <Box style={{
            position: 'absolute', top: 70, left: 16, zIndex: 100,
            background: 'rgba(255,255,255,0.96)', borderRadius: 12,
            padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            border: '1px solid #e2e8f0', maxWidth: 320,
            backdropFilter: 'blur(6px)',
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Typography style={{ fontSize: '0.68rem', fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                ⚖ Comparação de Rotas ({displayedSavedRoutes.length})
              </Typography>
              <Box style={{ flex: 1 }} />
              <Tooltip title="Sair da comparação">
                <IconButton size="small"
                  onClick={() => dispatch(setDisplayedSavedRoutes([]))}
                  style={{ padding: 3 }}>
                  <CloseIcon style={{ fontSize: 14, color: '#64748b' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {displayedSavedRoutes.map((r, idx) => {
                const color = COMPARE_COLORS[idx % COMPARE_COLORS.length];
                const km = r.computedRoute?.distance ? `${(r.computedRoute.distance / 1000).toFixed(0)} km` : '';
                const durSec = r.computedRoute?.duration || 0;
                const durStr = durSec ? `${Math.floor(durSec / 3600)}h${Math.round((durSec % 3600) / 60)}m` : '';
                return (
                  <Box key={r.dbId || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.68rem' }}>
                    <Box style={{
                      width: 18, height: 18, borderRadius: '50%', background: color,
                      color: '#fff', fontSize: '0.58rem', fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}>{idx + 1}</Box>
                    <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.name || `Rota ${idx + 1}`}
                    </Typography>
                    {km && <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#3b82f6' }}>{km}</Typography>}
                    {durStr && <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#f59e0b' }}>{durStr}</Typography>}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })()}

      {/* "No GPS fix" chip — shows when vehicles exist but have invalid positions */}
      {vehiclesWithoutPosition.length > 0 && (
        <Box style={{ position: 'absolute', top: 70, left: 12, zIndex: 110 }}>
          <Box
            onClick={() => setShowNoPosList(v => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 16, cursor: 'pointer',
              background: 'rgba(245,158,11,0.95)',
              border: '1px solid rgba(217,119,6,0.6)',
              boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
              color: '#fff', fontSize: '0.68rem', fontWeight: 700,
              userSelect: 'none',
            }}
          >
            ⚠ {vehiclesWithoutPosition.length} {vehiclesWithoutPosition.length === 1 ? 'veículo sem GPS' : 'veículos sem GPS'}
          </Box>
          {showNoPosList && (
            <Box style={{
              marginTop: 4, padding: '8px 12px', borderRadius: 10,
              background: '#fff', border: '1px solid #e2e8f0',
              boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
              minWidth: 240, maxWidth: 320,
            }}>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Sem posição GPS válida
              </Typography>
              {vehiclesWithoutPosition.map(v => (
                <Box key={v.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.7rem',
                }}>
                  <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {v.plate || `Veículo #${v.id}`}
                    </Typography>
                    <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                      Módulo GPS ainda não reportou coordenadas
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Custom map controls — right side */}
      <Box style={{
        position: 'absolute', top: 70, right: 12, zIndex: 100,
        display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center',
      }}>
        {/* Zoom in */}
        <MapBtn title="Zoom in" onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() || 7) + 1)}
          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="#5f6368"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>}
        />
        {/* Zoom out */}
        <MapBtn title="Zoom out" onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() || 7) - 1)}
          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="#5f6368"><path d="M19 13H5v-2h14v2z"/></svg>}
        />

        <Box style={{ width: 24, height: 1, background: '#e2e8f0', margin: '2px 0' }} />

        {/* Fit bounds (zoom to route) */}
        {routePath.length > 1 && (
          <MapBtn title="Ver rota completa" onClick={() => {
            if (!mapRef.current) return;
            const bounds = new window.google.maps.LatLngBounds();
            routePath.forEach(p => bounds.extend(p));
            mapRef.current.fitBounds(bounds, { top: 80, bottom: 80, left: 420, right: 400 });
          }}
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="#5f6368"><path d="M15 3l2.3 2.3-2.89 2.87 1.42 1.42L18.7 6.7 21 9V3h-6zM3 9l2.3-2.3 2.87 2.89 1.42-1.42L6.7 5.3 9 3H3v6zm6 12l-2.3-2.3 2.89-2.87-1.42-1.42L5.3 17.3 3 15v6h6zm12-6l-2.3 2.3-2.87-2.89-1.42 1.42 2.89 2.87L15 21h6v-6z"/></svg>}
          />
        )}

        {/* Resend updated version — only visible when a dispatched route has pending edits */}
        {route.computedRoute && route.sentToVehicle && route.dbId && !route.sourceOTId && route.modifiedSinceLoad && (
          <MapBtn
            title={lang === 'pt' ? 'Reenviar versão actualizada ao motorista' : 'Resend updated version to driver'}
            active
            activeColor="#f59e0b"
            onClick={async () => {
              try {
                const saved = await dispatch(saveRouteToAPI({ token, route })).unwrap();
                const routeId = saved?.id || saved?.dbId || route.dbId;
                if (routeId) {
                  await fetch(`/planner-api/routes/${routeId}`, {
                    method: 'PUT',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sentToVehicle: true, sentToVehicleAt: new Date().toISOString() }),
                  });
                }
                dispatch(fetchSavedRoutes(token));
                dispatch(addToast({
                  type: 'route', severity: 'success',
                  message: lang === 'pt' ? 'Nova versão enviada ao motorista' : 'Updated version sent to driver',
                }));
              } catch (e) {
                console.error('Resend error:', e);
                dispatch(addToast({
                  type: 'route', severity: 'warning',
                  message: lang === 'pt' ? 'Falha ao reenviar versão' : 'Failed to resend version',
                }));
              }
            }}
            icon={
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#f59e0b">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
            }
          />
        )}

        <Box style={{ width: 24, height: 1, background: '#e2e8f0', margin: '2px 0' }} />

        {/* Toggle vehicles */}
        <MapBtn title={showVehicles ? 'Esconder veículos' : 'Mostrar veículos'} active={showVehicles}
          onClick={() => setShowVehicles(v => !v)}
          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill={showVehicles ? '#3b82f6' : '#5f6368'}><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>}
        />

        {/* Toggle traffic */}
        <MapBtn title={showTraffic ? 'Esconder trânsito' : 'Mostrar trânsito'} active={showTraffic} activeColor="#ef4444"
          onClick={() => setShowTraffic(t => !t)}
          icon={<svg width="13" height="13" viewBox="0 0 24 24" fill={showTraffic ? '#ef4444' : '#5f6368'}><path d="M20 10h-3V7c0-1.1-.9-2-2-2H9c-1.1 0-2 .9-2 2v3H4c-1.1 0-2 .9-2 2v8h2v-1h16v1h2v-8c0-1.1-.9-2-2-2zM9 7h6v3H9V7zm11 11H4v-6h16v6z"/><circle cx="7.5" cy="14.5" r="1.5"/><circle cx="16.5" cy="14.5" r="1.5"/></svg>}
        />

        {/* Toggle traffic incidents — accidents, road closures, road
            works and congestion in the current map viewport (HERE API). */}
        <MapBtn title={showIncidents
          ? `Esconder incidentes${incidents.length ? ` (${incidents.length})` : ''}`
          : 'Mostrar incidentes (acidentes, vias cortadas, obras)'}
          active={showIncidents} activeColor="#dc2626"
          onClick={() => setShowIncidents(s => !s)}
          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill={showIncidents ? '#dc2626' : '#5f6368'}>
            <path d="M12 2L1 21h22L12 2zm0 4l8.53 14H3.47L12 6zm-1 7v3h2v-3h-2zm0 4v2h2v-2h-2z"/>
          </svg>}
        />

        {/* Locations button with submenu */}
        <Box style={{ position: 'relative' }}>
          <MapBtn title="Favoritos" active={showFavMenu || showLocationsOnMap} activeColor="#eab308"
            onClick={() => setShowFavMenu(f => !f)}
            icon={<svg width="13" height="13" viewBox="0 0 24 24" fill={(showFavMenu || showLocationsOnMap) ? '#eab308' : '#5f6368'}><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>}
          />
          {showFavMenu && (
            <Box style={{
              position: 'absolute', right: 44, top: 0, zIndex: 200,
              background: '#fff', borderRadius: 10, padding: 4,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0',
              whiteSpace: 'nowrap',
            }}>
              <Box onClick={() => { dispatch(setActiveView('favorites')); setShowFavMenu(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, color: '#1e293b' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                Gerir Favoritos
              </Box>
              <Box onClick={() => {
                  window.__favPickAddNewMode = true;
                  setAddFavMode(true);
                  setShowFavMenu(false);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, color: addFavMode ? '#3b82f6' : '#1e293b' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill={addFavMode ? '#3b82f6' : '#10b981'}><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                {addFavMode ? 'Clicar no mapa…' : 'Adicionar Favorito'}
              </Box>
              <Box onClick={() => { setShowLocationsOnMap(v => !v); setSelectedLocationsOnMap(null); setNearRouteDistance(null); setShowFavMenu(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, color: showLocationsOnMap ? '#22c55e' : '#1e293b' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill={showLocationsOnMap ? '#22c55e' : '#64748b'}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                {showLocationsOnMap && !nearRouteDistance ? '✓ Todos os Favoritos' : 'Mostrar Todos'}
              </Box>
              {/* Nomes no mapa — o operador escolhe a partir de que zoom os
                  nomes dos favoritos aparecem (evita a poluição visual com
                  muitos favoritos juntos). Persistido em localStorage. */}
              <Box style={{ height: 1, background: '#f1f5f9', margin: '2px 8px' }} />
              <Box style={{ padding: '4px 14px 2px', fontSize: '0.55rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Mostrar nomes
              </Box>
              {[
                { z: 0, label: 'Sempre' },
                { z: 8, label: 'A partir de zoom regional' },
                { z: 11, label: 'A partir de zoom cidade' },
                { z: 14, label: 'A partir de zoom rua' },
                { z: 99, label: 'Nunca (só ao clicar)' },
              ].map(opt => (
                <Box key={opt.z}
                  onClick={() => setFavNameZoomPersist(opt.z)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 8,
                    cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                    color: favNameZoom === opt.z ? '#eab308' : '#475569',
                    background: favNameZoom === opt.z ? '#fefce8' : 'transparent',
                  }}
                  onMouseEnter={e => { if (favNameZoom !== opt.z) e.currentTarget.style.background = '#f1f5f9'; }}
                  onMouseLeave={e => { if (favNameZoom !== opt.z) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ fontSize: '0.65rem' }}>{favNameZoom === opt.z ? '✓' : '○'}</span>
                  {opt.label}
                </Box>
              ))}
              {/* Near route filter — only when route is loaded */}
              {routeCoords.length > 1 && (
                <>
                  <Box style={{ height: 1, background: '#f1f5f9', margin: '2px 8px' }} />
                  <Box style={{ padding: '4px 14px 2px', fontSize: '0.55rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Perto da rota
                  </Box>
                  {[5, 10, 15, 20, 30].map(km => (
                    <Box key={km}
                      onClick={() => {
                        setNearRouteDistance(nearRouteDistance === km ? null : km);
                        setShowLocationsOnMap(true);
                        setSelectedLocationsOnMap(null);
                        setShowFavMenu(false);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 8,
                        cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                        color: nearRouteDistance === km ? '#3b82f6' : '#475569',
                        background: nearRouteDistance === km ? '#eff6ff' : 'transparent',
                      }}
                      onMouseEnter={e => { if (nearRouteDistance !== km) e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={e => { if (nearRouteDistance !== km) e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ fontSize: '0.65rem' }}>{nearRouteDistance === km ? '✓' : '○'}</span>
                      {km} km
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Road type legend removed from here — moved to top bar */}

        {/* "Ver rotas" button — opens the full Routes Library view, keeping context so the back button returns here */}
        <MapBtn
          title={lang === 'pt' ? 'Ver rotas' : 'View routes'}
          onClick={() => dispatch(navigateTo('routes'))}
          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="#5f6368"><path d="M3 4h18v2H3V4zm0 7h12v2H3v-2zm0 7h18v2H3v-2zM17 11l4 4-4 4v-8z"/></svg>}
        />

        {/* Map type selector with submenu */}
        <Box style={{ position: 'relative' }}>
          <MapBtn title="Camadas" active={showLayerMenu}
            onClick={() => setShowLayerMenu(v => !v)}
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill={showLayerMenu ? '#3b82f6' : '#5f6368'}><path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/></svg>}
          />
          {showLayerMenu && (
            <Box style={{
              position: 'absolute', right: 44, top: 0, zIndex: 200,
              background: '#fff', borderRadius: 12, padding: 8,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0',
              display: 'flex', gap: 6, whiteSpace: 'nowrap',
            }}>
              {[
                { id: 'roadmap', label: 'Mapa', icon: '🗺️' },
                { id: 'satellite', label: 'Satélite', icon: '🛰️' },
                { id: 'hybrid', label: 'Híbrido', icon: '🌍' },
                { id: 'terrain', label: 'Terreno', icon: '⛰️' },
              ].map(lt => (
                <Box
                  key={lt.id}
                  onClick={() => {
                    if (mapRef.current) mapRef.current.setMapTypeId(lt.id);
                    setMapTypeId(lt.id);
                    setShowLayerMenu(false);
                  }}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                    padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                    background: mapTypeId === lt.id ? '#eef2ff' : 'transparent',
                    border: mapTypeId === lt.id ? '2px solid #3b82f6' : '2px solid transparent',
                    transition: 'all 0.12s',
                    minWidth: 56,
                  }}
                  onMouseEnter={e => { if (mapTypeId !== lt.id) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (mapTypeId !== lt.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 20 }}>{lt.icon}</span>
                  <Typography style={{ fontSize: '0.58rem', fontWeight: mapTypeId === lt.id ? 700 : 500, color: mapTypeId === lt.id ? '#3b82f6' : '#64748b' }}>
                    {lt.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      </Box>{/* End map area */}

      {/* Street View floating panel — bottom right, draggable */}
      {streetViewOpen && (
        <Box
          style={{
            position: 'absolute', bottom: 16, right: 16, zIndex: 500,
            width: '38%', height: '42%', minWidth: 320, minHeight: 220,
            borderRadius: 12, overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            border: '2px solid #1a73e8',
            background: '#1e293b',
            resize: 'both',
          }}>
          {/* Header bar — draggable handle */}
          <Box
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '4px 8px', background: 'rgba(0,0,0,0.7)', position: 'absolute',
              top: 0, left: 0, right: 0, zIndex: 2, cursor: 'move',
            }}
            onMouseDown={(e) => {
              const panel = e.currentTarget.parentElement;
              const startX = e.clientX, startY = e.clientY;
              const startRight = parseInt(panel.style.right) || 16;
              const startBottom = parseInt(panel.style.bottom) || 16;
              function onMove(ev) {
                panel.style.right = (startRight - (ev.clientX - startX)) + 'px';
                panel.style.bottom = (startBottom - (ev.clientY - startY)) + 'px';
              }
              function onUp() { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); }
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
            }}>
            <Typography style={{ fontSize: '0.65rem', color: '#fff', fontWeight: 600 }}>
              🧍 Street View
            </Typography>
            <IconButton size="small" onClick={() => setStreetViewOpen(false)} style={{ color: '#fff', padding: 2 }}>
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          {/* Panorama container */}
          <div ref={streetViewRef} style={{ width: '100%', height: '100%' }} />
        </Box>
      )}
    </Box>
  );
}

// React.memo evita re-render do componente quando o pai (AppLayout) se
// re-renderiza por mudança de estado interno (ex.: setActiveOTLegId
// disparado ao clicar num card de OT no painel inferior). MapViewGoogle
// não recebe props, por isso o memo com comparação shallow padrão
// nunca encontra diferença → skip total. A re-render só corre quando
// o seu estado interno muda (monitoringOTId, vehicles via useSelector,
// etc.). Em rotas longas, isto poupa toda a reconciliação dos
// marcadores e polylines a cada clique de card.
export default React.memo(MapViewGoogle);
